var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);

// js/common/utilities/country-selector.js
var CountrySelector = class extends HTMLElement {
  constructor() {
    super();
    this._onCountryChangedListener = this._onCountryChanged.bind(this);
  }
  connectedCallback() {
    this.countryElement = this.querySelector('[name="address[country]"]');
    this.provinceElement = this.querySelector('[name="address[province]"]');
    this.countryElement.addEventListener("change", this._onCountryChangedListener);
    if (this.hasAttribute("country") && this.getAttribute("country") !== "") {
      this.countryElement.selectedIndex = Math.max(0, Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute("country")));
      this.countryElement.dispatchEvent(new Event("change"));
    } else {
      this._onCountryChanged();
    }
  }
  disconnectedCallback() {
    this.countryElement.removeEventListener("change", this._onCountryChangedListener);
  }
  _onCountryChanged() {
    const option = this.countryElement.options[this.countryElement.selectedIndex], provinces = JSON.parse(option.getAttribute("data-provinces"));
    this.provinceElement.parentElement.hidden = provinces.length === 0;
    if (provinces.length === 0) {
      return;
    }
    this.provinceElement.innerHTML = "";
    provinces.forEach((data) => {
      const selected = data[1] === this.getAttribute("province");
      this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
    });
  }
};
if (!window.customElements.get("country-selector")) {
  window.customElements.define("country-selector", CountrySelector);
}

// js/common/utilities/cached-fetch.js
var cachedMap = /* @__PURE__ */ new Map();
function cachedFetch(url, options) {
  const cacheKey = url;
  if (cachedMap.has(cacheKey)) {
    return Promise.resolve(new Response(new Blob([cachedMap.get(cacheKey)])));
  }
  return fetch(url, options).then((response) => {
    if (response.status === 200) {
      const contentType = response.headers.get("Content-Type");
      if (contentType && (contentType.match(/application\/json/i) || contentType.match(/text\//i))) {
        response.clone().text().then((content) => {
          cachedMap.set(cacheKey, content);
        });
      }
    }
    return response;
  });
}

// js/common/utilities/extract-section-id.js
function extractSectionId(element) {
  element = element.classList.contains("shopify-section") ? element : element.closest(".shopify-section");
  return element.id.replace("shopify-section-", "");
}

// js/common/utilities/dom.js
function deepQuerySelector(root, selector) {
  let element = root.querySelector(selector);
  if (element) {
    return element;
  }
  for (const template2 of root.querySelectorAll("template")) {
    element = deepQuerySelector(template2.content, selector);
    if (element) {
      return element;
    }
  }
  return null;
}
function throttle(callback) {
  let requestId = null, lastArgs;
  const later = (context) => () => {
    requestId = null;
    callback.apply(context, lastArgs);
  };
  const throttled = (...args) => {
    lastArgs = args;
    if (requestId === null) {
      requestId = requestAnimationFrame(later(this));
    }
  };
  throttled.cancel = () => {
    cancelAnimationFrame(requestId);
    requestId = null;
  };
  return throttled;
}
function debounce(fn, delay) {
  let timer = null;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}
function waitForEvent(element, eventName) {
  return new Promise((resolve) => {
    const done = (event) => {
      if (event.target === element) {
        element.removeEventListener(eventName, done);
        resolve(event);
      }
    };
    element.addEventListener(eventName, done);
  });
}

// js/common/utilities/player.js
var _callback, _duration, _remainingTime, _startTime, _timer, _state, _onVisibilityChangeListener, _Player_instances, onVisibilityChange_fn;
var Player = class extends EventTarget {
  constructor(durationInSec, stopOnVisibility = true) {
    super();
    __privateAdd(this, _Player_instances);
    __privateAdd(this, _callback);
    __privateAdd(this, _duration);
    __privateAdd(this, _remainingTime);
    __privateAdd(this, _startTime);
    __privateAdd(this, _timer);
    __privateAdd(this, _state, "paused");
    __privateAdd(this, _onVisibilityChangeListener, __privateMethod(this, _Player_instances, onVisibilityChange_fn).bind(this));
    __privateSet(this, _callback, () => this.dispatchEvent(new CustomEvent("player:end")));
    this.setDuration(durationInSec);
    if (stopOnVisibility) {
      document.addEventListener("visibilitychange", __privateGet(this, _onVisibilityChangeListener));
    }
  }
  getDuration() {
    return __privateGet(this, _duration) / 1e3;
  }
  setDuration(durationInSec) {
    __privateSet(this, _duration, __privateSet(this, _remainingTime, durationInSec * 1e3));
  }
  pause() {
    if (__privateGet(this, _state) !== "started") {
      return;
    }
    clearTimeout(__privateGet(this, _timer));
    __privateSet(this, _state, "paused");
    __privateSet(this, _remainingTime, __privateGet(this, _remainingTime) - ((/* @__PURE__ */ new Date()).getTime() - __privateGet(this, _startTime)));
    this.dispatchEvent(new CustomEvent("player:pause", { detail: { duration: __privateGet(this, _duration) / 1e3, remainingTime: __privateGet(this, _remainingTime) / 1e3 } }));
  }
  resume(restartTimer = false) {
    if (__privateGet(this, _state) !== "stopped") {
      if (restartTimer) {
        this.start();
      } else {
        clearTimeout(__privateGet(this, _timer));
        __privateSet(this, _startTime, (/* @__PURE__ */ new Date()).getTime());
        __privateSet(this, _state, "started");
        __privateSet(this, _timer, setTimeout(__privateGet(this, _callback), __privateGet(this, _remainingTime)));
        this.dispatchEvent(new CustomEvent("player:resume", { detail: { duration: __privateGet(this, _duration) / 1e3, remainingTime: __privateGet(this, _remainingTime) / 1e3 } }));
      }
    }
  }
  start() {
    clearTimeout(__privateGet(this, _timer));
    __privateSet(this, _startTime, (/* @__PURE__ */ new Date()).getTime());
    __privateSet(this, _state, "started");
    __privateSet(this, _remainingTime, __privateGet(this, _duration));
    __privateSet(this, _timer, setTimeout(__privateGet(this, _callback), __privateGet(this, _remainingTime)));
    this.dispatchEvent(new CustomEvent("player:start", { detail: { duration: __privateGet(this, _duration) / 1e3, remainingTime: __privateGet(this, _remainingTime) / 1e3 } }));
  }
  stop() {
    clearTimeout(__privateGet(this, _timer));
    __privateSet(this, _state, "stopped");
    this.dispatchEvent(new CustomEvent("player:stop"));
  }
};
_callback = new WeakMap();
_duration = new WeakMap();
_remainingTime = new WeakMap();
_startTime = new WeakMap();
_timer = new WeakMap();
_state = new WeakMap();
_onVisibilityChangeListener = new WeakMap();
_Player_instances = new WeakSet();
onVisibilityChange_fn = function() {
  if (document.visibilityState === "hidden") {
    this.pause();
  } else if (document.visibilityState === "visible") {
    this.resume();
  }
};

// js/common/actions/confirm-button.js
var ConfirmButton = class extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener("click", (event) => {
      if (!window.confirm(this.getAttribute("data-message") ?? "Are you sure you wish to do this?")) {
        event.preventDefault();
      }
    });
  }
};
if (!window.customElements.get("confirm-button")) {
  window.customElements.define("confirm-button", ConfirmButton, { extends: "button" });
}

// js/common/actions/controls.js
var PageDots = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.items = Array.from(this.children);
    this.items.forEach((button, index) => button.addEventListener("click", () => this.select(index), { signal: this._abortController.signal }));
    this.addEventListener("control:filter", this._filterItems, { signal: this._abortController.signal });
    if (this.controlledElement) {
      this.controlledElement.addEventListener("carousel:change", (event) => this.select(event.detail.index, false), { signal: this._abortController.signal });
    }
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.select(this.items.indexOf(event.target)));
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get controlledElement() {
    return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
  }
  get selectedIndex() {
    return this.items.findIndex((button) => button.getAttribute("aria-current") === "true");
  }
  select(selectedIndex, dispatchEvent = true) {
    if (this.hasAttribute("align-selected")) {
      const scrollElement = this.getAttribute("align-selected") !== "" ? this.closest(this.getAttribute("align-selected")) : this;
      scrollElement.scrollTo({
        left: this.items[selectedIndex].offsetLeft - scrollElement.clientWidth / 2 + this.items[selectedIndex].clientWidth / 2,
        top: this.items[selectedIndex].offsetTop - scrollElement.clientHeight / 2 - this.items[selectedIndex].clientHeight / 2,
        behavior: window.matchMedia("(prefers-reduced-motion: no-preference)").matches ? "smooth" : "auto"
      });
    }
    if (this.selectedIndex === selectedIndex) {
      return;
    }
    this.items.forEach((button, index) => button.setAttribute("aria-current", selectedIndex === index ? "true" : "false"));
    if (dispatchEvent) {
      this._dispatchEvent(selectedIndex);
    }
  }
  _filterItems(event) {
    this.items.forEach((item, index) => item.hidden = event.detail.filteredIndexes.includes(index));
  }
  _dispatchEvent(index) {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:select", { bubbles: true, cancelable: true, detail: { index } }));
  }
};
var PrevButton = class extends HTMLButtonElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.addEventListener("click", () => (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:prev", { bubbles: true, cancelable: true })), { signal: this._abortController.signal });
    if (this.controlledElement) {
      this.controlledElement.addEventListener("scroll:edge-nearing", (event) => this.disabled = event.detail.position === "start", { signal: this._abortController.signal });
      this.controlledElement.addEventListener("scroll:edge-leaving", (event) => this.disabled = event.detail.position === "start" ? false : this.disabled, { signal: this._abortController.signal });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get controlledElement() {
    return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
  }
};
var NextButton = class extends HTMLButtonElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.addEventListener("click", () => (this.controlledElement ?? this).dispatchEvent(new CustomEvent("control:next", { bubbles: true, cancelable: true })), { signal: this._abortController.signal });
    if (this.controlledElement) {
      this.controlledElement.addEventListener("scroll:edge-nearing", (event) => this.disabled = event.detail.position === "end", { signal: this._abortController.signal });
      this.controlledElement.addEventListener("scroll:edge-leaving", (event) => this.disabled = event.detail.position === "end" ? false : this.disabled, { signal: this._abortController.signal });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get controlledElement() {
    return this.hasAttribute("aria-controls") ? document.getElementById(this.getAttribute("aria-controls")) : null;
  }
};
if (!window.customElements.get("page-dots")) {
  window.customElements.define("page-dots", PageDots);
}
if (!window.customElements.get("prev-button")) {
  window.customElements.define("prev-button", PrevButton, { extends: "button" });
}
if (!window.customElements.get("next-button")) {
  window.customElements.define("next-button", NextButton, { extends: "button" });
}

// js/common/actions/copy-button.js
import { timeline } from "vendor";
var CopyButton = class extends HTMLButtonElement {
  constructor() {
    super();
    this.append(this.contentElement, this.animationElement);
    this.addEventListener("click", this._copyToClipboard.bind(this));
  }
  get contentElement() {
    if (this._contentElement) {
      return this._contentElement;
    }
    this._contentElement = document.createElement("div");
    this._contentElement.append(...this.childNodes);
    return this._contentElement = this._contentElement || document.createElement("div").append();
  }
  get animationElement() {
    return this._animationElement = this._animationElement || document.createRange().createContextualFragment(`
      <span class="button__feedback">  
        <svg role="presentation" focusable="false" fill="none" width="18px" height="18px" viewBox="0 0 20 20">
          <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/>
          <path d="m6 9.8 2.63 2.8L14 7" stroke="currentColor" stroke-width="2"/>
        </svg>
      </span>
    `).firstElementChild;
  }
  async _copyToClipboard() {
    if (!navigator.clipboard) {
      return;
    }
    await navigator.clipboard.writeText(this.getAttribute("data-text") || "");
    timeline([
      [this.contentElement, { y: ["translateY(0)", "translateY(-10px)"], opacity: 0 }, { duration: 0.15 }],
      [this.animationElement, { transform: ["translateY(10px)", "translateY(0)"], opacity: [0, 1] }, { duration: 0.15 }],
      [this.animationElement, { transform: ["translateY(0)", "translateY(-10px)"], opacity: 0 }, { duration: 0.15, at: "+0.5" }],
      [this.contentElement, { transform: ["translateY(10px)", "translateY(0)"], opacity: 1 }, { duration: 0.15 }]
    ]);
  }
};
if (!window.customElements.get("copy-button")) {
  window.customElements.define("copy-button", CopyButton, { extends: "button" });
}

// js/common/actions/custom-button.js
import { animate, timeline as timeline2, stagger } from "vendor";
var CustomButton = class extends HTMLButtonElement {
  static get observedAttributes() {
    return ["aria-busy"];
  }
  constructor() {
    super();
    if (this.type === "submit" && this.form) {
      this.form.addEventListener("submit", () => this.setAttribute("aria-busy", "true"));
    }
    this.append(this.contentElement, this.animationElement);
    window.addEventListener("pageshow", () => this.removeAttribute("aria-busy"));
  }
  get contentElement() {
    if (this._contentElement) {
      return this._contentElement;
    }
    this._contentElement = document.createElement("div");
    this._contentElement.append(...this.childNodes);
    return this._contentElement = this._contentElement || document.createElement("div").append();
  }
  get animationElement() {
    return this._animationElement = this._animationElement || document.createRange().createContextualFragment(`
      <span class="button__loader">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `).firstElementChild;
  }
  async attributeChangedCallback(name, oldValue, newValue) {
    if (newValue === "true") {
      timeline2([
        [this.contentElement, { transform: ["translateY(0)", "translateY(-10px)"], opacity: 0 }, { duration: 0.15 }],
        [this.animationElement, { transform: ["translateY(10px)", "translateY(0)"], opacity: 1 }, { duration: 0.15 }]
      ]);
      animate(this.animationElement.children, { opacity: [1, 0.1] }, { duration: 0.35, delay: stagger(0.35 / 3), direction: "alternate", repeat: Infinity });
    } else {
      timeline2([
        [this.animationElement, { transform: ["translateY(0)", "translateY(-10px)"], opacity: 0 }, { duration: 0.15 }],
        [this.contentElement, { transform: ["translateY(10px)", "translateY(0)"], opacity: 1 }, { duration: 0.15 }]
      ]);
    }
  }
};
if (!window.customElements.get("custom-button")) {
  window.customElements.define("custom-button", CustomButton, { extends: "button" });
}

// js/common/actions/share-button.js
var ShareButton = class extends HTMLButtonElement {
  constructor() {
    super();
    if (navigator.share) {
      this.addEventListener("click", this._showSystemShare);
    }
  }
  _showSystemShare() {
    navigator.share({
      title: this.hasAttribute("share-title") ? this.getAttribute("share-title") : document.title,
      url: this.hasAttribute("share-url") ? this.getAttribute("share-url") : window.location.href
    });
  }
};
if (!window.customElements.get("share-button")) {
  window.customElements.define("share-button", ShareButton, { extends: "button" });
}

// js/common/animation/heading.js
import { stagger as stagger2 } from "vendor";
function getHeadingKeyframe(element, options = {}) {
  if (!element) {
    return [];
  }
  const splitLines = element.querySelector("split-lines")?.lines;
  if (window.themeVariables.settings.headingApparition === "fade" || !splitLines) {
    return [element, { opacity: [0, 1] }, { duration: 0.2, ...options }];
  } else {
    element.style.opacity = "1";
    switch (window.themeVariables.settings.headingApparition) {
      case "split_fade":
        return [splitLines, { transform: ["translateY(0.5em)", "translateY(0)"], opacity: [0, 1] }, { duration: 0.3, delay: stagger2(0.1), ...options }];
      case "split_clip":
        return [splitLines, { clipPath: ["inset(0 0 100% 0)", "inset(0 0 -0.3em 0)"], transform: ["translateY(100%)", "translateY(0)"], opacity: [0, 1] }, { duration: 0.7, delay: stagger2(0.15), easing: [0.22, 1, 0.36, 1], ...options }];
      case "split_rotation":
        const rotatedSpans = splitLines.map((line) => line.querySelector("span"));
        rotatedSpans.forEach((span) => span.style.transformOrigin = "top left");
        splitLines.forEach((line) => line.style.clipPath = "inset(0 0 -0.3em 0)");
        return [rotatedSpans, { transform: ["translateY(0.5em) rotateZ(5deg)", "translateY(0) rotateZ(0)"], opacity: [0, 1] }, { duration: 0.4, delay: stagger2(0.1), ...options }];
    }
  }
}

// js/common/animation/reveal-items.js
import { animate as animate2, stagger as stagger3, inView } from "vendor";
var _RevealItems_instances, reveal_fn;
var RevealItems = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _RevealItems_instances);
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches && window.themeVariables.settings.staggerProductsApparition) {
      inView(this, __privateMethod(this, _RevealItems_instances, reveal_fn).bind(this), { margin: this.getAttribute("margin") ?? "-50px 0px" });
    }
  }
};
_RevealItems_instances = new WeakSet();
reveal_fn = function() {
  this.style.opacity = "1";
  animate2(
    this.hasAttribute("selector") ? this.querySelectorAll(this.getAttribute("selector")) : this.children,
    { opacity: [0, 1], transform: ["translateY(15px)", "translateY(0)"] },
    { duration: 0.35, delay: stagger3(0.05, { easing: "ease-out" }), easing: "ease" }
  );
};
if (!window.customElements.get("reveal-items")) {
  window.customElements.define("reveal-items", RevealItems);
}

// js/common/behavior/custom-cursor.js
var _abortController, _CustomCursor_instances, onPointerLeave_fn, onPointerMove_fn;
var CustomCursor = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CustomCursor_instances);
    __privateAdd(this, _abortController);
  }
  connectedCallback() {
    __privateSet(this, _abortController, new AbortController());
    this.parentElement.addEventListener("pointermove", __privateMethod(this, _CustomCursor_instances, onPointerMove_fn).bind(this), { passive: true, signal: __privateGet(this, _abortController).signal });
    this.parentElement.addEventListener("pointerleave", __privateMethod(this, _CustomCursor_instances, onPointerLeave_fn).bind(this), { signal: __privateGet(this, _abortController).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController).abort();
  }
};
_abortController = new WeakMap();
_CustomCursor_instances = new WeakSet();
onPointerLeave_fn = function() {
  this.classList.remove("is-visible", "is-half-start", "is-half-end");
};
onPointerMove_fn = function(event) {
  if (event.target.matches("button, a[href], button :scope, a[href] :scope")) {
    return this.classList.remove("is-visible");
  }
  const parentBoundingRect = this.parentElement.getBoundingClientRect(), parentXCenter = (parentBoundingRect.left + parentBoundingRect.right) / 2, isOnStartHalfPart = event.pageX < parentXCenter;
  this.classList.toggle("is-half-start", isOnStartHalfPart);
  this.classList.toggle("is-half-end", !isOnStartHalfPart);
  this.classList.add("is-visible");
  const mouseY = event.clientY - parentBoundingRect.y - this.clientHeight / 2, mouseX = event.clientX - parentBoundingRect.x - this.clientWidth / 2;
  this.style.translate = `${mouseX.toFixed(3)}px ${mouseY.toFixed(3)}px`;
  this.style.transform = `${mouseX.toFixed(3)}px ${mouseY.toFixed(3)}px`;
};
if (!window.customElements.get("custom-cursor")) {
  window.customElements.define("custom-cursor", CustomCursor);
}

// js/common/behavior/gesture-area.js
var _domElement, _thresholdDistance, _thresholdTime, _signal, _firstClientX, _tracking, _start, _GestureArea_instances, touchStart_fn, preventTouch_fn, gestureStart_fn, gestureMove_fn, gestureEnd_fn;
var GestureArea = class {
  constructor(domElement, { thresholdDistance = 80, thresholdTime = 500, signal = null } = {}) {
    __privateAdd(this, _GestureArea_instances);
    __privateAdd(this, _domElement);
    __privateAdd(this, _thresholdDistance);
    __privateAdd(this, _thresholdTime);
    __privateAdd(this, _signal);
    __privateAdd(this, _firstClientX);
    __privateAdd(this, _tracking, false);
    __privateAdd(this, _start, {});
    __privateSet(this, _domElement, domElement);
    __privateSet(this, _thresholdDistance, thresholdDistance);
    __privateSet(this, _thresholdTime, thresholdTime);
    __privateSet(this, _signal, signal);
    __privateGet(this, _domElement).addEventListener("touchstart", __privateMethod(this, _GestureArea_instances, touchStart_fn).bind(this), { passive: true, signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("touchmove", __privateMethod(this, _GestureArea_instances, preventTouch_fn).bind(this), { passive: false, signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointerdown", __privateMethod(this, _GestureArea_instances, gestureStart_fn).bind(this), { signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointermove", __privateMethod(this, _GestureArea_instances, gestureMove_fn).bind(this), { passive: false, signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointerup", __privateMethod(this, _GestureArea_instances, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointerleave", __privateMethod(this, _GestureArea_instances, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
    __privateGet(this, _domElement).addEventListener("pointercancel", __privateMethod(this, _GestureArea_instances, gestureEnd_fn).bind(this), { signal: __privateGet(this, _signal) });
  }
};
_domElement = new WeakMap();
_thresholdDistance = new WeakMap();
_thresholdTime = new WeakMap();
_signal = new WeakMap();
_firstClientX = new WeakMap();
_tracking = new WeakMap();
_start = new WeakMap();
_GestureArea_instances = new WeakSet();
touchStart_fn = function(event) {
  __privateSet(this, _firstClientX, event.touches[0].clientX);
};
preventTouch_fn = function(event) {
  if (Math.abs(event.touches[0].clientX - __privateGet(this, _firstClientX)) > 10) {
    event.preventDefault();
  }
};
gestureStart_fn = function(event) {
  __privateSet(this, _tracking, true);
  __privateSet(this, _start, {
    time: (/* @__PURE__ */ new Date()).getTime(),
    x: event.clientX,
    y: event.clientY
  });
};
gestureMove_fn = function(event) {
  if (__privateGet(this, _tracking)) {
    event.preventDefault();
  }
};
gestureEnd_fn = function(event) {
  if (!__privateGet(this, _tracking)) {
    return;
  }
  __privateSet(this, _tracking, false);
  const now = (/* @__PURE__ */ new Date()).getTime(), deltaTime = now - __privateGet(this, _start).time, deltaX = event.clientX - __privateGet(this, _start).x, deltaY = event.clientY - __privateGet(this, _start).y;
  if (deltaTime > __privateGet(this, _thresholdTime)) {
    return;
  }
  let matchedEvent;
  if (deltaX === 0 && deltaY === 0 && !event.target.matches("a, button, a :scope, button :scope")) {
    matchedEvent = "tap";
  } else if (deltaX > __privateGet(this, _thresholdDistance) && Math.abs(deltaY) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swiperight";
  } else if (-deltaX > __privateGet(this, _thresholdDistance) && Math.abs(deltaY) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swipeleft";
  } else if (deltaY > __privateGet(this, _thresholdDistance) && Math.abs(deltaX) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swipedown";
  } else if (-deltaY > __privateGet(this, _thresholdDistance) && Math.abs(deltaX) < __privateGet(this, _thresholdDistance)) {
    matchedEvent = "swipeup";
  }
  if (matchedEvent) {
    __privateGet(this, _domElement).dispatchEvent(new CustomEvent(matchedEvent, { bubbles: true, composed: true, detail: { originalEvent: event } }));
  }
};

// js/common/behavior/height-observer.js
var HeightObserver = class extends HTMLElement {
  constructor() {
    super();
    if (window.ResizeObserver) {
      new ResizeObserver(this._updateCustomProperties.bind(this)).observe(this);
    }
  }
  connectedCallback() {
    if (!window.ResizeObserver) {
      document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${this.clientHeight.toFixed(1)}px`);
    }
  }
  _updateCustomProperties(entries) {
    requestAnimationFrame(() => {
      entries.forEach((entry) => {
        if (entry.target === this) {
          const height = entry.borderBoxSize ? entry.borderBoxSize.length > 0 ? entry.borderBoxSize[0].blockSize : entry.borderBoxSize.blockSize : entry.target.clientHeight;
          document.documentElement.style.setProperty(`--${this.getAttribute("variable")}-height`, `${Math.round(height)}px`);
        }
      });
    });
  }
};
if (!window.customElements.get("height-observer")) {
  window.customElements.define("height-observer", HeightObserver);
}

// js/common/behavior/safe-sticky.js
import { inView as inView2 } from "vendor";
var _resizeObserver, _checkPositionListener, _initialTop, _lastKnownY, _currentTop, _position, _SafeSticky_instances, recalculateStyles_fn, checkPosition_fn;
var SafeSticky = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _SafeSticky_instances);
    __privateAdd(this, _resizeObserver, new ResizeObserver(__privateMethod(this, _SafeSticky_instances, recalculateStyles_fn).bind(this)));
    __privateAdd(this, _checkPositionListener, throttle(__privateMethod(this, _SafeSticky_instances, checkPosition_fn).bind(this)));
    __privateAdd(this, _initialTop, 0);
    __privateAdd(this, _lastKnownY, 0);
    /* we could initialize it to window.scrollY but this avoids a costly reflow */
    __privateAdd(this, _currentTop, 0);
    __privateAdd(this, _position, "relative");
  }
  connectedCallback() {
    inView2(this, () => {
      __privateGet(this, _resizeObserver).observe(this);
      window.addEventListener("scroll", __privateGet(this, _checkPositionListener));
      return () => {
        __privateGet(this, _resizeObserver).unobserve(this);
        window.removeEventListener("scroll", __privateGet(this, _checkPositionListener));
      };
    }, { margin: "500px" });
  }
  disconnectedCallback() {
    window.removeEventListener("scroll", __privateGet(this, _checkPositionListener));
  }
};
_resizeObserver = new WeakMap();
_checkPositionListener = new WeakMap();
_initialTop = new WeakMap();
_lastKnownY = new WeakMap();
_currentTop = new WeakMap();
_position = new WeakMap();
_SafeSticky_instances = new WeakSet();
recalculateStyles_fn = function() {
  this.style.removeProperty("top");
  const computedStyles = getComputedStyle(this);
  __privateSet(this, _initialTop, parseInt(computedStyles.top));
  __privateSet(this, _position, computedStyles.position);
  __privateMethod(this, _SafeSticky_instances, checkPosition_fn).call(this);
};
checkPosition_fn = function() {
  if (__privateGet(this, _position) !== "sticky") {
    return this.style.removeProperty("top");
  }
  let bounds = this.getBoundingClientRect(), maxTop = bounds.top + window.scrollY - this.offsetTop + __privateGet(this, _initialTop), minTop = this.clientHeight - window.innerHeight + 20;
  if (window.scrollY < __privateGet(this, _lastKnownY)) {
    __privateSet(this, _currentTop, __privateGet(this, _currentTop) - (window.scrollY - __privateGet(this, _lastKnownY)));
  } else {
    __privateSet(this, _currentTop, __privateGet(this, _currentTop) + (__privateGet(this, _lastKnownY) - window.scrollY));
  }
  __privateSet(this, _currentTop, Math.min(Math.max(__privateGet(this, _currentTop), -minTop), maxTop, __privateGet(this, _initialTop)));
  __privateSet(this, _lastKnownY, window.scrollY);
  this.style.top = `${Math.round(__privateGet(this, _currentTop))}px`;
};
if (!window.customElements.get("safe-sticky")) {
  window.customElements.define("safe-sticky", SafeSticky);
}

// js/common/behavior/scroll-area.js
var ScrollArea = class {
  constructor(element, abortController2 = null) {
    this._element = element;
    this._allowTriggerNearingStartEvent = false;
    this._allowTriggerLeavingStartEvent = true;
    this._allowTriggerNearingEndEvent = true;
    this._allowTriggerLeavingEndEvent = false;
    new ResizeObserver(this._checkIfScrollable.bind(this)).observe(element);
    this._element.addEventListener("scroll", throttle(this._onScroll.bind(this)), { signal: abortController2?.signal });
  }
  get scrollNearingThreshold() {
    return 125;
  }
  get scrollDirection() {
    if (this._element.scrollWidth > this._element.clientWidth) {
      return "inline";
    } else if (this._element.scrollHeight - this._element.clientHeight > 1) {
      return "block";
    } else {
      return "none";
    }
  }
  _checkIfScrollable() {
    this._element.classList.toggle("is-scrollable", this.scrollDirection !== "none");
  }
  _onScroll() {
    clearTimeout(this._scrollTimeout);
    this._lastScrollPosition = this._lastScrollPosition ?? (this.scrollDirection === "inline" ? Math.abs(this._element.scrollLeft) : Math.abs(this._element.scrollTop));
    let direction;
    if (this.scrollDirection === "inline") {
      direction = this._lastScrollPosition > Math.abs(this._element.scrollLeft) ? "start" : "end";
      this._lastScrollPosition = Math.abs(this._element.scrollLeft);
    } else {
      direction = this._lastScrollPosition > Math.abs(this._element.scrollTop) ? "start" : "end";
      this._lastScrollPosition = Math.abs(this._element.scrollTop);
    }
    const scrollPosition = Math.round(Math.abs(this.scrollDirection === "inline" ? this._element.scrollLeft : this._element.scrollTop)), scrollMinusSize = Math.round(this.scrollDirection === "inline" ? this._element.scrollWidth - this._element.clientWidth : this._element.scrollHeight - this._element.clientHeight);
    if (direction === "start" && this._allowTriggerNearingStartEvent && scrollPosition <= this.scrollNearingThreshold) {
      this._allowTriggerNearingStartEvent = false;
      this._allowTriggerLeavingStartEvent = true;
      this._element.dispatchEvent(new CustomEvent("scroll:edge-nearing", { bubbles: true, detail: { position: "start" } }));
    } else if (direction === "end" && scrollPosition > this.scrollNearingThreshold) {
      this._allowTriggerNearingStartEvent = true;
      if (this._allowTriggerLeavingStartEvent) {
        this._allowTriggerLeavingStartEvent = false;
        this._element.dispatchEvent(new CustomEvent("scroll:edge-leaving", { bubbles: true, detail: { position: "start" } }));
      }
    }
    if (direction === "end" && this._allowTriggerNearingEndEvent && scrollMinusSize <= scrollPosition + this.scrollNearingThreshold) {
      this._allowTriggerNearingEndEvent = false;
      this._allowTriggerLeavingEndEvent = true;
      this._element.dispatchEvent(new CustomEvent("scroll:edge-nearing", { bubbles: true, detail: { position: "end" } }));
    } else if (direction === "start" && scrollMinusSize > scrollPosition + this.scrollNearingThreshold) {
      this._allowTriggerNearingEndEvent = true;
      if (this._allowTriggerLeavingEndEvent) {
        this._allowTriggerLeavingEndEvent = false;
        this._element.dispatchEvent(new CustomEvent("scroll:edge-leaving", { bubbles: true, detail: { position: "end" } }));
      }
    }
    if (window.onscrollend === void 0) {
      this._scrollTimeout = setTimeout(() => {
        this._element.dispatchEvent(new CustomEvent("scrollend", { bubbles: true, composed: true }));
      }, 75);
    }
  }
};

// js/common/behavior/scroll-progress.js
var ScrollProgress = class extends HTMLElement {
  connectedCallback() {
    this.scrolledElement.addEventListener("scroll", throttle(this._updateScrollProgress.bind(this)));
    if (window.ResizeObserver) {
      new ResizeObserver(this._updateScrollProgress.bind(this)).observe(this.scrolledElement);
    }
  }
  get scrolledElement() {
    return this._scrolledElement = this._scrolledElement || document.getElementById(this.getAttribute("observes"));
  }
  _updateScrollProgress() {
    const scrollLeft = document.dir === "ltr" ? this.scrolledElement.scrollLeft : Math.abs(this.scrolledElement.scrollLeft), advancement = (scrollLeft + this.scrolledElement.clientWidth) / this.scrolledElement.scrollWidth;
    this.style.setProperty("--scroll-progress", Math.max(0, Math.min(advancement, 1)).toFixed(6));
  }
};
if (!window.customElements.get("scroll-progress")) {
  window.customElements.define("scroll-progress", ScrollProgress);
}

// js/common/behavior/scroll-shadow.js
var template = `
  <style>
    :host {
      display: inline-block;
      contain: layout;
      position: relative;
    }
    
    :host([hidden]) {
      display: none;
    }
    
    s {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      pointer-events: none;
      background-image:
        var(--scroll-shadow-top, linear-gradient(to bottom, rgb(var(--background)), rgb(var(--background) / 0))),
        var(--scroll-shadow-bottom, linear-gradient(to top, rgb(var(--background)), rgb(var(--background) / 0))),
        var(--scroll-shadow-left, linear-gradient(to right, rgb(var(--background)), rgb(var(--background) / 0))),
        var(--scroll-shadow-right, linear-gradient(to left, rgb(var(--background)), rgb(var(--background) / 0)));
      background-position: top, bottom, left, right;
      background-repeat: no-repeat;
      background-size: 100% var(--top, 0), 100% var(--bottom, 0), var(--left, 0) 100%, var(--right, 0) 100%;
    }
  </style>
  <slot></slot>
  <s></s>
`;
var Updater = class {
  constructor(targetElement) {
    this.scheduleUpdate = throttle(() => this.update(targetElement, getComputedStyle(targetElement)));
    this.resizeObserver = new ResizeObserver(this.scheduleUpdate.bind(this));
  }
  start(element) {
    if (this.element) {
      this.stop();
    }
    if (element) {
      element.addEventListener("scroll", this.scheduleUpdate);
      this.resizeObserver.observe(element);
      this.element = element;
    }
  }
  stop() {
    if (!this.element) {
      return;
    }
    this.element.removeEventListener("scroll", this.scheduleUpdate);
    this.resizeObserver.unobserve(this.element);
    this.element = null;
  }
  update(targetElement, style) {
    if (!this.element) {
      return;
    }
    const maxSize = style.getPropertyValue("--scroll-shadow-size") ? parseInt(style.getPropertyValue("--scroll-shadow-size")) : 0;
    const scroll5 = {
      top: Math.max(this.element.scrollTop, 0),
      bottom: Math.max(this.element.scrollHeight - this.element.offsetHeight - this.element.scrollTop, 0),
      left: Math.max(this.element.scrollLeft, 0),
      right: Math.max(this.element.scrollWidth - this.element.offsetWidth - this.element.scrollLeft, 0)
    };
    requestAnimationFrame(() => {
      for (const position of ["top", "bottom", "left", "right"]) {
        targetElement.style.setProperty(
          `--${position}`,
          `${scroll5[position] > maxSize ? maxSize : scroll5[position]}px`
        );
      }
    });
  }
};
var ScrollShadow = class extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).innerHTML = template;
    this.updater = new Updater(this.shadowRoot.lastElementChild);
  }
  connectedCallback() {
    this.shadowRoot.querySelector("slot").addEventListener("slotchange", this.start);
    this.start();
  }
  disconnectedCallback() {
    this.updater.stop();
  }
  start() {
    if (this.firstElementChild) {
      this.updater.start(this.firstElementChild);
    }
  }
};
if ("ResizeObserver" in window && !window.customElements.get("scroll-shadow")) {
  window.customElements.define("scroll-shadow", ScrollShadow);
}

// js/common/behavior/split-lines.js
var _requireSplit, _lastScreenWidth, _SplitLines_instances, split_fn, onWindowResized_fn;
var SplitLines = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _SplitLines_instances);
    __privateAdd(this, _requireSplit, true);
    __privateAdd(this, _lastScreenWidth, window.innerWidth);
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(document.createRange().createContextualFragment("<slot></slot>"));
    window.addEventListener("resize", throttle(__privateMethod(this, _SplitLines_instances, onWindowResized_fn).bind(this)));
    new MutationObserver(__privateMethod(this, _SplitLines_instances, split_fn).bind(this, true)).observe(this, { characterData: true, attributes: false, childList: false, subtree: true });
  }
  connectedCallback() {
    __privateMethod(this, _SplitLines_instances, split_fn).call(this);
  }
  get lines() {
    return Array.from(this.shadowRoot.children);
  }
};
_requireSplit = new WeakMap();
_lastScreenWidth = new WeakMap();
_SplitLines_instances = new WeakSet();
split_fn = function(force = false) {
  if (!__privateGet(this, _requireSplit) && !force) {
    return;
  }
  this.shadowRoot.innerHTML = this.textContent.replace(/./g, "<span>$&</span>").replace(/\s/g, " ");
  const bounds = /* @__PURE__ */ new Map();
  Array.from(this.shadowRoot.children).forEach((letter) => {
    const key = Math.round(letter.getBoundingClientRect().top);
    bounds.set(key, (bounds.get(key) || "").concat(letter.textContent));
  });
  this.shadowRoot.replaceChildren(...Array.from(bounds.values(), (line) => document.createRange().createContextualFragment(`
      <span style="display: inline-block;">
        <span style="display: block">${line}</span>
      </span>
    `)));
  __privateSet(this, _requireSplit, false);
};
onWindowResized_fn = function() {
  if (__privateGet(this, _lastScreenWidth) === window.innerWidth) {
    return;
  }
  __privateMethod(this, _SplitLines_instances, split_fn).call(this, true);
  __privateSet(this, _lastScreenWidth, window.innerWidth);
};
if (!window.customElements.get("split-lines")) {
  window.customElements.define("split-lines", SplitLines);
}

// js/common/carousel/effect-carousel.js
import { timeline as timeline3, inView as inView3 } from "vendor";

// js/common/carousel/base-carousel.js
var BaseCarousel = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this._reloaded = false;
    if (Shopify.designMode) {
      this.closest(".shopify-section")?.addEventListener("shopify:section:select", (event) => this._reloaded = event.detail.load);
    }
    if (this.items.length > 1) {
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", (event) => this.select(this.items.indexOf(event.target), { animate: !event.detail.load }));
      }
      if (this.hasAttribute("adaptive-height")) {
        this.addEventListener("carousel:settle", this._adjustHeight);
        this._adjustHeight();
      }
      this.addEventListener("carousel:select", this._preloadImages, { signal: this._abortController.signal });
      this.addEventListener("carousel:filter", this._filterItems, { signal: this._abortController.signal });
      this.addEventListener("control:prev", this.previous, { signal: this._abortController.signal });
      this.addEventListener("control:next", this.next, { signal: this._abortController.signal });
      this.addEventListener("control:select", (event) => this.select(event.detail.index), { signal: this._abortController.signal });
    }
    if (this.selectedIndex === 0) {
      this.selectedSlide.classList.add("is-selected");
      this._dispatchEvent("carousel:select", 0);
    } else {
      this.select(this.selectedIndex, { animate: false, force: true });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get items() {
    if (this.hasAttribute("reversed")) {
      return this._items = this._items || Array.from(this.hasAttribute("selector") ? this.querySelectorAll(this.getAttribute("selector")) : this.children).reverse();
    } else {
      return this._items = this._items || Array.from(this.hasAttribute("selector") ? this.querySelectorAll(this.getAttribute("selector")) : this.children);
    }
  }
  get visibleItems() {
    return this.items.filter((item) => item.offsetParent !== null);
  }
  get selectedIndex() {
    return this._selectedIndex = this._selectedIndex ?? parseInt(this.getAttribute("initial-index") || 0);
  }
  get selectedIndexAmongVisible() {
    return this.visibleItems.indexOf(this.selectedSlide);
  }
  get loop() {
    return false;
  }
  get selectedSlide() {
    return this.items[this.selectedIndex];
  }
  get previousSlide() {
    return this.visibleItems[this.loop ? (this.selectedIndexAmongVisible - 1 + this.visibleItems.length) % this.visibleItems.length : Math.max(this.selectedIndexAmongVisible - 1, 0)];
  }
  get nextSlide() {
    return this.visibleItems[this.loop ? (this.selectedIndexAmongVisible + 1 + this.visibleItems.length) % this.visibleItems.length : Math.min(this.selectedIndexAmongVisible + 1, this.visibleItems.length - 1)];
  }
  previous(animate19 = true) {
    this.select(this.items.indexOf(this.previousSlide), { direction: "previous", animate: animate19 });
  }
  next(animate19 = true) {
    this.select(this.items.indexOf(this.nextSlide), { direction: "next", animate: animate19 });
  }
  _transitionTo(fromSlide, toSlide, options = {}) {
  }
  _adjustHeight() {
    if (this.hasAttribute("adaptive-height") && this.selectedSlide.clientHeight !== this.clientHeight) {
      this.style.maxHeight = `${this.selectedSlide.clientHeight}px`;
    }
  }
  _filterItems(event) {
    this.items.forEach((item, index) => item.hidden = event.detail.filteredIndexes.includes(index));
  }
  _preloadImages() {
    requestAnimationFrame(() => {
      [this.previousSlide, this.nextSlide].forEach((item) => {
        if (item) {
          Array.from(item.querySelectorAll('img[loading="lazy"]')).forEach((img) => img.removeAttribute("loading"));
        }
      });
    });
  }
  _dispatchEvent(eventName, index) {
    this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: { slide: this.items[index], index } }));
  }
};

// js/common/carousel/effect-carousel.js
var EffectCarousel = class extends BaseCarousel {
  connectedCallback() {
    if (this.items.length > 1 && this.hasAttribute("autoplay")) {
      this._player = new Player(this.getAttribute("autoplay"));
      this._player.addEventListener("player:end", this.next.bind(this));
      inView3(this, () => this._player.resume(true));
      if (Shopify.designMode) {
        this.addEventListener("shopify:block:select", () => this._player.stop());
        this.addEventListener("shopify:block:deselect", () => this._player.start());
      }
    }
    super.connectedCallback();
    if (this.items.length > 1) {
      if (this.swipeable) {
        new GestureArea(this, { signal: this._abortController.signal });
        this.addEventListener("swipeleft", this.next, { signal: this._abortController.signal });
        this.addEventListener("swiperight", this.previous, { signal: this._abortController.signal });
      }
    }
  }
  get player() {
    return this._player;
  }
  get loop() {
    return true;
  }
  get swipeable() {
    return this.getAttribute("swipeable") !== "false";
  }
  async select(index, { direction, animate: animate19 = true } = {}) {
    const indexBeforeChange = this.selectedIndex;
    this._selectedIndex = index;
    this._dispatchEvent("carousel:select", index);
    if (!direction) {
      direction = index > indexBeforeChange ? "next" : "previous";
    }
    if (index !== indexBeforeChange) {
      const [fromSlide, toSlide] = [this.items[indexBeforeChange], this.items[index]];
      this._dispatchEvent("carousel:change", index);
      this.player?.pause();
      await this._transitionTo(fromSlide, toSlide, { direction, animate: animate19 });
      this.player?.resume(true);
      this._dispatchEvent("carousel:settle", index);
    }
  }
  /**
   * Perform a simple fade animation. For more complex animations, you should implement your own custom elements
   * that extends the EffectCarousel, and implement your own transition. You should make sure to return a promise
   * that resolves when the animation is finished
   */
  _transitionTo(fromSlide, toSlide, { direction = "next", animate: animate19 = true } = {}) {
    fromSlide.classList.remove("is-selected");
    toSlide.classList.add("is-selected");
    const timelineControls = timeline3([
      [fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"], zIndex: 0 }, { zIndex: { easing: "step-end" } }],
      [toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"], zIndex: 1 }, { at: "<", zIndex: { easing: "step-end" } }]
    ], { duration: animate19 ? 0.3 : 0 });
    return timelineControls.finished;
  }
};
if (!window.customElements.get("effect-carousel")) {
  window.customElements.define("effect-carousel", EffectCarousel);
}

// js/common/carousel/scroll-carousel.js
var ScrollCarousel = class extends BaseCarousel {
  constructor() {
    super();
    if (window.ResizeObserver) {
      new ResizeObserver(throttle(this._adjustHeight.bind(this))).observe(this);
    }
  }
  connectedCallback() {
    this._hasPendingProgrammaticScroll = false;
    this._scrollArea = new ScrollArea(this, this._abortController);
    super.connectedCallback();
    this.addEventListener("scroll", throttle(this._onCarouselScroll.bind(this)), { signal: this._abortController.signal });
    this.addEventListener("scrollend", this._onScrollSettled, { signal: this._abortController.signal });
  }
  get itemOffset() {
    return this.visibleItems.length < 2 ? 0 : this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
  }
  get slidesPerPage() {
    return this.visibleItems.length < 2 ? 1 : Math.floor((this.clientWidth - this.visibleItems[0].offsetLeft) / (Math.abs(this.itemOffset) - (parseInt(getComputedStyle(this).gap) || 0)));
  }
  get totalPages() {
    return this.visibleItems.length < 2 ? 1 : this.visibleItems.length - this.slidesPerPage + 1;
  }
  select(index, { animate: animate19 = true, force = false } = {}) {
    const indexBeforeChange = this.selectedIndex;
    if (!this.offsetParent || this._scrollArea.scrollDirection === "none") {
      return this._selectedIndex = index;
    }
    const indexAmongVisible = this.visibleItems.indexOf(this.items[index]);
    index = this.items.indexOf(this.visibleItems[Math.min(this.totalPages, indexAmongVisible)]);
    this._selectedIndex = index;
    this._dispatchEvent("carousel:select", index);
    if (index !== indexBeforeChange || force) {
      const [fromSlide, toSlide] = [this.items[indexBeforeChange], this.items[index]];
      this._dispatchEvent("carousel:change", index);
      this._transitionTo(fromSlide, toSlide, { animate: animate19 });
    }
  }
  /**
   * Transition using the scrollTo method. To prevent the intersection observer to caught up the change, we set up
   * a "hasPendingProgrammaticScroll" variable to true, that is set back to false once the scroll has settled
   */
  _transitionTo(fromSlide, toSlide, { animate: animate19 = true } = {}) {
    fromSlide.classList.remove("is-selected");
    toSlide.classList.add("is-selected");
    let slideAlign = this._extractSlideAlign(toSlide), scrollAmount = 0;
    switch (slideAlign) {
      case "start":
        scrollAmount = this.itemOffset * this.visibleItems.indexOf(toSlide);
        break;
      case "center":
        scrollAmount = toSlide.offsetLeft - (this.clientWidth / 2 - (parseInt(getComputedStyle(this).scrollPaddingInline) || 0)) + toSlide.clientWidth / 2;
        break;
    }
    this._hasPendingProgrammaticScroll = animate19;
    this.scrollTo({ left: scrollAmount, behavior: animate19 ? "smooth" : "auto" });
  }
  /**
   * Update the index when manually scrolling (which allows to update the controls)
   */
  _onCarouselScroll() {
    if (this._hasPendingProgrammaticScroll || this._scrollArea.scrollDirection === "none") {
      return;
    }
    const newIndex = this.items.indexOf(this.visibleItems[Math.round(this.scrollLeft / this.itemOffset)]);
    if (newIndex !== this.selectedIndex) {
      this._selectedIndex = newIndex;
      this._dispatchEvent("carousel:select", this.selectedIndex);
      this._dispatchEvent("carousel:change", this.selectedIndex);
    }
  }
  /**
   * On the scroll has settled we dispatch the event (which covers both programmatic scroll and swipe)
   */
  _onScrollSettled() {
    this.items.forEach((item) => item.classList.remove("is-selected"));
    this.selectedSlide.classList.add("is-selected");
    this._hasPendingProgrammaticScroll = false;
    this._dispatchEvent("carousel:settle", this.selectedIndex);
  }
  _adjustHeight() {
    this.style.maxHeight = null;
    if (this._scrollArea?.scrollDirection !== "none") {
      super._adjustHeight();
    }
  }
  _extractSlideAlign(slide) {
    return getComputedStyle(slide).scrollSnapAlign === "center" ? "center" : "start";
  }
};
if (!window.customElements.get("scroll-carousel")) {
  window.customElements.define("scroll-carousel", ScrollCarousel);
}

// js/common/cart/cart-count.js
import { animate as animate3 } from "vendor";

// js/common/cart/fetch-cart.js
var createCartPromise = () => {
  return new Promise(async (resolve) => {
    resolve(await (await fetch(`${Shopify.routes.root}cart.js`)).json());
  });
};
var fetchCart = createCartPromise();
document.addEventListener("cart:refresh", () => {
  fetchCart = createCartPromise();
});
document.addEventListener("cart:change", (event) => {
  fetchCart = event.detail["cart"];
});
window.addEventListener("pageshow", (event) => {
  if (event.persisted) {
    fetchCart = createCartPromise();
  }
});

// js/common/cart/cart-count.js
var CartCount = class extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(document.createRange().createContextualFragment("<span><slot></slot></span>"));
  }
  connectedCallback() {
    this._abortController = new AbortController();
    document.addEventListener("cart:change", (event) => this.itemCount = event.detail["cart"]["item_count"], { signal: this._abortController.signal });
    document.addEventListener("cart:refresh", this._updateFromServer.bind(this), { signal: this._abortController.signal });
    window.addEventListener("pageshow", this._updateFromServer.bind(this));
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  async _updateFromServer() {
    this.itemCount = (await fetchCart)["item_count"];
  }
  get itemCount() {
    return parseInt(this.innerText);
  }
  set itemCount(count) {
    if (this.itemCount === count) {
      return;
    }
    if (count === 0) {
      animate3(this, { opacity: 0 }, { duration: 0.1 });
      this.innerText = count;
    } else if (this.itemCount === 0) {
      animate3(this, { opacity: 1 }, { duration: 0.1 });
      this.innerText = count;
    } else {
      (async () => {
        await animate3(this.shadowRoot.firstElementChild, { transform: ["translateY(-50%)"], opacity: 0 }, { duration: 0.25, easing: [1, 0, 0, 1] }).finished;
        this.innerText = count;
        animate3(this.shadowRoot.firstElementChild, { transform: ["translateY(50%)", "translateY(0)"], opacity: 1 }, { duration: 0.25, easing: [1, 0, 0, 1] });
      })();
    }
  }
};
if (!window.customElements.get("cart-count")) {
  window.customElements.define("cart-count", CartCount);
}

// js/common/cart/cart-drawer.js
import { animate as animate4 } from "vendor";

// js/common/overlay/dialog-element.js
import { FocusTrap, Delegate } from "vendor";
var DialogElement = class _DialogElement extends HTMLElement {
  static get observedAttributes() {
    return ["id", "open"];
  }
  static #lockLayerCount = 0;
  #isLocked = false;
  constructor() {
    super();
    this.addEventListener("dialog:force-close", (event) => {
      this.hide();
      event.stopPropagation();
    });
  }
  connectedCallback() {
    if (this.id) {
      this.delegate.off().on("click", `[aria-controls="${this.id}"]`, this._onToggleClicked.bind(this));
    }
    this._abortController = new AbortController();
    this.setAttribute("role", "dialog");
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.show(!event.detail.load), { signal: this._abortController.signal });
      this.addEventListener("shopify:block:deselect", this.hide, { signal: this._abortController.signal });
      this._shopifySection = this._shopifySection || this.closest(".shopify-section");
      if (this._shopifySection) {
        if (this.hasAttribute("handle-section-events")) {
          this._shopifySection.addEventListener("shopify:section:select", (event) => this.show(!event.detail.load), { signal: this._abortController.signal });
          this._shopifySection.addEventListener("shopify:section:deselect", this.hide.bind(this), { signal: this._abortController.signal });
        }
        this._shopifySection.addEventListener("shopify:section:unload", () => this.remove(), { signal: this._abortController.signal });
      }
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
    this.delegate.off();
    this.focusTrap?.deactivate({ onDeactivate: () => {
    } });
    if (this.#isLocked) {
      this.#isLocked = false;
      document.documentElement.classList.toggle("lock", --_DialogElement.#lockLayerCount > 0);
    }
  }
  show(animate19 = true) {
    if (this.open) {
      return;
    }
    this.setAttribute("open", animate19 ? "" : "immediate");
    return waitForEvent(this, "dialog:after-show");
  }
  hide() {
    if (!this.open) {
      return;
    }
    this.removeAttribute("open");
    return waitForEvent(this, "dialog:after-hide");
  }
  get delegate() {
    return this._delegate = this._delegate || new Delegate(document.body);
  }
  get controls() {
    return Array.from(this.getRootNode().querySelectorAll(`[aria-controls="${this.id}"]`));
  }
  get open() {
    return this.hasAttribute("open");
  }
  get shouldTrapFocus() {
    return true;
  }
  get shouldLock() {
    return false;
  }
  /**
   * Sometimes (especially for drawer) we need to ensure that an element is on top of everything else. To do that,
   * we need to move the element to the body. We are doing that on open, and then restore the initial position on
   * close
   */
  get shouldAppendToBody() {
    return false;
  }
  get initialFocus() {
    return this.hasAttribute("initial-focus") ? this.getAttribute("initial-focus") : this.hasAttribute("tabindex") ? this : this.querySelector('input:not([type="hidden"])') || false;
  }
  get preventScrollWhenTrapped() {
    return true;
  }
  get focusTrap() {
    return this._focusTrap = this._focusTrap || new FocusTrap.createFocusTrap(this, {
      onDeactivate: this.hide.bind(this),
      allowOutsideClick: this._allowOutsideClick.bind(this),
      initialFocus: window.matchMedia("screen and (pointer: fine)").matches ? this.initialFocus : void 0,
      fallbackFocus: this,
      tabbableOptions: {
        getShadowRoot: true
      },
      preventScroll: this.preventScrollWhenTrapped
    });
  }
  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case "id":
        if (newValue) {
          this.delegate.off().on("click", `[aria-controls="${this.id}"]`, this._onToggleClicked.bind(this));
        }
        break;
      case "open":
        this.controls.forEach((toggle) => toggle.setAttribute("aria-expanded", newValue === null ? "false" : "true"));
        if (oldValue === null && (newValue === "" || newValue === "immediate")) {
          this.removeAttribute("inert");
          this._originalParentBeforeAppend = null;
          if (this.shouldAppendToBody && this.parentElement !== document.body) {
            this._originalParentBeforeAppend = this.parentElement;
            document.body.append(this);
          }
          const showTransitionPromise = this._showTransition(newValue !== "immediate") || Promise.resolve();
          showTransitionPromise.then(() => {
            this.dispatchEvent(new CustomEvent("dialog:after-show", { bubbles: true }));
          });
          if (this.shouldTrapFocus) {
            this.focusTrap.activate({
              checkCanFocusTrap: () => showTransitionPromise
            });
          }
          if (this.shouldLock) {
            _DialogElement.#lockLayerCount += 1;
            this.#isLocked = true;
            document.documentElement.classList.add("lock");
          }
        } else if (oldValue !== null && newValue === null) {
          this.setAttribute("inert", "");
          const hideTransitionPromise = this._hideTransition() || Promise.resolve();
          hideTransitionPromise.then(() => {
            if (this.parentElement === document.body && this._originalParentBeforeAppend) {
              this._originalParentBeforeAppend.appendChild(this);
              this._originalParentBeforeAppend = null;
            }
            this.dispatchEvent(new CustomEvent("dialog:after-hide", { bubbles: true }));
          });
          this.focusTrap?.deactivate({
            checkCanReturnFocus: () => hideTransitionPromise
          });
          if (this.shouldLock) {
            this.#isLocked = false;
            document.documentElement.classList.toggle("lock", --_DialogElement.#lockLayerCount > 0);
          }
        }
        this.dispatchEvent(new CustomEvent("toggle", { bubbles: true }));
        break;
    }
  }
  /* Those methods are used to perform custom show/hide transition, and must return a promise */
  _showTransition(animate19 = true) {
  }
  _hideTransition() {
  }
  /* By default, a focus element is deactivated when you click outside it */
  _allowOutsideClick(event) {
    if ("TouchEvent" in window && event instanceof TouchEvent) {
      return this._allowOutsideClickTouch(event);
    } else {
      return this._allowOutsideClickMouse(event);
    }
  }
  _allowOutsideClickTouch(event) {
    event.target.addEventListener("touchend", (subEvent) => {
      const endTarget = document.elementFromPoint(subEvent.changedTouches.item(0).clientX, subEvent.changedTouches.item(0).clientY);
      if (!this.contains(endTarget)) {
        this.hide();
      }
    }, { once: true });
    return false;
  }
  _allowOutsideClickMouse(event) {
    if (event.type !== "click") {
      return false;
    }
    if (!this.contains(event.target)) {
      this.hide();
    }
    let target = event.target, closestControl = event.target.closest("[aria-controls]");
    if (closestControl && closestControl.getAttribute("aria-controls") === this.id) {
      target = closestControl;
    }
    return this.id !== target.getAttribute("aria-controls");
  }
  _onToggleClicked(event) {
    event?.preventDefault();
    this.open ? this.hide() : this.show();
  }
};
var CloseButton = class extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener("click", () => this.dispatchEvent(new CustomEvent("dialog:force-close", { bubbles: true, cancelable: true, composed: true })));
  }
};
if (!window.customElements.get("dialog-element")) {
  window.customElements.define("dialog-element", DialogElement);
}
if (!window.customElements.get("close-button")) {
  window.customElements.define("close-button", CloseButton, { extends: "button" });
}

// js/common/overlay/drawer.js
import { animate as motionAnimate, timeline as motionTimeline } from "vendor";
var reduceDrawerAnimation = window.matchMedia("(prefers-reduced-motion: reduce)").matches || window.themeVariables.settings.reduceDrawerAnimation;
var Drawer = class extends DialogElement {
  constructor() {
    super();
    const template2 = document.getElementById(this.template).content.cloneNode(true);
    this.attachShadow({ mode: "open" }).appendChild(template2);
    this.shadowRoot.addEventListener("slotchange", (event) => this._updateSlotVisibility(event.target));
  }
  connectedCallback() {
    super.connectedCallback();
    this.setAttribute("aria-modal", "true");
    this.shadowRoot.querySelector('[part="overlay"]')?.addEventListener("click", this.hide.bind(this), { signal: this._abortController.signal });
    Array.from(this.shadowRoot.querySelectorAll("slot")).forEach((slot) => this._updateSlotVisibility(slot));
  }
  get template() {
    return this.getAttribute("template") || "drawer-default-template";
  }
  get shouldLock() {
    return true;
  }
  get shouldAppendToBody() {
    return true;
  }
  get openFrom() {
    return window.matchMedia(`${window.themeVariables.breakpoints["sm-max"]}`).matches ? "bottom" : this.getAttribute("open-from") || "right";
  }
  _getClipPathProperties() {
    switch (this.openFrom) {
      case "left":
        return document.dir === "ltr" ? ["inset(0 100% 0 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"] : ["inset(0 0 0 100% round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
      case "right":
        return document.dir === "ltr" ? ["inset(0 0 0 100% round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm)"] : ["inset(0 100% 0 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
      case "bottom":
        return ["inset(100% 0 0 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
      case "top":
        return ["inset(0 0 100% 0 round var(--rounded-sm))", "inset(0 0 0 0 round var(--rounded-sm))"];
    }
  }
  _setInitialPosition() {
    this.style.left = document.dir === "ltr" && this.openFrom === "left" || document.dir === "rtl" && this.openFrom === "right" ? "0px" : "auto";
    this.style.right = this.style.left === "auto" ? "0px" : "auto";
    this.style.bottom = this.openFrom === "bottom" ? "0px" : null;
    this.style.top = this.style.bottom === "" ? "0px" : null;
  }
  _showTransition(animate19 = true) {
    let animationControls;
    this._setInitialPosition();
    if (reduceDrawerAnimation) {
      animationControls = motionAnimate(this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.2 });
    } else {
      let content = this.shadowRoot.querySelector('[part="content"]'), closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');
      animationControls = motionTimeline([
        [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
        [content, { clipPath: this._getClipPathProperties() }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
        [content.children, { opacity: [0, 1] }, { duration: 0.15 }],
        [closeButton, { opacity: [0, 1] }, { at: "<", duration: 0.15 }]
      ]);
    }
    animate19 ? animationControls.play() : animationControls.finish();
    return animationControls.finished.then(() => this.classList.add("show-close-cursor"));
  }
  _hideTransition() {
    let animationControls;
    if (reduceDrawerAnimation) {
      animationControls = motionAnimate(this, { opacity: [1, 0], visibility: ["visibility", "hidden"] }, { duration: 0.2 });
    } else {
      let content = this.shadowRoot.querySelector('[part="content"]'), closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');
      animationControls = motionTimeline([
        [closeButton, { opacity: [null, 0] }, { duration: 0.15 }],
        [content.children, { opacity: [null, 0] }, { at: "<", duration: 0.15 }],
        [content, { clipPath: this._getClipPathProperties().reverse() }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
        [this, { opacity: [null, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 }]
      ]);
    }
    return animationControls.finished.then(() => this.classList.remove("show-close-cursor"));
  }
  _updateSlotVisibility(slot) {
    if (!["header", "footer", "body"].includes(slot.name)) {
      return;
    }
    slot.parentElement.hidden = slot.assignedElements({ flatten: true }).length === 0;
  }
};
if (!window.customElements.get("x-drawer")) {
  window.customElements.define("x-drawer", Drawer);
}

// js/common/overlay/popover.js
import { animate as motionAnimate2, timeline as motionTimeline2 } from "vendor";
var Popover = class extends DialogElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(document.getElementById(this.template).content.cloneNode(true));
  }
  connectedCallback() {
    super.connectedCallback();
    this.shadowRoot.querySelector('[part="overlay"]')?.addEventListener("click", this.hide.bind(this), { signal: this._abortController.signal });
    this.controls.forEach((control) => control.setAttribute("aria-haspopup", "dialog"));
    if (this.hasAttribute("close-on-listbox-select")) {
      if (this.querySelector("x-listbox")) {
        this.addEventListener("listbox:select", this.hide, { signal: this._abortController.signal });
      } else {
        this.addEventListener("change", this.hide, { signal: this._abortController.signal });
      }
    }
  }
  get template() {
    return this.getAttribute("template") || "popover-default-template";
  }
  get shouldLock() {
    return window.matchMedia("screen and (max-width: 999px)").matches;
  }
  get shouldAppendToBody() {
    return window.matchMedia("screen and (max-width: 999px)").matches;
  }
  get anchor() {
    return {
      vertical: this.getAttribute("anchor-vertical") || "start",
      horizontal: this.getAttribute("anchor-horizontal") || "end"
    };
  }
  _showTransition(animate19 = true) {
    let animationControls, content = this.shadowRoot.querySelector('[part="content"]'), closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');
    this.style.display = "block";
    if (window.matchMedia("screen and (max-width: 999px)").matches) {
      this.style.insetInlineStart = "0px";
      this.style.insetInlineEnd = null;
      this.style.insetBlockEnd = "0px";
      this.style.insetBlockStart = null;
      animationControls = motionTimeline2([
        [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
        [content, { clipPath: ["inset(100% 0 0 0 round 8px)", "inset(0 0 0 0 round 8px"] }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
        [content.children, { opacity: [0, 1] }, { duration: 0.15 }],
        [closeButton, { opacity: [0, 1] }, { at: "<", duration: 0.15 }]
      ]);
    } else {
      let spacingBlockValue = "var(--popover-anchor-block-spacing)", spacingInlineValue = "var(--popover-anchor-inline-spacing)";
      this.style.insetInlineStart = this.anchor.horizontal === "start" ? spacingInlineValue : null;
      this.style.insetInlineEnd = this.anchor.horizontal === "end" ? spacingInlineValue : null;
      if (this.anchor.vertical === "center") {
        this.style.insetBlockStart = `calc(50% - ${parseInt(this.clientHeight / 2)}px)`;
        this.style.insetBlockEnd = null;
      } else {
        this.style.insetBlockStart = this.anchor.vertical === "end" ? `calc(100% + ${spacingBlockValue})` : null;
        this.style.insetBlockEnd = this.anchor.vertical === "start" ? `calc(100% + ${spacingBlockValue})` : null;
      }
      animationControls = motionTimeline2([
        [this, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.15 }],
        [content, { clipPath: "none" }, { at: "<", duration: 0 }],
        [content.children, { opacity: 1 }, { at: "<", duration: 0 }]
      ]);
    }
    animate19 ? animationControls.play() : animationControls.finish();
    return animationControls.finished;
  }
  _hideTransition() {
    let animationControls;
    if (window.matchMedia("screen and (max-width: 999px)").matches) {
      let content = this.shadowRoot.querySelector('[part="content"]'), closeButton = this.shadowRoot.querySelector('[part="outside-close-button"]');
      animationControls = motionTimeline2([
        [closeButton, { opacity: [null, 0] }, { duration: 0.15 }],
        [content.children, { opacity: [null, 0] }, { at: "<", duration: 0.15 }],
        [content, { clipPath: [null, "inset(100% 0 0 0 round 8px)"] }, { duration: 0.4, easing: [0.86, 0, 0.07, 1] }],
        [this, { opacity: [null, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 }]
      ]);
    } else {
      animationControls = motionAnimate2(this, { opacity: [null, 0], visibility: ["visible", "hidden"] }, { duration: 0.15 });
    }
    return animationControls.finished.then(() => this.style.display = "none");
  }
};
if (!window.customElements.get("x-popover")) {
  window.customElements.define("x-popover", Popover);
}

// js/common/overlay/privacy-bar.js
import { Delegate as Delegate2 } from "vendor";
var PrivacyBar = class extends HTMLElement {
  constructor() {
    super();
    this._delegate = new Delegate2(this);
    window.Shopify.loadFeatures([{
      name: "consent-tracking-api",
      version: "0.1",
      onLoad: this._onConsentLibraryLoaded.bind(this)
    }]);
    if (Shopify.designMode) {
      const section = this.closest(".shopify-section");
      section.addEventListener("shopify:section:select", this.show.bind(this));
      section.addEventListener("shopify:section:deselect", this.hide.bind(this));
    }
  }
  connectedCallback() {
    this._delegate.on("click", '[data-action="accept"]', this._acceptPolicy.bind(this));
    this._delegate.on("click", '[data-action="decline"]', this._declinePolicy.bind(this));
    this._delegate.on("click", '[data-action="close"]', this.hide.bind(this));
  }
  disconnectedCallback() {
    this._delegate.off();
  }
  show() {
    this.hidden = false;
  }
  hide() {
    this.hidden = true;
  }
  _onConsentLibraryLoaded() {
    if (window.Shopify.customerPrivacy?.shouldShowBanner()) {
      this.show();
    }
  }
  _acceptPolicy() {
    window.Shopify.customerPrivacy?.setTrackingConsent(true, this.hide.bind(this));
  }
  _declinePolicy() {
    window.Shopify.customerPrivacy?.setTrackingConsent(false, this.hide.bind(this));
  }
};
if (!window.customElements.get("privacy-bar")) {
  window.customElements.define("privacy-bar", PrivacyBar);
}

// js/common/cart/cart-drawer.js
var CartDrawer = class extends Drawer {
  constructor() {
    super();
    this._onPrepareBundledSectionsListener = this._onPrepareBundledSections.bind(this);
    this._onCartChangedListener = this._onCartChanged.bind(this);
    this._onCartRefreshListener = this._onCartRefresh.bind(this);
    this._onVariantAddedListener = this._onVariantAdded.bind(this);
    window.addEventListener("pageshow", this._onPageShow.bind(this));
  }
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("cart:prepare-bundled-sections", this._onPrepareBundledSectionsListener);
    document.addEventListener("cart:change", this._onCartChangedListener);
    document.addEventListener("cart:refresh", this._onCartRefreshListener);
    document.addEventListener("variant:add", this._onVariantAddedListener);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("cart:prepare-bundled-sections", this._onPrepareBundledSectionsListener);
    document.removeEventListener("cart:change", this._onCartChangedListener);
    document.removeEventListener("cart:refresh", this._onCartRefreshListener);
    document.removeEventListener("variant:add", this._onVariantAddedListener);
  }
  get shouldAppendToBody() {
    return false;
  }
  get openFrom() {
    return "right";
  }
  _onPrepareBundledSections(event) {
    event.detail.sections.push(extractSectionId(this));
  }
  /**
   * Update the cart drawer content when cart content changes.
   */
  async _onCartChanged(event) {
    const updatedDrawerContent = new DOMParser().parseFromString(event.detail.cart["sections"][extractSectionId(this)], "text/html");
    if (event.detail.cart["item_count"] > 0) {
      const currentInner = this.querySelector(".cart-drawer__inner"), updatedInner = updatedDrawerContent.querySelector(".cart-drawer__inner");
      if (!currentInner) {
        this.replaceChildren(document.createRange().createContextualFragment(updatedDrawerContent.querySelector(".cart-drawer").innerHTML));
      } else {
        setTimeout(() => {
          currentInner.innerHTML = updatedInner.innerHTML;
        }, event.detail.baseEvent === "variant:add" ? 0 : 1250);
        this.querySelector('[slot="footer"]').replaceChildren(...updatedDrawerContent.querySelector('[slot="footer"]').childNodes);
      }
    } else {
      await animate4(this.children, { opacity: 0 }, { duration: 0.15 }).finished;
      this.replaceChildren(...updatedDrawerContent.querySelector(".cart-drawer").childNodes);
      animate4(this.querySelector(".empty-state"), { opacity: [0, 1], transform: ["translateY(20px)", "translateY(0)"] }, { duration: 0.15 });
    }
  }
  /**
   * Handle the case when the page is served from BF cache
   */
  _onPageShow(event) {
    if (!event.persisted) {
      return;
    }
    this._onCartRefresh();
  }
  /**
   * Listeners called when a new variant has been added
   */
  _onVariantAdded(event) {
    if (window.themeVariables.settings.cartType !== "drawer" || event.detail?.blockCartDrawerOpening) {
      return;
    }
    this.show();
  }
  /**
   * Force a complete refresh of the cart drawer (this is called by dispatching the 'cart:refresh' on the document)
   */
  async _onCartRefresh() {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = await (await fetch(`${window.Shopify.routes.root}?section_id=${extractSectionId(this)}`)).text();
    this.replaceChildren(...tempDiv.querySelector("#cart-drawer").children);
  }
};
var CartNotificationDrawer = class extends Drawer {
  constructor() {
    super();
    this._onVariantAddedListener = this._onVariantAdded.bind(this);
  }
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener("variant:add", this._onVariantAddedListener);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("variant:add", this._onVariantAddedListener);
  }
  async show() {
    this.style.display = "block";
    return super.show();
  }
  async hide() {
    super.hide()?.then(() => {
      this.style.display = "none";
    });
  }
  _onVariantAdded(event) {
    if (window.themeVariables.settings.cartType !== "popover" || event.detail?.blockCartDrawerOpening) {
      return;
    }
    const tempContent = document.createElement("div");
    tempContent.innerHTML = event.detail.cart["sections"]["variant-added"];
    this.replaceChildren(...tempContent.querySelector(".shopify-section").children);
    this.show();
  }
};
var LineItem = class extends HTMLElement {
  connectedCallback() {
    this.pillLoaderElement = this.querySelector("pill-loader");
    this.addEventListener("line-item:will-change", this._onWillChange.bind(this));
    this.addEventListener("line-item:error", this._onErrored.bind(this));
    this.addEventListener("line-item:change", this._onChanged.bind(this));
  }
  _onWillChange() {
    this.pillLoaderElement.setAttribute("aria-busy", "true");
  }
  _onErrored() {
    this.pillLoaderElement.removeAttribute("aria-busy");
  }
  async _onChanged(event) {
    this.pillLoaderElement.removeAttribute("aria-busy");
    if (event.detail.cart["item_count"] === 0 || event.detail.quantity !== 0) {
      return;
    }
    let marginCompensation = 0;
    if (this.nextElementSibling) {
      marginCompensation = `-${getComputedStyle(this.nextElementSibling).paddingTop}`;
    }
    await animate4(this, { height: [`${this.clientHeight}px`, 0], marginBottom: [0, marginCompensation], overflow: "hidden", opacity: [1, 0] }, { duration: 0.2, easing: "ease" }).finished;
    this.remove();
  }
};
if (!window.customElements.get("cart-drawer")) {
  window.customElements.define("cart-drawer", CartDrawer);
}
if (!window.customElements.get("cart-notification-drawer")) {
  window.customElements.define("cart-notification-drawer", CartNotificationDrawer);
}
if (!window.customElements.get("line-item")) {
  window.customElements.define("line-item", LineItem);
}

// js/common/cart/cart-note.js
var CartNote = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", this._onNoteChanged);
  }
  _onNoteChanged(event) {
    if (event.target.getAttribute("name") !== "note") {
      return;
    }
    fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: event.target.value }),
      keepalive: true
      // Allows to make sure the request is fired even when submitting the form
    });
  }
};
var CartNoteDialog = class extends DialogElement {
  constructor() {
    super();
    this.addEventListener("change", this._onNoteChanged);
  }
  _onNoteChanged(event) {
    if (event.target.value === "") {
      this.controls.forEach((control) => control.innerHTML = `<span class="link text-sm text-subdued">${window.themeVariables.strings.addOrderNote}</span>`);
    } else {
      this.controls.forEach((control) => control.innerHTML = `<span class="link text-sm text-subdued">${window.themeVariables.strings.editOrderNote}</span>`);
    }
  }
};
if (!window.customElements.get("cart-note")) {
  window.customElements.define("cart-note", CartNote);
}
if (!window.customElements.get("cart-note-dialog")) {
  window.customElements.define("cart-note-dialog", CartNoteDialog);
}

// js/common/cart/free-shipping-bar.js
var FreeShippingBar = class extends HTMLElement {
  static get observedAttributes() {
    return ["threshold", "total-price"];
  }
  constructor() {
    super();
    this._onCartChangedListener = this._onCartChanged.bind(this);
    this._currencyFormatter = new Intl.NumberFormat(Shopify.locale, { style: "currency", currency: Shopify.currency.active });
  }
  async connectedCallback() {
    this.threshold = Math.round(this.threshold * (Shopify.currency.rate || 1));
    document.addEventListener("cart:change", this._onCartChangedListener);
  }
  disconnectedCallback() {
    document.removeEventListener("cart:change", this._onCartChangedListener);
  }
  get threshold() {
    return parseFloat(this.getAttribute("threshold"));
  }
  set threshold(value) {
    this.setAttribute("threshold", value);
  }
  get totalPrice() {
    return parseFloat(this.getAttribute("total-price"));
  }
  set totalPrice(value) {
    this.setAttribute("total-price", value);
  }
  async attributeChangedCallback(attribute, oldValue, newValue) {
    await window.customElements.whenDefined("progress-bar");
    const progressBarElement = this.querySelector("progress-bar");
    switch (attribute) {
      case "threshold":
        progressBarElement.valueMax = newValue;
        break;
      case "total-price":
        progressBarElement.valueNow = newValue;
        break;
    }
    this._updateMessage();
  }
  _updateMessage() {
    const messageElement = this.querySelector("span");
    if (this.totalPrice >= this.threshold) {
      messageElement.innerHTML = this.getAttribute("reached-message");
    } else {
      const replacement = `<span class="bold text-accent">${this._currencyFormatter.format((this.threshold - this.totalPrice) / 100).replace(/\$/g, "$$$$")}</span>`;
      messageElement.innerHTML = this.getAttribute("unreached-message").replace(new RegExp("({{.*}})", "g"), replacement);
    }
  }
  _onCartChanged(event) {
    const priceForItems = event.detail["cart"]["items"].filter((item) => item["requires_shipping"]).reduce((sum, item) => sum + item["final_line_price"], 0), cartDiscount = event.detail["cart"]["cart_level_discount_applications"].reduce((sum, discountAllocation) => sum + discountAllocation["total_allocated_amount"], 0);
    this.totalPrice = priceForItems - cartDiscount;
  }
};
if (!window.customElements.get("free-shipping-bar")) {
  window.customElements.define("free-shipping-bar", FreeShippingBar);
}

// js/common/cart/line-item-quantity.js
var LineItemQuantity = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("change", this._onQuantityChanged);
    this.addEventListener("click", this._onRemoveLinkClicked);
  }
  _onQuantityChanged(event) {
    if (!event.target.hasAttribute("data-line-key")) {
      return;
    }
    this._changeLineItemQuantity(event.target.getAttribute("data-line-key"), parseInt(event.target.value));
  }
  _onRemoveLinkClicked(event) {
    if (event.target.tagName !== "A" || !event.target.href.includes("/cart/change")) {
      return;
    }
    event.preventDefault();
    const url = new URL(event.target.href);
    this._changeLineItemQuantity(url.searchParams.get("id"), parseInt(url.searchParams.get("quantity")));
  }
  async _changeLineItemQuantity(lineKey, targetQuantity) {
    const lineItem = this.closest("line-item");
    lineItem?.dispatchEvent(new CustomEvent("line-item:will-change", { bubbles: true, detail: { targetQuantity } }));
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
    const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: targetQuantity,
        sections: sectionsToBundle
      })
    });
    if (!response.ok) {
      const responseContent = await response.json();
      this.closest(".line-item, tr").querySelector('[role="alert"]')?.remove();
      const errorSvg = `<svg width="14" viewBox="0 0 18 18">
        <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="#eb001b"></path>
        <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
      </svg>`;
      this.closest(".line-item, tr").querySelector(".line-item__info").insertAdjacentHTML("beforeend", `<p class="text-with-icon text-error text-xs" role="alert">${errorSvg} ${responseContent["description"]}</p>`);
      this.querySelector(".quantity-input").value = this.querySelector(".quantity-input").defaultValue;
      lineItem?.dispatchEvent(new CustomEvent("line-item:error", { bubbles: true }));
    } else {
      const cartContent = await response.json();
      if (window.themeVariables.settings.pageType === "cart") {
        window.location.reload();
      } else {
        const lineItemAfterChange = cartContent["items"].filter((lineItem2) => lineItem2["key"] === lineKey);
        lineItem?.dispatchEvent(new CustomEvent("line-item:change", {
          bubbles: true,
          detail: {
            quantity: lineItemAfterChange.length === 0 ? 0 : lineItemAfterChange[0]["quantity"],
            cart: cartContent
          }
        }));
        document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "line-item:change",
            cart: cartContent
          }
        }));
      }
    }
  }
};
if (!window.customElements.get("line-item-quantity")) {
  window.customElements.define("line-item-quantity", LineItemQuantity);
}

// js/common/cart/shipping-estimator.js
var ShippingEstimator = class extends HTMLElement {
  constructor() {
    super();
    this._estimateShippingListener = this._estimateShipping.bind(this);
  }
  connectedCallback() {
    this.submitButton = this.querySelector('[type="submit"]');
    this.resultsElement = this.lastElementChild;
    this.submitButton.addEventListener("click", this._estimateShippingListener);
  }
  disconnectedCallback() {
    this.submitButton.removeEventListener("click", this._estimateShippingListener);
  }
  /**
   * @doc https://shopify.dev/docs/themes/ajax-api/reference/cart#generate-shipping-rates
   */
  async _estimateShipping(event) {
    event.preventDefault();
    const zip = this.querySelector('[name="address[zip]"]').value, country = this.querySelector('[name="address[country]"]').value, province = this.querySelector('[name="address[province]"]').value;
    this.submitButton.setAttribute("aria-busy", "true");
    const prepareResponse = await fetch(`${Shopify.routes.root}cart/prepare_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`, { method: "POST" });
    if (prepareResponse.ok) {
      const shippingRates = await this._getAsyncShippingRates(zip, country, province);
      this._formatShippingRates(shippingRates);
    } else {
      const jsonError = await prepareResponse.json();
      this._formatError(jsonError);
    }
    this.resultsElement.hidden = false;
    this.submitButton.removeAttribute("aria-busy");
  }
  async _getAsyncShippingRates(zip, country, province) {
    const response = await fetch(`${Shopify.routes.root}cart/async_shipping_rates.json?shipping_address[zip]=${zip}&shipping_address[country]=${country}&shipping_address[province]=${province}`);
    const responseAsText = await response.text();
    if (responseAsText === "null") {
      return this._getAsyncShippingRates(zip, country, province);
    } else {
      return JSON.parse(responseAsText)["shipping_rates"];
    }
  }
  _formatShippingRates(shippingRates) {
    let formattedShippingRates = shippingRates.map((shippingRate) => {
      return `<li>${shippingRate["presentment_name"]}: ${shippingRate["currency"]} ${shippingRate["price"]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${shippingRates.length === 0 ? window.themeVariables.strings.shippingEstimatorNoResults : shippingRates.length === 1 ? window.themeVariables.strings.shippingEstimatorOneResult : window.themeVariables.strings.shippingEstimatorMultipleResults}</p>
        ${formattedShippingRates === "" ? "" : `<ul class="list-disc" role="list">${formattedShippingRates.join("")}</ul>`}
      </div>
    `;
  }
  _formatError(errors) {
    let formattedShippingRates = Object.keys(errors).map((errorKey) => {
      return `<li>${errors[errorKey]}</li>`;
    });
    this.resultsElement.innerHTML = `
      <div class="v-stack gap-2">
        <p>${window.themeVariables.strings.shippingEstimatorError}</p>
        <ul class="list-disc" role="list">${formattedShippingRates}</ul>
      </div>
    `;
  }
};
if (!window.customElements.get("shipping-estimator")) {
  window.customElements.define("shipping-estimator", ShippingEstimator);
}

// js/common/facets/facet-apply-button.js
var FacetApplyButton = class extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener("click", this._closeDrawer);
    this.form.addEventListener("change", this._updateCount.bind(this));
    this.filterCountElement = document.createElement("span");
    this.appendChild(this.filterCountElement);
  }
  connectedCallback() {
    this._updateCount();
  }
  _updateCount() {
    const form = new FormData(this.form);
    this.filterCountElement.innerText = ` (${Array.from(form.values()).filter((item) => item !== "").length})`;
  }
  async _closeDrawer() {
    this.closest("facet-drawer").hide();
  }
};
if (!window.customElements.get("facet-apply-button")) {
  window.customElements.define("facet-apply-button", FacetApplyButton, { extends: "button" });
}

// js/common/facets/facet-dialog.js
var FacetDialog = class extends DialogElement {
  get initialFocus() {
    return false;
  }
};
if (!window.customElements.get("facet-dialog")) {
  window.customElements.define("facet-dialog", FacetDialog);
}

// js/common/facets/facet-drawer.js
var FacetDrawer = class extends Drawer {
  constructor() {
    super();
    this.addEventListener("dialog:after-hide", this._submitForm);
  }
  _submitForm() {
    const form = this.querySelector("#facet-form");
    if (HTMLFormElement.prototype.requestSubmit) {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event("submit", { cancelable: true }));
    }
  }
};
if (!window.customElements.get("facet-drawer")) {
  window.customElements.define("facet-drawer", FacetDrawer);
}

// js/common/facets/facet-floating-filter.js
import { animate as animate5 } from "vendor";
var FacetFloatingFilter = class extends HTMLElement {
  connectedCallback() {
    new IntersectionObserver(this._onFooterVisibilityChanged.bind(this), { rootMargin: "50px 0px" }).observe(document.querySelector(".shopify-section--footer"));
  }
  _onFooterVisibilityChanged(entries) {
    if (entries[0].isIntersecting) {
      animate5(this, { opacity: 0, transform: [null, "translateY(15px)"], visibility: "hidden" }, { duration: 0.15 });
    } else {
      animate5(this, { opacity: 1, transform: [null, "translateY(0)"], visibility: "visible" }, { duration: 0.15 });
    }
  }
};
if (!window.customElements.get("facet-floating-filter")) {
  window.customElements.define("facet-floating-filter", FacetFloatingFilter);
}

// js/common/facets/facet-form.js
var COMPATIBLE_TAG_PREFIX = "fit-", GARAGE_STORAGE_KEY = "vs_garage_v1", SELECTED_VEHICLE_STORAGE_KEY = "vs_selected_vehicle_v1", USER_COOKIE_STORAGE_KEY = "vs_user_cookie_v1", SKU_STORAGE_KEY = "vs_sku_storage_v1", FITMENT_NOTES_STORAGE_KEY = "vs_fitment_notes_v4", COMPATIBLE_ONLY_KEY = "compatible_only", BRAKE_POSITION_KEY = "position", CATEGORIES_KEY = "categories", BRAKE_COLLECTION_HANDLES = ["brakes", "brake-pads", "brake-discs-rotors"], APP_PROXY_COLLECTION_PATH = "/apps/fitment/collection", APP_PROXY_SEARCH_PATH = "/apps/fitment/search";
/** Reads normalized category handles from a collection/search URL. */
function readCategoriesFromUrl(urlLike) {
  const url = toCollectionRequestUrl(urlLike || location.href);
  const handles = [];
  const seen = /* @__PURE__ */ new Set();
  url.searchParams.getAll(CATEGORIES_KEY).forEach((raw) => {
    String(raw || "").split(",").forEach((part) => {
      const handle = String(part || "").trim().toLowerCase();
      if (!handle || seen.has(handle)) return;
      seen.add(handle);
      handles.push(handle);
    });
  });
  return handles;
}
/** Returns true when one or more category filters are active. */
function hasActiveCategories(urlLike) {
  return readCategoriesFromUrl(urlLike).length > 0;
}
/** Returns true when listing results must come from the fitment proxy. */
function isProxyListingMode(urlLike) {
  const url = toCollectionRequestUrl(urlLike || location.href);
  const hasTagFilter = !!(url.searchParams.get("filter.v.tag") || "").startsWith(COMPATIBLE_TAG_PREFIX);
  const hasCompatibleOnly = url.searchParams.get(COMPATIBLE_ONLY_KEY) === "1";
  return hasTagFilter || hasCompatibleOnly || hasActiveCategories(url);
}
/** Writes repeated categories params onto a URLSearchParams / URL instance. */
function writeCategoriesToSearchParams(target, handles) {
  if (!target?.delete || !target?.append) return;
  target.delete(CATEGORIES_KEY);
  (Array.isArray(handles) ? handles : []).forEach((handle) => {
    const value = String(handle || "").trim();
    if (value) target.append(CATEGORIES_KEY, value);
  });
}
/**
 * Writes categories for App Proxy requests.
 * Repeated categories break Shopify proxy signature verification, so proxy calls
 * use a single comma-separated value (accepted by the backend contract).
 */
function writeCategoriesToProxyQuery(target, handles) {
  if (!target?.delete || !target?.set) return;
  target.delete(CATEGORIES_KEY);
  const values = (Array.isArray(handles) ? handles : []).map((handle) => String(handle || "").trim()).filter(Boolean);
  if (values.length === 0) return;
  target.set(CATEGORIES_KEY, values.join(","));
}
/** Resolves a display label for a category handle from the facet checkboxes. */
function getCategoryFacetLabel(handle) {
  const value = String(handle || "").trim();
  if (!value) return "";
  const escaped = typeof CSS !== "undefined" && typeof CSS.escape === "function"
    ? CSS.escape(value)
    : value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  const input = document.querySelector('input[type="checkbox"][name="' + CATEGORIES_KEY + '"][value="' + escaped + '"]');
  if (!input?.id) return value;
  const labelEscaped = typeof CSS !== "undefined" && typeof CSS.escape === "function"
    ? CSS.escape(input.id)
    : String(input.id).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  const label = document.querySelector('label[for="' + labelEscaped + '"]');
  const text = label?.textContent?.trim();
  return text || value;
}
function extractVehicleIdFromRecord(vehicle) {
  if (!vehicle || typeof vehicle !== "object") return null;
  const vehicleId = vehicle.vehicleId ?? vehicle.VehicleId ?? vehicle.id;
  return vehicleId != null && vehicleId !== "" ? String(vehicleId) : null;
}
function getVehicleId() {
  try {
    const raw = localStorage.getItem(GARAGE_STORAGE_KEY);
    if (raw) {
      const garage = JSON.parse(raw);
      const vehicle = Array.isArray(garage) ? garage[0] : null;
      const vehicleId = extractVehicleIdFromRecord(vehicle);
      if (vehicleId) return vehicleId;
    }
    const selectedRaw = localStorage.getItem(SELECTED_VEHICLE_STORAGE_KEY);
    if (selectedRaw) {
      const selected = JSON.parse(selectedRaw);
      return extractVehicleIdFromRecord(selected);
    }
    return null;
  } catch (_) {
    return null;
  }
}
function refreshFitmentForGarageChange() {
  syncVehicleUiForGarage();
  updateCompatibilityBadges();
  applyCompatibleOnlyFallback();
  initProductPageFitmentNote();
  updateRelatedProductsCompatibility();
}
function syncVehicleUiForGarage() {
  try {
    document.documentElement.classList.toggle("vs-has-vehicle", !!getVehicleId());
  } catch (_) {
  }
}
function getUserCookie() {
  try {
    const raw = localStorage.getItem(USER_COOKIE_STORAGE_KEY);
    return raw ? String(raw) : "";
  } catch (_) {
    return "";
  }
}
function normalizeSkuKey(sku) {
  if (!sku) return "";
  return String(sku).trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
}
function parseSkuList(raw) {
  if (!raw) return [];
  return [...new Set(String(raw).split(/[\n\r,\t,]+/).map((sku) => normalizeSkuKey(sku)).filter(Boolean))];
}
function decodeFitmentText(text) {
  return String(text || "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}
function cleanFitmentDisplayText(text) {
  return decodeFitmentText(text)
    .replace(/^(\s*--&gt;|\s*-->|\s*->|\s*→)+\s*/i, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}
function extractFitmentFootnote(entry) {
  if (!entry || typeof entry !== "object") return "";
  const note = entry.footnote ?? entry.Footnote ?? entry.fitmentNote ?? entry.FitmentNote ?? "";
  return cleanFitmentDisplayText(note);
}
function extractLongFootnote(entry) {
  if (!entry || typeof entry !== "object") return "";
  const note = entry.longFootnote ?? entry.LongFootnote ?? "";
  return cleanFitmentDisplayText(note);
}
function extractSubCatDescription(entry) {
  if (!entry || typeof entry !== "object") return "";
  const note = entry.subCatDescription ?? entry.SubCatDescription ?? "";
  return cleanFitmentDisplayText(note);
}
function extractFitmentGrade(entry) {
  if (!entry || typeof entry !== "object") return "";
  const note = entry.grade ?? entry.Grade ?? "";
  return cleanFitmentDisplayText(note);
}
function normalizeFitmentEntry(entry) {
  return {
    footnote: extractFitmentFootnote(entry),
    subCatDescription: extractSubCatDescription(entry),
    grade: extractFitmentGrade(entry),
    longFootnote: extractLongFootnote(entry)
  };
}
function mergeFitmentEntry(existing, next) {
  const prev = typeof existing === "string" ? { footnote: existing, subCatDescription: "", grade: "", longFootnote: existing } : existing || {};
  const add = typeof next === "string" ? { footnote: next, subCatDescription: "", grade: "", longFootnote: next } : next || {};
  return {
    footnote: add.footnote || prev.footnote || "",
    subCatDescription: add.subCatDescription || prev.subCatDescription || "",
    grade: add.grade || prev.grade || "",
    longFootnote: add.longFootnote || prev.longFootnote || ""
  };
}
function hasFitmentEntryData(entry) {
  return !!(entry?.footnote || entry?.subCatDescription || entry?.grade || entry?.longFootnote);
}
function pickCollectionBadgeFromEntry(entry) {
  return normalizeFitmentEntry(entry).footnote || "";
}
function pickPdpFootnoteFromEntry(entry) {
  const normalized = normalizeFitmentEntry(entry);
  return normalized.footnote || normalized.subCatDescription || "";
}
function pickLongFootnoteForPdp(entry) {
  const normalized = normalizeFitmentEntry(entry);
  const footnote = normalized.footnote;
  const subCat = normalized.subCatDescription;
  const long = normalized.longFootnote;
  const grade = normalized.grade;
  const isDistinct = (text) => {
    if (!text) return false;
    const value = text.toLowerCase();
    return value !== footnote.toLowerCase() && value !== subCat.toLowerCase();
  };
  if (long) {
    const segments = long.split(/\s*;\s*/).map((part) => cleanFitmentDisplayText(part)).filter(Boolean);
    const extra = segments.filter(isDistinct);
    if (extra.length) return extra.join(" · ");
    if (isDistinct(long)) return long;
  }
  if (isDistinct(grade)) return grade;
  return "";
}
function mergeFitmentNoteMaps(...maps) {
  const out = /* @__PURE__ */ new Map();
  maps.forEach((map) => {
    if (!map || !map.size) return;
    map.forEach((entry, key) => {
      out.set(key, mergeFitmentEntry(out.get(key), entry));
    });
  });
  return out;
}
function extractFitmentNote(entry) {
  return extractFitmentFootnote(entry);
}
function getProductUrlFromCard(record) {
  const card = record?.el?.querySelector(".product-card");
  if (!card) return "";
  const link = card.querySelector(".product-card__figure a[href], .product-card__title a[href]");
  const href = link?.getAttribute("href") || link?.href || "";
  return href ? String(href) : "";
}
function buildFitmentNoteMapFromMatchedFitment(matchedFitment) {
  const map = /* @__PURE__ */ new Map();
  if (!Array.isArray(matchedFitment)) return map;
  matchedFitment.forEach((row) => {
    const entry = normalizeFitmentEntry(row);
    if (!hasFitmentEntryData(entry)) return;
    const skuRaw = row?.sku ?? row?.Sku ?? row?.PartNo ?? row?.partNo;
    const key = normalizeSkuKey(skuRaw);
    if (key) map.set(key, mergeFitmentEntry(map.get(key), entry));
  });
  return map;
}
function buildFitmentNoteMapFromApiResponse(data) {
  let map = buildFitmentNoteMapFromMatchedFitment(data?.matchedFitment);
  const parts = Array.isArray(data?.parts) ? data.parts : [];
  parts.forEach((part) => {
    const entry = normalizeFitmentEntry(part);
    if (!hasFitmentEntryData(entry)) return;
    const key = normalizeSkuKey(part?.PartNo ?? part?.Partno ?? part?.sku);
    if (key) map.set(key, mergeFitmentEntry(map.get(key), entry));
  });
  const products = Array.isArray(data?.products) ? data.products : [];
  products.forEach((product) => {
    const productEntry = normalizeFitmentEntry(product);
    const matchedSkus = Array.isArray(product?.matchedSkus) ? product.matchedSkus : [];
    if (hasFitmentEntryData(productEntry)) {
      matchedSkus.forEach((sku) => {
        const key = normalizeSkuKey(sku);
        if (key && !map.has(key)) map.set(key, productEntry);
      });
    }
    const nested = buildFitmentNoteMapFromMatchedFitment(product?.matchedFitment);
    nested.forEach((entry, key) => {
      if (!map.has(key)) map.set(key, entry);
    });
  });
  return map;
}
function cacheFitmentNotesForVehicle(vehicleId, noteMap) {
  if (!vehicleId || !noteMap || noteMap.size === 0) return;
  try {
    const merged = mergeFitmentNoteMaps(getCachedFitmentNotesForVehicle(vehicleId), noteMap);
    const notesBySku = {};
    merged.forEach((note, key) => {
      const entry = typeof note === "string" ? { footnote: note, longFootnote: note } : note;
      notesBySku[key] = entry;
    });
    localStorage.setItem(FITMENT_NOTES_STORAGE_KEY, JSON.stringify({
      vehicleId: String(vehicleId),
      dateStored: new Date().toISOString(),
      notesBySku
    }));
  } catch (_) {
  }
}
function getCachedFitmentNotesForVehicle(vehicleId) {
  if (!vehicleId) return null;
  try {
    const raw = localStorage.getItem(FITMENT_NOTES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || String(parsed.vehicleId ?? "") !== String(vehicleId)) return null;
    const storedAt = parsed.dateStored ? new Date(parsed.dateStored).getTime() : NaN;
    const ttlMs = 24 * 60 * 60 * 1e3;
    if (!Number.isFinite(storedAt) || Date.now() - storedAt >= ttlMs) return null;
    const notesBySku = parsed.notesBySku && typeof parsed.notesBySku === "object" ? parsed.notesBySku : {};
    const map = /* @__PURE__ */ new Map();
    Object.keys(notesBySku).forEach((key) => {
      const normalized = normalizeSkuKey(key);
      const raw = notesBySku[key];
      const entry = typeof raw === "string"
        ? { footnote: String(raw).trim(), subCatDescription: "", grade: "", longFootnote: String(raw).trim() }
        : {
            footnote: String(raw?.footnote || "").trim(),
            subCatDescription: String(raw?.subCatDescription || "").trim(),
            grade: String(raw?.grade || "").trim(),
            longFootnote: String(raw?.longFootnote || "").trim()
          };
      if (normalized && hasFitmentEntryData(entry)) map.set(normalized, entry);
    });
    return map.size > 0 ? map : null;
  } catch (_) {
    return null;
  }
}
function pickFitmentFieldForSkus(skus, noteMap, field) {
  if (!noteMap || !Array.isArray(skus) || !field) return "";
  for (const sku of skus) {
    const entry = noteMap.get(sku);
    if (!entry) continue;
    const value = typeof entry === "string" ? entry : entry[field];
    if (value) return String(value).trim();
  }
  return "";
}
function footnoteHasSide(text, side) {
  const word = side === "left" ? "left" : "right";
  return new RegExp("\\b" + word + "\\b", "i").test(String(text || ""));
}
function getVariantSideLabelFromDom() {
  const picker = document.querySelector(".shopify-section--main-product variant-picker");
  if (!picker) return "";
  const options = picker.querySelectorAll(".variant-picker__option");
  for (const optionEl of options) {
    const legend = optionEl.querySelector("legend");
    const legendText = String(legend?.textContent || "").replace(/:\s*$/, "").trim().toLowerCase();
    if (legendText !== "side") continue;
    const selectedText = optionEl.querySelector(".variant-picker__option-info span");
    const value = String(selectedText?.textContent || "").trim().toLowerCase();
    if (value === "left" || value === "right") {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
    const checked = optionEl.querySelector("input[data-option-position]:checked");
    if (checked) {
      const label = checked.closest("label")?.textContent || checked.getAttribute("aria-label") || "";
      const normalized = String(label).trim().toLowerCase();
      if (normalized === "left" || normalized === "right") {
        return normalized.charAt(0).toUpperCase() + normalized.slice(1);
      }
    }
  }
  return "";
}
function getVariantSideLabelFromVariantObject(variant) {
  const options = [variant?.option1, variant?.option2, variant?.option3];
  for (const option of options) {
    const value = String(option || "").trim().toLowerCase();
    if (value === "left" || value === "right") {
      return value.charAt(0).toUpperCase() + value.slice(1);
    }
  }
  const title = String(variant?.title || variant?.name || "").toLowerCase();
  if (/\bright\b/.test(title)) return "Right";
  if (/\bleft\b/.test(title)) return "Left";
  return "";
}
function getPdpVariantSideLabel(variant) {
  if (window.themeVariables?.settings?.pageType === "product") {
    const fromDom = getVariantSideLabelFromDom();
    if (fromDom) return fromDom;
  }
  return getVariantSideLabelFromVariantObject(variant);
}
function getVariantSideLabel(variant) {
  return getPdpVariantSideLabel(variant);
}
function alignFootnoteToVariantSide(footnote, variant) {
  const text = cleanFitmentDisplayText(footnote);
  const side = getPdpVariantSideLabel(variant);
  if (!text || !side) return text;
  const stripped = stripSideFromCollectionFootnote(text);
  return stripped ? side + ", " + stripped : side;
}
function alignLongFootnoteForPdp(longText, variant) {
  const text = cleanFitmentDisplayText(longText);
  if (!text) return "";
  if (footnoteHasSide(text, "left") || footnoteHasSide(text, "right")) {
    return alignFootnoteToVariantSide(text, variant);
  }
  return text;
}
function stripSideFromCollectionFootnote(text) {
  return cleanFitmentDisplayText(
    String(text || "")
      .replace(/\bLeft\b\s*,?\s*/gi, "")
      .replace(/\bRight\b\s*,?\s*/gi, "")
      .replace(/^\s*,\s*/, "")
      .replace(/,\s*,/g, ",")
  );
}
function collectFootnotesForSkus(skus, noteMap) {
  const out = [];
  if (!noteMap || !Array.isArray(skus)) return out;
  for (const sku of skus) {
    const entry = noteMap.get(sku);
    if (!entry) continue;
    const note = typeof entry === "string" ? entry : entry.footnote;
    const trimmed = cleanFitmentDisplayText(String(note || ""));
    if (trimmed) out.push(trimmed);
  }
  return out;
}
function footnotesCoverBothSides(footnotes) {
  if (!Array.isArray(footnotes) || !footnotes.length) return false;
  const hasLeft = footnotes.some((n) => footnoteHasSide(n, "left"));
  const hasRight = footnotes.some((n) => footnoteHasSide(n, "right"));
  return hasLeft && hasRight;
}
function getCollectionFitmentNoteMap(noteMapHint) {
  return mergeFitmentNoteMaps(getCachedFitmentNotesForVehicle(getVehicleId()), noteMapHint);
}
function getCollectionContextSkus(skus, noteMap, matchedSet) {
  const normalized = (skus || []).map((sku) => normalizeSkuKey(sku)).filter(Boolean);
  if (matchedSet?.size) {
    const matched = normalized.filter((sku) => matchedSet.has(sku));
    if (matched.length) return matched;
  }
  if (noteMap?.size) {
    const withNotes = normalized.filter((sku) => noteMap.has(sku));
    if (withNotes.length) return withNotes;
  }
  return normalized;
}
function formatCollectionFootnoteForBothSides(raw) {
  const stripped = stripSideFromCollectionFootnote(raw);
  if (!stripped) return cleanFitmentDisplayText(raw);
  return "Left & Right, " + stripped;
}
function shouldStripSidesOnCollection(skus, noteMap, extraFootnotes) {
  const footnotes = collectFootnotesForSkus(skus, noteMap);
  const merged = footnotes.concat(Array.isArray(extraFootnotes) ? extraFootnotes : []);
  if (footnotesCoverBothSides(merged)) return true;
  let hasLeft = false;
  let hasRight = false;
  for (const sku of skus || []) {
    const entry = noteMap?.get(sku);
    if (!entry) continue;
    const note = typeof entry === "string" ? entry : entry.footnote || "";
    const text = cleanFitmentDisplayText(String(note || ""));
    if (footnoteHasSide(text, "left")) hasLeft = true;
    if (footnoteHasSide(text, "right")) hasRight = true;
  }
  if (hasLeft && hasRight) return true;
  if ((skus || []).length >= 2 && footnotes.length >= 2) {
    const hasSideWord = footnotes.some((n) => footnoteHasSide(n, "left") || footnoteHasSide(n, "right"));
    const stripped = footnotes.map((n) => stripSideFromCollectionFootnote(n)).filter(Boolean);
    if (hasSideWord && stripped.length >= 2 && new Set(stripped).size === 1) return true;
  }
  return false;
}
function pickCollectionDisplayFootnote(skus, noteMap, productEntry, matchedSet) {
  const mergedMap = getCollectionFitmentNoteMap(noteMap);
  const contextSkus = getCollectionContextSkus(skus, mergedMap, matchedSet);
  const entryFootnote = productEntry ? pickCollectionBadgeFromEntry(productEntry) : "";
  const raw = entryFootnote || pickFitmentFieldForSkus(contextSkus, mergedMap, "footnote");
  if (!raw) return "";
  const extraFootnotes = entryFootnote ? [entryFootnote] : [];
  if (shouldStripSidesOnCollection(contextSkus, mergedMap, extraFootnotes)) {
    return formatCollectionFootnoteForBothSides(raw);
  }
  return raw;
}
function pickFitmentNoteForSkus(skus, noteMap) {
  return pickFitmentFieldForSkus(skus, noteMap, "footnote");
}
function pickLongFootnoteForSkus(skus, noteMap) {
  return pickFitmentFieldForSkus(skus, noteMap, "longFootnote");
}
function pickCollectionFootnoteForSkus(skus, noteMap, matchedSet) {
  return pickCollectionDisplayFootnote(skus, noteMap, null, matchedSet);
}
function pickCollectionFootnoteFromEntry(entry) {
  return pickCollectionBadgeFromEntry(entry);
}
function getFitmentListingSection() {
  return document.querySelector(".shopify-section--main-collection, .shopify-section--main-search");
}
function isFitmentSearchPage(urlLike) {
  const url = toCollectionRequestUrl(urlLike || location.href);
  return url.pathname.includes("/search");
}
function getSearchQueryFromUrl(urlLike) {
  const url = toCollectionRequestUrl(urlLike || location.href);
  const raw = url.searchParams.get("q") || url.searchParams.get("query") || url.searchParams.get("search") || "";
  return String(raw).trim();
}
function pickFitmentEntryForSkus(skus, noteMap) {
  if (!noteMap || !Array.isArray(skus)) return null;
  for (const sku of skus) {
    const entry = noteMap.get(sku);
    if (!entry) continue;
    if (typeof entry === "string") {
      const text = String(entry).trim();
      if (text) return { footnote: text, subCatDescription: "", grade: "", longFootnote: text };
      continue;
    }
    const normalized = {
      footnote: String(entry.footnote || "").trim(),
      subCatDescription: String(entry.subCatDescription || "").trim(),
      grade: String(entry.grade || "").trim(),
      longFootnote: String(entry.longFootnote || "").trim()
    };
    if (hasFitmentEntryData(normalized)) return normalized;
  }
  return null;
}
function pickFitmentEntryForVariant(skuKey, noteMap, variant) {
  const direct = pickFitmentEntryForSkus([skuKey], noteMap);
  if (direct) return direct;
  if (!noteMap?.size || !getVariantSideLabel(variant)) return null;
  for (const [, candidate] of noteMap) {
    const entry = typeof candidate === "string"
      ? { footnote: String(candidate).trim(), subCatDescription: "", grade: "", longFootnote: String(candidate).trim() }
      : candidate;
    if (!hasFitmentEntryData(entry)) continue;
    if (stripSideFromCollectionFootnote(pickPdpFootnoteFromEntry(entry))) {
      return entry;
    }
  }
  return null;
}
function getProductMainForm() {
  const section = document.querySelector(".shopify-section--main-product");
  if (!section) return null;
  const formId = section.querySelector('form[is="product-form"]')?.id || section.querySelector("form#product-form")?.id;
  return formId ? document.getElementById(formId) : section.querySelector('form[is="product-form"], form[action*="/cart/add"]');
}
function resolveActiveProductVariant(fallbackVariant) {
  const picker = document.querySelector(".shopify-section--main-product variant-picker");
  let parsed = null;
  try {
    parsed = JSON.parse(picker?.querySelector("script[data-variant]")?.textContent || "null");
  } catch (_) {
    parsed = null;
  }
  const eventVariant = fallbackVariant && typeof fallbackVariant === "object" ? fallbackVariant : null;
  const domSku = getVariantSkuFromProductPage();
  const merged = { ...(parsed || {}), ...(eventVariant || {}) };
  if (domSku) merged.sku = domSku;
  if (eventVariant?.id) merged.id = eventVariant.id;
  if (eventVariant?.option1 != null) merged.option1 = eventVariant.option1;
  if (eventVariant?.option2 != null) merged.option2 = eventVariant.option2;
  if (eventVariant?.option3 != null) merged.option3 = eventVariant.option3;
  if (merged.sku || merged.id) return merged;
  if (eventVariant?.sku || eventVariant?.id) return eventVariant;
  return domSku ? { sku: domSku } : null;
}
function buildProductFitmentBadgeHtml(primary, longFootnote) {
  const short = String(primary || "").trim();
  const long = String(longFootnote || "").trim();
  if (!short && !long) return "";
  if (!long || short.toLowerCase() === long.toLowerCase()) return escapeHtml(short || long);
  if (!short) return escapeHtml(long);
  return escapeHtml(short) + "<br>" + escapeHtml(long);
}
function getCompatibleBadgeLabel() {
  return "Compatible with your car";
}
function getCachedMatchedSkusForVehicle(vehicleId) {
  if (!vehicleId) return null;
  try {
    const raw = localStorage.getItem(SKU_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || String(parsed.vehicleId ?? "") !== String(vehicleId)) return null;
    const storedAt = parsed.dateStored ? new Date(parsed.dateStored).getTime() : NaN;
    const ttlMs = 24 * 60 * 60 * 1e3;
    if (!Number.isFinite(storedAt) || Date.now() - storedAt >= ttlMs) return null;
    const skus = Array.isArray(parsed.skus) ? parsed.skus : [];
    return new Set(skus.map((sku) => normalizeSkuKey(sku)).filter(Boolean));
  } catch (_) {
    return null;
  }
}
function getVsportApiBaseUrl() {
  const base = window.__VSPORT_API__;
  if (!base || typeof base !== "string") return "";
  return base.replace(/\/+$/, "");
}
async function fetchVehicleSkusFromFitmentService(vehicleId, userCookie, signal) {
  const apiBase = getVsportApiBaseUrl();
  if (!apiBase || !vehicleId) return null;
  const response = await fetch(apiBase + "/api/v1/fitment/parts-list", {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(userCookie ? { "x-user-cookie": userCookie } : {})
    },
    body: JSON.stringify({
      VehicleId: Number(vehicleId),
      CatType: 1,
      PartGroup: 0,
      SubGroup: 0,
      Layout: 0
    }),
    signal
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.ok) {
    throw new Error(data?.message || ("Fitment parts-list failed (" + response.status + ")"));
  }
  const parts = Array.isArray(data.parts) ? data.parts : [];
  const skus = /* @__PURE__ */ new Set();
  const noteBySku = /* @__PURE__ */ new Map();
  parts.forEach((part) => {
    const key = normalizeSkuKey(part?.PartNo);
    if (!key) return;
    skus.add(key);
    const entry = normalizeFitmentEntry(part);
    if (hasFitmentEntryData(entry)) noteBySku.set(key, mergeFitmentEntry(noteBySku.get(key), entry));
  });
  cacheFitmentNotesForVehicle(vehicleId, noteBySku);
  try {
    localStorage.setItem(SKU_STORAGE_KEY, JSON.stringify({
      vehicleId: String(vehicleId),
      dateStored: new Date().toISOString(),
      skus: [...skus]
    }));
  } catch (_) {
  }
  return { skus, noteBySku };
}
function syncCompatibleFromUrl(form) {
  if (!form || !form.querySelector("[data-compatible-my-car]")) return;
  const url = new URL(location.href);
  const tagInput = form.querySelector("[data-compatible-tag-input]");
  const onlyInput = form.querySelector("[data-compatible-only-input]");
  const checkbox = form.querySelector("[data-compatible-my-car]");
  const tagVal = url.searchParams.get("filter.v.tag") || "";
  const onlyVal = url.searchParams.get(COMPATIBLE_ONLY_KEY) || "";
  const checked = onlyVal === "1" || !!(tagVal && tagVal.startsWith(COMPATIBLE_TAG_PREFIX));
  if (tagInput) tagInput.value = "";
  if (onlyInput) onlyInput.value = checked ? "1" : "";
  if (checkbox) checkbox.checked = checked;
}
function setCompatibleFormValues(form, checked) {
  if (!form) return;
  const tagInput = form.querySelector("[data-compatible-tag-input]");
  const onlyInput = form.querySelector("[data-compatible-only-input]");
  if (!tagInput || !onlyInput) return;
  tagInput.value = "";
  onlyInput.value = checked ? "1" : "";
}
function isBrakesCollectionPage() {
  return BRAKE_COLLECTION_HANDLES.includes(getCollectionHandleFromPath());
}
function readBrakePositionFromUrl(urlLike) {
  const url = toCollectionRequestUrl(urlLike || location.href);
  const positions = url.searchParams.getAll(BRAKE_POSITION_KEY).flatMap((value) => value.split(",")).map((entry) => entry.trim().toLowerCase()).filter((entry) => entry === "front" || entry === "rear");
  return [...new Set(positions)];
}
function buildBrakePositionsFromForm(form) {
  if (!form) return [];
  const positions = [];
  if (form.querySelector("[data-brake-position-front]")?.checked) positions.push("front");
  if (form.querySelector("[data-brake-position-rear]")?.checked) positions.push("rear");
  return positions;
}
function buildBrakePositionFromForm(form) {
  const positions = buildBrakePositionsFromForm(form);
  return positions.length > 0 ? positions.join(",") : null;
}
function writeBrakePositionToUrl(urlLike, positions) {
  const url = toCollectionRequestUrl(urlLike || location.href);
  url.searchParams.delete(BRAKE_POSITION_KEY);
  const validPositions = positions.filter((entry) => entry === "front" || entry === "rear");
  if (validPositions.length > 0) {
    url.searchParams.set(BRAKE_POSITION_KEY, validPositions.join(","));
  }
  return url;
}
function syncBrakePositionFromUrl(form, urlLike) {
  if (!form?.querySelector("[data-brake-position-facet]")) return;
  const positions = readBrakePositionFromUrl(urlLike || location.href);
  const frontCheckbox = form.querySelector("[data-brake-position-front]");
  const rearCheckbox = form.querySelector("[data-brake-position-rear]");
  if (frontCheckbox) frontCheckbox.checked = positions.includes("front");
  if (rearCheckbox) rearCheckbox.checked = positions.includes("rear");
}
function syncBrakePositionAcrossForms(sourceForm) {
  if (!sourceForm) return;
  const frontChecked = !!sourceForm.querySelector("[data-brake-position-front]")?.checked;
  const rearChecked = !!sourceForm.querySelector("[data-brake-position-rear]")?.checked;
  document.querySelectorAll('form[is="facet-form"]').forEach((form) => {
    if (form === sourceForm) return;
    const frontCheckbox = form.querySelector("[data-brake-position-front]");
    const rearCheckbox = form.querySelector("[data-brake-position-rear]");
    if (frontCheckbox) frontCheckbox.checked = frontChecked;
    if (rearCheckbox) rearCheckbox.checked = rearChecked;
  });
}
function clearBrakePositionCheckboxes() {
  document.querySelectorAll("[data-brake-position-front], [data-brake-position-rear]").forEach((checkbox) => {
    checkbox.checked = false;
  });
}
function applyBrakePositionToUrl(urlLike, form) {
  const url = toCollectionRequestUrl(urlLike || location.href);
  if (!isBrakesCollectionPage()) return url;
  const positions = buildBrakePositionsFromForm(form || document.querySelector('form[is="facet-form"]'));
  return writeBrakePositionToUrl(url, positions);
}
function toggleBrakePositionFacetVisibility() {
  const url = new URL(location.href);
  const isCompatibleOnly = url.searchParams.get(COMPATIBLE_ONLY_KEY) === "1" || !!document.querySelector("[data-compatible-my-car]:checked");
  document.querySelectorAll("[data-brake-position-facet]").forEach((facet) => {
    facet.hidden = !isBrakesCollectionPage() || !isCompatibleOnly;
  });
}
function applyBrakePositionToQueryString(currentUrl, qs) {
  if (!isBrakesCollectionPage()) return;
  const positions = readBrakePositionFromUrl(currentUrl);
  if (positions.length > 0) qs.set(BRAKE_POSITION_KEY, positions.join(","));
}
function productHasVehicleMatchedSkus(product) {
  if (Number(product?.matchedSkuCount) > 0) return true;
  return Array.isArray(product?.matchedSkus) && product.matchedSkus.some((sku) => String(sku ?? "").trim());
}
function getKnownUniversalHandlesFromSearchPage() {
  const handles = /* @__PURE__ */ new Set();
  if (typeof window.vsUniversalFitHandles !== "undefined" && window.vsUniversalFitHandles.forEach) {
    window.vsUniversalFitHandles.forEach((handle) => {
      if (handle) handles.add(String(handle));
    });
  }
  const section = document.querySelector(".shopify-section--main-search");
  if (!section) return handles;
  section.querySelectorAll(".collection__product-item").forEach((el) => {
    const attrHandle = el.getAttribute("data-product-handle") || "";
    const href = el.querySelector('a[href*="/products/"]')?.getAttribute("href") || "";
    const hrefMatch = href.match(/\/products\/([^/?#]+)/);
    let handle = attrHandle;
    if (!handle && hrefMatch?.[1]) {
      try {
        handle = decodeURIComponent(hrefMatch[1]);
      } catch (_) {
        handle = hrefMatch[1];
      }
    }
    if (!handle) return;
    if (el.getAttribute("data-universal-fit") === "true" || el.querySelector(".badge--universal-fit")) {
      handles.add(handle);
    }
  });
  return handles;
}
function isUniversalFitProduct(product, extraUniversalHandles) {
  const flag = product?.isUniversal;
  if (flag === true || flag === "true" || flag === 1 || flag === "1") return true;
  if (product?.universalFit === true || product?.universal_fit === true) return true;
  if (productHasVehicleMatchedSkus(product)) return false;
  const handle = product?.handle || "";
  if (handle && extraUniversalHandles instanceof Set && extraUniversalHandles.has(handle)) return true;
  return typeof window.vsUniversalFitHandles !== "undefined" && window.vsUniversalFitHandles.has(handle);
}
// Stable partition: vehicle-specific first, then Universal Fit (preserves proxy/sort_by order within each group).
function sortCompatibleProductsVehicleFirst(products) {
  const list = Array.isArray(products) ? products.slice() : [];
  return list
    .map((product, index) => ({ product, index }))
    .sort((a, b) => {
      const aUni = isUniversalFitProduct(a.product) ? 1 : 0;
      const bUni = isUniversalFitProduct(b.product) ? 1 : 0;
      if (aUni !== bUni) return aUni - bUni;
      return a.index - b.index;
    })
    .map((entry) => entry.product);
}
function getProductFitmentTexts(product) {
  const texts = [];
  const noteMap = buildFitmentNoteMapFromMatchedFitment(product?.matchedFitment);
  noteMap.forEach((entry) => {
    if (typeof entry === "string") {
      texts.push(entry);
      return;
    }
    texts.push(entry?.footnote, entry?.subCatDescription, entry?.longFootnote);
  });
  return texts.map((text) => cleanFitmentDisplayText(text)).filter(Boolean);
}
function footnoteTextMatchesBrakePosition(text, position) {
  const word = position === "rear" ? "rear" : "front";
  return new RegExp("\\b" + word + "\\b", "i").test(String(text || ""));
}
function productMatchesBrakePositionFilter(product, positions) {
  if (!positions.length) return true;
  if (isUniversalFitProduct(product)) return false;
  const texts = getProductFitmentTexts(product);
  if (!texts.length) return true;
  return positions.some((position) => texts.some((text) => footnoteTextMatchesBrakePosition(text, position)));
}
function filterProductsForBrakePosition(products, sourceUrl) {
  if (!isBrakesCollectionPage()) return products;
  const positions = readBrakePositionFromUrl(sourceUrl);
  if (!positions.length) return products;
  return (Array.isArray(products) ? products : []).filter((product) => productMatchesBrakePositionFilter(product, positions));
}
function isProductAvailableOnCard(product) {
  if (product?.hasPositiveInventory === true || product?.has_positive_inventory === true) return true;
  if (product?.available === true) return true;
  if (product?.available === false) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    if (variants.length === 0) return false;
    return variants.some((variant) => {
      if (variant?.available === true) return true;
      if (variant?.available === false) return false;
      const qtyRaw = variant?.inventoryQuantity ?? variant?.inventory_quantity;
      const qty = qtyRaw === null || qtyRaw === undefined || qtyRaw === "" ? null : Number(qtyRaw);
      if (Number.isFinite(qty) && qty > 0) return true;
      const policy = String(variant?.inventoryPolicy || variant?.inventory_policy || "").toLowerCase();
      return policy === "continue";
    });
  }
  return product?.available !== false;
}
function setPaginationParams(section) {
  const currentUrl = new URL(location.href);
  const tagVal = currentUrl.searchParams.get("filter.v.tag");
  const compatibleOnlyVal = currentUrl.searchParams.get(COMPATIBLE_ONLY_KEY);
  const positionVal = currentUrl.searchParams.get(BRAKE_POSITION_KEY);
  const categoryHandles = readCategoriesFromUrl(currentUrl);
  section.querySelectorAll(".pagination a[href]").forEach((a) => {
    const u = new URL(a.href);
    if (tagVal) u.searchParams.set("filter.v.tag", tagVal);
    else u.searchParams.delete("filter.v.tag");
    if (compatibleOnlyVal) u.searchParams.set(COMPATIBLE_ONLY_KEY, compatibleOnlyVal);
    else u.searchParams.delete(COMPATIBLE_ONLY_KEY);
    if (positionVal) u.searchParams.set(BRAKE_POSITION_KEY, positionVal);
    else u.searchParams.delete(BRAKE_POSITION_KEY);
    writeCategoriesToSearchParams(u.searchParams, categoryHandles);
    a.href = u.toString();
  });
}
var compatibleCollectionViewState = /* @__PURE__ */ new WeakMap();
function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>\"']/g, (ch) => {
    if (ch === "&") return "&amp;";
    if (ch === "<") return "&lt;";
    if (ch === ">") return "&gt;";
    if (ch === '"') return "&quot;";
    return "&#39;";
  });
}
function formatShopMoney(amount) {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "";
  try {
    const currency = Shopify?.currency?.active || "AUD";
    const locale = Shopify?.locale || "en-AU";
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(num);
  } catch (_) {
    return "$" + num.toFixed(2);
  }
}
function buildCompatiblePageUrl(page) {
  const url = new URL(location.href);
  const p = Math.max(1, Math.floor(Number(page) || 1));
  if (p <= 1) url.searchParams.delete("page");
  else url.searchParams.set("page", String(p));
  if (url.searchParams.get(COMPATIBLE_ONLY_KEY) === "1") {
    url.searchParams.set(COMPATIBLE_ONLY_KEY, "1");
    url.searchParams.delete("filter.v.tag");
  }
  writeCategoriesToSearchParams(url.searchParams, readCategoriesFromUrl(url));
  url.searchParams.delete("section_id");
  return url.toString();
}
function buildUrlWithoutCompatibleOnly(urlLike) {
  const url = toPublicCollectionUrl(urlLike || location.href);
  url.searchParams.delete(COMPATIBLE_ONLY_KEY);
  url.searchParams.delete("filter.v.tag");
  url.searchParams.delete(BRAKE_POSITION_KEY);
  url.searchParams.delete("page");
  return url.toString();
}
/** Builds a public URL with one category handle removed. */
function buildUrlWithoutCategory(urlLike, handleToRemove) {
  const url = toPublicCollectionUrl(urlLike || location.href);
  const remaining = readCategoriesFromUrl(url).filter((handle) => handle !== String(handleToRemove || "").trim().toLowerCase());
  writeCategoriesToSearchParams(url.searchParams, remaining);
  url.searchParams.delete("page");
  return url.toString();
}
function formatMoneyForPill(value) {
  const raw = formatShopMoney(value);
  return raw.replace(/\.00\b/, "");
}
function dispatchFacetUpdateFromLink(linkEl, href) {
  const section = linkEl?.closest(".shopify-section");
  if (!section?.id) {
    window.location.href = href;
    return;
  }
  const sectionId = section.id.replace("shopify-section-", "");
  const nextUrl = new URL(href, location.origin);
  nextUrl.searchParams.set("section_id", sectionId);
  linkEl.dispatchEvent(new CustomEvent("facet:update", {
    bubbles: true,
    detail: {
      url: nextUrl
    }
  }));
}
function syncFacetFormsFromUrl(urlLike) {
  const url = toCollectionRequestUrl(urlLike || location.href);
  document.querySelectorAll('form[is="facet-form"]').forEach((form) => {
    syncCompatibleFromUrl(form);
    syncBrakePositionFromUrl(form, url);
    form.querySelectorAll('input[type="checkbox"][name], input[type="radio"][name]').forEach((input) => {
      if (input.hasAttribute("data-compatible-my-car")) return;
      if (input.hasAttribute("data-brake-position-front") || input.hasAttribute("data-brake-position-rear")) return;
      const urlValues = url.searchParams.getAll(input.name);
      input.checked = urlValues.includes(input.value);
    });
    form.querySelectorAll('input[name="filter.v.price.gte"], input[name="filter.v.price.lte"]').forEach((input) => {
      const urlValue = url.searchParams.get(input.name);
      input.value = urlValue !== null && urlValue !== "" ? urlValue : "";
    });
    const sortBy = url.searchParams.get("sort_by");
    form.querySelectorAll('input[name="sort_by"]').forEach((input) => {
      if (input.type === "hidden") {
        input.value = sortBy || "";
      }
    });
  });
}
function applyProxyUrlState(nextUrlLike) {
  const nextUrl = toPublicCollectionUrl(nextUrlLike || location.href);
  window.history.replaceState({ path: nextUrl.toString() }, "", nextUrl.toString());
  syncFacetFormsFromUrl(nextUrl);
  syncProxyModeActiveFacets(nextUrl);
  updateCompatibilityBadges(nextUrl);
  applyCompatibleOnlyFallback(nextUrl);
}
function createActiveFacetPill(label, removeHref) {
  const pill = document.createElement("div");
  pill.className = "removable-facet";
  pill.setAttribute("data-proxy-pill", "1");
  const textNode = document.createTextNode(label);
  const removeLink = document.createElement("a");
  removeLink.href = removeHref;
  removeLink.className = "tap-area";
  removeLink.setAttribute("aria-label", 'Remove filter "' + label + '"');
  removeLink.setAttribute("data-no-instant", "");
  removeLink.innerHTML = '<svg role="presentation" stroke-width="2" focusable="false" width="10" height="10" class="icon icon-delete" viewBox="0 0 10 10"><path d="m.757.757 8.486 8.486m-8.486 0L9.243.757" stroke="currentColor"></path></svg>';
  removeLink.addEventListener("click", (event) => {
    event.preventDefault();
    const targetUrl = toPublicCollectionUrl(removeHref);
    if (!isProxyListingMode(targetUrl)) {
      window.location.href = targetUrl.toString();
      return;
    }
    applyProxyUrlState(targetUrl);
  });
  pill.appendChild(textNode);
  pill.appendChild(removeLink);
  return pill;
}
function ensureProxyActiveFacetContainers(isProxyMode) {
  let containers = Array.from(document.querySelectorAll('.active-facets[data-proxy-active-facets="1"]'));
  if (!isProxyMode) {
    containers.forEach((el) => el.remove());
    return [];
  }
  if (containers.length > 0) return containers;
  const mountTargets = [
    document.querySelector(".collection__top-bar .facets-summary"),
    document.querySelector(".collection__results .v-stack.gap-4")
  ].filter(Boolean);
  mountTargets.forEach((target) => {
    const container = document.createElement("div");
    container.className = "active-facets";
    container.setAttribute("data-proxy-active-facets", "1");
    target.prepend(container);
    containers.push(container);
  });
  return containers;
}
function toggleNativeActiveFacets(isProxyMode) {
  document.querySelectorAll(".active-facets:not([data-proxy-active-facets='1'])").forEach((el) => {
    if (isProxyMode) {
      if (el.dataset.proxyPrevDisplay === undefined) {
        el.dataset.proxyPrevDisplay = el.style.display || "";
      }
      el.style.display = "none";
      return;
    }
    if (el.dataset.proxyPrevDisplay !== undefined) {
      el.style.display = el.dataset.proxyPrevDisplay;
      delete el.dataset.proxyPrevDisplay;
    } else {
      el.style.display = "";
    }
  });
}
function syncProxyModeActiveFacets(urlLike) {
  const url = toCollectionRequestUrl(urlLike || location.href);
  const isProxyMode = isProxyListingMode(url);
  const isCompatibleOnly = url.searchParams.get(COMPATIBLE_ONLY_KEY) === "1";
  const categoryHandles = readCategoriesFromUrl(url);
  toggleNativeActiveFacets(isProxyMode);
  const containers = ensureProxyActiveFacetContainers(isProxyMode);
  containers.forEach((container) => {
    container.innerHTML = "";
    if (!isProxyMode) return;
    if (isCompatibleOnly) {
      const removeCompatibleUrl = new URL(toPublicCollectionUrl(url));
      removeCompatibleUrl.searchParams.delete(COMPATIBLE_ONLY_KEY);
      removeCompatibleUrl.searchParams.delete("filter.v.tag");
      removeCompatibleUrl.searchParams.delete(BRAKE_POSITION_KEY);
      removeCompatibleUrl.searchParams.delete("page");
      container.appendChild(createActiveFacetPill("Compatible with your car", removeCompatibleUrl.toString()));
    }
    categoryHandles.forEach((handle) => {
      container.appendChild(createActiveFacetPill(getCategoryFacetLabel(handle), buildUrlWithoutCategory(url, handle)));
    });
    const hasInStock = url.searchParams.get("filter.v.availability") === "1";
    if (hasInStock) {
      const removeInStockUrl = new URL(toPublicCollectionUrl(url));
      removeInStockUrl.searchParams.delete("filter.v.availability");
      removeInStockUrl.searchParams.delete("page");
      container.appendChild(createActiveFacetPill("In stock", removeInStockUrl.toString()));
    }
    const priceMin = url.searchParams.get("filter.v.price.gte") || url.searchParams.get("price_min") || "";
    const priceMax = url.searchParams.get("filter.v.price.lte") || url.searchParams.get("price_max") || "";
    if (priceMin !== "" || priceMax !== "") {
      const removePriceUrl = new URL(toPublicCollectionUrl(url));
      removePriceUrl.searchParams.delete("filter.v.price.gte");
      removePriceUrl.searchParams.delete("filter.v.price.lte");
      removePriceUrl.searchParams.delete("price_min");
      removePriceUrl.searchParams.delete("price_max");
      removePriceUrl.searchParams.delete("page");
      const minLabel = priceMin !== "" ? formatMoneyForPill(Number(priceMin)) : "Min";
      const maxLabel = priceMax !== "" ? formatMoneyForPill(Number(priceMax)) : "Max";
      container.appendChild(createActiveFacetPill(minLabel + " - " + maxLabel, removePriceUrl.toString()));
    }
    const productFilterLabels = {
      "filter.p.vendor": "Brand",
      "filter.p.product_type": "Type",
      "filter.p.tag": "Tag"
    };
    Object.entries(productFilterLabels).forEach(([paramName, groupLabel]) => {
      url.searchParams.getAll(paramName).forEach((value) => {
        if (!value) return;
        const removeUrl = new URL(toPublicCollectionUrl(url));
        const remaining = removeUrl.searchParams.getAll(paramName).filter((entry) => entry !== value);
        removeUrl.searchParams.delete(paramName);
        remaining.forEach((entry) => removeUrl.searchParams.append(paramName, entry));
        removeUrl.searchParams.delete("page");
        container.appendChild(createActiveFacetPill(groupLabel + ": " + value, removeUrl.toString()));
      });
    });
    readBrakePositionFromUrl(url).forEach((brakePosition) => {
      const removePositionUrl = new URL(toPublicCollectionUrl(url));
      const remaining = readBrakePositionFromUrl(removePositionUrl).filter((entry) => entry !== brakePosition);
      removePositionUrl.searchParams.delete(BRAKE_POSITION_KEY);
      if (remaining.length > 0) {
        removePositionUrl.searchParams.set(BRAKE_POSITION_KEY, remaining.join(","));
      }
      removePositionUrl.searchParams.delete("page");
      const positionLabel = brakePosition === "front" ? "Front" : "Rear";
      container.appendChild(createActiveFacetPill(positionLabel, removePositionUrl.toString()));
    });
    const pillCount = container.querySelectorAll("[data-proxy-pill]").length;
    if (pillCount > 1) {
      const clearUrl = toPublicCollectionUrl(url);
      clearUrl.searchParams.delete(COMPATIBLE_ONLY_KEY);
      clearUrl.searchParams.delete("filter.v.tag");
      clearUrl.searchParams.delete(BRAKE_POSITION_KEY);
      clearUrl.searchParams.delete(CATEGORIES_KEY);
      clearUrl.searchParams.delete("filter.v.availability");
      clearUrl.searchParams.delete("filter.v.price.gte");
      clearUrl.searchParams.delete("filter.v.price.lte");
      clearUrl.searchParams.delete("price_min");
      clearUrl.searchParams.delete("price_max");
      clearUrl.searchParams.delete("page");
      ["filter.p.vendor", "filter.p.product_type", "filter.p.tag"].forEach((paramName) => {
        clearUrl.searchParams.delete(paramName);
      });
      const clearLink = document.createElement("a");
      clearLink.href = clearUrl.toString();
      clearLink.className = "facet-clear-all text-subdued";
      clearLink.setAttribute("data-proxy-pill", "1");
      clearLink.innerHTML = '<span class="link">Clear filters</span>';
      clearLink.addEventListener("click", (event) => {
        event.preventDefault();
        window.location.href = clearUrl.toString();
      });
      container.appendChild(clearLink);
    }
  });
}
var proxyLoaderStyleInjected = false;
function ensureProxyLoaderStyles() {
  if (proxyLoaderStyleInjected) return;
  const style = document.createElement("style");
  style.textContent = `
    .fitment-proxy-loader {
      display: none;
      padding: 12px 12px 4px;
      margin: 8px 0 0;
      border-radius: 14px;
      background: rgba(255, 255, 255, 0.04);
      border: 1px solid rgba(255, 255, 255, 0.08);
      color: #ffffff;
    }
    .fitment-proxy-loader[aria-hidden="false"] {
      display: block;
    }
    .fitment-proxy-loader .col-xs-12 {
      width: 100%;
    }
    .fitment-proxy-loader .st0 {}
    .fitment-proxy-loader .carDefault {
      opacity: 0;
      margin-left: -800px;
    }
    .fitment-proxy-loader .car-wrapper svg {
      margin: 0 0 0 10%;
      width: 90%;
      overflow: hidden;
      display: block;
    }
    .fitment-proxy-loader .car-wrapper {
      max-width: 250px;
      margin: 0 auto;
      margin-bottom: 12px;
      margin-top: 8px;
    }
    .fitment-proxy-loader .bounce {
      animation: fitment-bounce 0.5s infinite;
      transform-origin: center center;
    }
    .fitment-proxy-loader .vroom {
      animation: fitment-vroom 3s ease;
    }
    .fitment-proxy-loader__text {
      text-align: center;
      font-size: 14px;
      line-height: 1.4;
      opacity: .95;
      padding-bottom: 10px;
    }
    @keyframes fitment-bounce {
      0% { transform: translate(0px, 0px); }
      25% { transform: translate(0px, -0.35px); }
      75% { transform: translate(-0.5px, 0.35px); }
      100% { transform: translate(0px, 0px); }
    }
    @keyframes fitment-vroom {
      0% { transform: translateX(300%); }
      100% { transform: translateX(0%); }
    }
    @media (min-width: 767px) {
      .fitment-proxy-loader .car-wrapper {
        max-width: 480px;
      }
    }
    @media (min-width: 1200px) {
      .fitment-proxy-loader .car-wrapper {
        max-width: 540px !important;
      }
    }
  `;
  document.head.appendChild(style);
  proxyLoaderStyleInjected = true;
}
function ensureProxyLoader(section) {
  if (!section) return null;
  ensureProxyLoaderStyles();
  const results = section.querySelector(".collection__results");
  if (!results) return null;
  let loader = results.querySelector(".fitment-proxy-loader");
  if (loader) return loader;
  loader = document.createElement("div");
  loader.className = "fitment-proxy-loader";
  loader.setAttribute("aria-hidden", "true");
  loader.innerHTML = `
    <div class="col-xs-12">
      <div class="car-wrapper vroom" id="jsCarWrapper">
        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" x="0px" y="0px" viewBox="0 0 100 125" enable-background="new 0 0 100 100" xml:space="preserve">
          <path d="M55.831,38.459l-0.069,0.023c-0.19,0.077-4.71,1.949-9.06,6.177l-0.771,0.75l1.073,0.077c0.04,0.002,1.027,0.071,2.844,0.071l0,0c3.377,0,8.894-0.231,15.999-1.33l0.324-0.049l1.341-5.943l-0.557-0.041C61.936,37.818,58.14,37.908,55.831,38.459z M65.377,43.326c-6.89,1.046-12.237,1.269-15.528,1.269c-0.66,0-1.211-0.01-1.643-0.021c3.759-3.381,7.502-5.022,7.886-5.185c2.122-0.5,5.622-0.593,10.234-0.275L65.377,43.326z"></path>
          <path d="M69.446,38.407l-0.422-0.041l-1.252,5.55l0.712-0.126c2.477-0.437,4.975-0.954,7.426-1.536l0.174-0.042l1.944-2.704l-0.771-0.124C77.224,39.38,73.714,38.819,69.446,38.407z M75.513,41.359c-2.142,0.506-4.318,0.961-6.482,1.355l0.746-3.307c2.877,0.287,5.354,0.634,6.556,0.811L75.513,41.359z"></path>
          <path d="M13.771,48.221l-0.572,0.197c-2.92,1.009-5.851,2.555-5.974,2.62l-1.712,0.905h6.998l0.134-0.109c1.371-1.137,1.197-2.936,1.19-3.011L13.771,48.221z M12.153,50.982h-2.64c0.932-0.437,2.12-0.963,3.333-1.415C12.78,49.991,12.604,50.546,12.153,50.982z"></path>
          <path d="M96.409,43.292c-0.298-0.827-0.58-1.611-0.775-2.399l0.7-1.852l-0.527-0.13c-0.064-0.016-1.591-0.387-3.361-0.387c-0.622,0-1.205,0.047-1.731,0.14l-0.301,0.071l-0.084,0.29c-0.002,0.007-0.258,0.825-2.161,0.825c-0.52,0-1.117-0.061-1.775-0.183c-0.848-0.157-2.029-0.433-3.524-0.784c-5.602-1.312-15.21-3.564-24.135-3.275c-9.123,0.294-14.221,3.223-18.316,5.575c-0.957,0.551-1.865,1.072-2.762,1.525c-1.397,0.003-16.381,0.115-23.043,3.069l-1.191,0.527c-5.424,2.397-9.006,3.981-9.564,5.722c-0.147,0.462-0.092,0.918,0.166,1.356c1.08,1.821-0.113,3.188-1.303,4.011c-0.481,0.333-0.436,0.741-0.395,0.899c0.463,1.771,9.438,2.895,9.527,2.902l0.031,0.005l1.619-0.054l-0.271-0.645c-0.433-1.031-0.651-2.123-0.651-3.246c0-4.623,3.761-8.383,8.383-8.383s8.384,3.76,8.384,8.383c0,0.945-0.166,1.893-0.494,2.813l-0.234,0.657l0.697-0.016c3.529-0.075,6.374-0.113,8.455-0.113c3.145,0,7.29-0.043,11.679-0.089c5.305-0.056,11.316-0.12,16.843-0.12c2.02,0,3.985,0.009,5.85,0.028l1.244,0.013l-0.201-0.634c-0.261-0.825-0.394-1.678-0.394-2.539c0-4.623,3.762-8.383,8.384-8.383s8.384,3.76,8.384,8.383c0,0.396-0.034,0.804-0.109,1.287l-0.103,0.659l0.658-0.111c0.453-0.078,0.9-0.153,1.35-0.224c4.497-0.705,5.316-3.328,6.133-7.564C98.117,48.037,97.211,45.516,96.409,43.292z"></path>
          <path d="M56.969,55.789c-0.544-0.043-1.087-0.101-1.632-0.126l-1.632-0.1c-0.545-0.038-1.088-0.054-1.633-0.079l-1.632-0.071l-3.264-0.09c-0.544-0.018-1.088-0.013-1.633-0.02l-1.632-0.015c-1.087-0.018-2.175,0.002-3.264,0.011c-1.088,0.004-2.176,0.017-3.265,0.054l-1.631,0.043c-0.338,0.006-0.674,0.023-1.01,0.038c-0.012-0.144-0.02-0.287-0.052-0.43l-0.104-0.564c-0.027-0.189-0.087-0.374-0.142-0.557c-0.057-0.183-0.106-0.367-0.169-0.548l-0.214-0.53c-0.064-0.181-0.158-0.346-0.243-0.516c-0.091-0.168-0.163-0.344-0.275-0.499l-0.313-0.473l-0.346-0.446c-0.117-0.146-0.25-0.28-0.373-0.42c-0.121-0.141-0.266-0.262-0.396-0.393c-0.131-0.131-0.281-0.243-0.419-0.364c-0.143-0.117-0.292-0.226-0.44-0.333c-0.311-0.201-0.627-0.387-0.965-0.532l-0.045,0.085c0.609,0.36,1.156,0.833,1.635,1.359c0.115,0.136,0.242,0.262,0.346,0.407c0.106,0.143,0.221,0.279,0.32,0.427l0.291,0.446l0.257,0.469c0.091,0.154,0.152,0.322,0.226,0.483c0.071,0.163,0.148,0.324,0.199,0.496l0.168,0.505c0.049,0.172,0.084,0.346,0.127,0.521c0.041,0.172,0.084,0.346,0.102,0.521l0.073,0.53c0.032,0.175,0.022,0.354,0.035,0.533l0.022,0.533l0.004,0.07l0.068,0.002l6.528,0.018l6.528-0.007l13.057-0.036v-0.095c-0.545-0.06-1.089-0.133-1.632-0.172L56.969,55.789z"></path>
          <path d="M94.379,46.438c-1.014,0-1.375,1.794-1.375,3.33c0,1.535,0.361,3.329,1.375,3.329c1.017,0,1.377-1.794,1.377-3.329C95.756,48.232,95.396,46.438,94.379,46.438z M94.379,52.056c-0.174-0.284-0.412-1.078-0.412-2.287c0-1.211,0.238-2.006,0.412-2.288c0.175,0.282,0.415,1.077,0.415,2.288C94.794,50.978,94.554,51.771,94.379,52.056z"></path>
          <g class="bounce">
            <path class="st0" d="M20.962,50.097c-3.953,0-7.159,3.205-7.159,7.16s3.206,7.16,7.159,7.16c3.955,0,7.161-3.205,7.161-7.16S24.917,50.097,20.962,50.097z"></path>
            <animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 21 57" to="360 21 57" begin="0s" dur="0.7s" repeatCount="indefinite"></animateTransform>
          </g>
          <g class="bounce">
            <path class="st0" d="M81.174,50.097c-3.953,0-7.159,3.205-7.159,7.16s3.206,7.16,7.159,7.16c3.955,0,7.16-3.205,7.16-7.16S85.129,50.097,81.174,50.097z"></path>
            <animateTransform attributeType="xml" attributeName="transform" type="rotate" from="0 81 57" to="360 81 57" begin="0s" dur="0.7s" repeatCount="indefinite"></animateTransform>
          </g>
        </svg>
      </div>
    </div>
    <div class="fitment-proxy-loader__text">We are loading your parts...</div>
  `;
  results.prepend(loader);
  return loader;
}
function setProxyLoading(section, loading) {
  const loader = ensureProxyLoader(section);
  if (!loader) return;
  loader.setAttribute("aria-hidden", loading ? "false" : "true");
}
var COMPATIBLE_PAGE_LIMIT_STORAGE_KEY = "vs_fitment_page_limit_v1";
function clampCompatiblePageLimit(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.max(1, Math.min(100, Math.floor(n)));
}
function readConfiguredProductsPerPage(section) {
  if (!section) return null;
  const host = section.querySelector("[data-products-per-page]") || (section.matches?.("[data-products-per-page]") ? section : null);
  if (!host) return null;
  return clampCompatiblePageLimit(host.getAttribute("data-products-per-page"));
}
function rememberCompatiblePageLimit(limit) {
  const clamped = clampCompatiblePageLimit(limit);
  if (!clamped) return;
  try {
    sessionStorage.setItem(COMPATIBLE_PAGE_LIMIT_STORAGE_KEY, String(clamped));
  } catch (_) {
  }
}
function getRememberedCompatiblePageLimit() {
  try {
    return clampCompatiblePageLimit(sessionStorage.getItem(COMPATIBLE_PAGE_LIMIT_STORAGE_KEY));
  } catch (_) {
    return null;
  }
}
function captureCompatibleCollectionView(section) {
  if (!section || compatibleCollectionViewState.has(section)) return;
  const listEl = section.querySelector("product-list.product-list, .product-list");
  const paginationEl = section.querySelector(".collection__pagination.pagination");
  const configuredLimit = readConfiguredProductsPerPage(section);
  const domCount = listEl ? listEl.querySelectorAll(".collection__product-item[data-variant-skus]").length : 0;
  const page = Math.max(1, Math.floor(Number(new URL(location.href).searchParams.get("page") || 1) || 1));
  // Prefer theme setting; never treat a short remainder page (page > 1) as the page size.
  let initialLimit = configuredLimit;
  if (!initialLimit && page <= 1 && domCount > 0) initialLimit = clampCompatiblePageLimit(domCount);
  if (!initialLimit) initialLimit = getRememberedCompatiblePageLimit();
  if (initialLimit) rememberCompatiblePageLimit(initialLimit);
  harvestProductCardAsides(listEl);
  compatibleCollectionViewState.set(section, {
    listEl,
    listHtml: listEl ? listEl.innerHTML : null,
    initialLimit,
    paginationEl,
    paginationHtml: paginationEl ? paginationEl.innerHTML : null,
    paginationDisplay: paginationEl ? paginationEl.style.display : ""
  });
}
function restoreCompatibleCollectionView(section) {
  const state = compatibleCollectionViewState.get(section);
  if (!state) return false;
  if (state.listEl && state.listEl.isConnected && state.listHtml != null) {
    state.listEl.innerHTML = state.listHtml;
  }
  if (state.paginationEl && state.paginationEl.isConnected) {
    if (state.paginationHtml != null) state.paginationEl.innerHTML = state.paginationHtml;
    state.paginationEl.style.display = state.paginationDisplay || "";
  }
  compatibleCollectionViewState.delete(section);
  return true;
}
function getCompatiblePageLimit(section, fallback = 24) {
  const configured = readConfiguredProductsPerPage(section);
  if (configured) {
    rememberCompatiblePageLimit(configured);
    return configured;
  }
  const remembered = getRememberedCompatiblePageLimit();
  if (remembered) return remembered;
  const state = compatibleCollectionViewState.get(section);
  const fromState = clampCompatiblePageLimit(state?.initialLimit);
  const page = Math.max(1, Math.floor(Number(new URL(location.href).searchParams.get("page") || 1) || 1));
  // On page > 1, DOM card count / records.length is often a short remainder — ignore it.
  if (fromState && page <= 1) {
    rememberCompatiblePageLimit(fromState);
    return fromState;
  }
  const fromFallback = clampCompatiblePageLimit(fallback);
  if (fromFallback && page <= 1) {
    rememberCompatiblePageLimit(fromFallback);
    return fromFallback;
  }
  return 24;
}
function renderCompatiblePagination(section, pagination) {
  const paginationEl = section.querySelector(".collection__pagination.pagination");
  if (!paginationEl) return;
  const pagesRaw = Number(pagination?.pages);
  const pages = Number.isFinite(pagesRaw) && pagesRaw > 0 ? Math.floor(pagesRaw) : 0;
  const pageRaw = Number(pagination?.page);
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? Math.floor(pageRaw) : 1;
  if (pages <= 1) {
    paginationEl.style.display = "none";
    return;
  }
  paginationEl.style.display = "";
  const prevHref = page > 1 ? buildCompatiblePageUrl(page - 1) : "";
  const nextHref = page < pages ? buildCompatiblePageUrl(page + 1) : "";
  const prevHtml = prevHref
    ? '<a class="pagination__item group" rel="prev" href="' + escapeHtml(prevHref) + '"><span class="animated-arrow animated-arrow--reverse"></span></a>'
    : '<span class="pagination__item pagination__item--disabled"><span class="animated-arrow animated-arrow--reverse"></span></span>';
  const nextHtml = nextHref
    ? '<a class="pagination__item group" rel="next" href="' + escapeHtml(nextHref) + '"><span class="animated-arrow"></span></a>'
    : '<span class="pagination__item pagination__item--disabled"><span class="animated-arrow"></span></span>';
  paginationEl.innerHTML = prevHtml + '<span class="pagination__current">' + page + '&nbsp;&nbsp;/&nbsp;&nbsp;' + pages + '</span>' + nextHtml;
}
function getEmptyResultsConfig() {
  const cfg = window.vsEmptyResultsConfig || {};
  const showHelp = cfg.showHelp !== false;
  return {
    heading: String(cfg.heading || "").trim() || "No compatible products found.",
    helpText: String(cfg.helpText || "").trim() || (showHelp ? "Looking for something? Call us on" : ""),
    phone: String(cfg.phone || "").trim() || (showHelp ? "02 9679 8644" : ""),
    showHelp
  };
}
function buildEmptyResultsHelpHtml(cfg) {
  if (!cfg?.showHelp) return "";
  const helpText = String(cfg.helpText || "").trim();
  const phone = String(cfg.phone || "").trim();
  if (!helpText && !phone) return "";
  const tel = phone.replace(/[^\d+]/g, "");
  const phoneHtml = phone
    ? tel
      ? '<a href="tel:' + escapeHtml(tel) + '">' + escapeHtml(phone) + "</a>"
      : escapeHtml(phone)
    : "";
  const textHtml = helpText ? escapeHtml(helpText) + (phoneHtml ? " " : "") : "";
  return '<p class="text-subdued">' + textHtml + phoneHtml + "</p>";
}
function buildCompatibleEmptyStateHtml(options = {}) {
  const cfg = getEmptyResultsConfig();
  const heading = String(options.emptyHeading || cfg.heading || "").trim() || "No compatible products found.";
  const showHelp = options.showHelp === false ? false : cfg.showHelp;
  return '<div class="empty-state"><div class="prose"><p class="h5">' + escapeHtml(heading) + "</p>" + buildEmptyResultsHelpHtml({ ...cfg, showHelp }) + "</div></div>";
}
function buildServerSkuCache(listEl) {
  const cache = new Map();
  if (!listEl) return cache;
  listEl.querySelectorAll('[data-handle]').forEach((card) => {
    const handle = card.getAttribute('data-handle');
    const skuEl = card.querySelector('.product-card__sku');
    if (handle && skuEl && skuEl.textContent.trim()) {
      cache.set(handle, skuEl.textContent.trim());
    }
  });
  listEl.querySelectorAll('product-card').forEach((card) => {
    const handle = card.getAttribute('handle');
    const skuEl = card.querySelector('.product-card__sku');
    if (handle && skuEl && skuEl.textContent.trim()) {
      cache.set(handle, skuEl.textContent.trim());
    }
  });
  return cache;
}
var PRODUCT_CARD_ASIDE_STORAGE_KEY = "vs_product_card_aside_v1";
var productCardAsideByHandle = /* @__PURE__ */ new Map();
function loadProductCardAsideCache() {
  if (productCardAsideByHandle.size) return;
  try {
    const parsed = JSON.parse(sessionStorage.getItem(PRODUCT_CARD_ASIDE_STORAGE_KEY) || "{}");
    if (parsed && typeof parsed === "object") {
      Object.entries(parsed).forEach(([handle, html]) => {
        if (handle && typeof html === "string" && (html.includes("product-card__aside") || html.startsWith("<!--no-color-aside-->"))) {
          productCardAsideByHandle.set(handle, html);
        }
      });
    }
  } catch (_) {
  }
}
function persistProductCardAsideCache() {
  try {
    const obj = {};
    productCardAsideByHandle.forEach((html, handle) => {
      if (handle && html) obj[handle] = html;
    });
    sessionStorage.setItem(PRODUCT_CARD_ASIDE_STORAGE_KEY, JSON.stringify(obj));
  } catch (_) {
  }
}
function harvestProductCardAsides(root) {
  loadProductCardAsideCache();
  if (!root) return;
  const scope = typeof root === "string" ? null : root;
  let nodes = [];
  if (typeof root === "string" && root) {
    try {
      nodes = [...new DOMParser().parseFromString(root, "text/html").querySelectorAll("product-card[handle]")];
    } catch (_) {
      nodes = [];
    }
  } else if (scope?.querySelectorAll) {
    nodes = [...scope.querySelectorAll("product-card[handle]")];
  }
  let changed = false;
  nodes.forEach((card) => {
    const handle = String(card.getAttribute("handle") || "").trim();
    const aside = card.querySelector(".product-card__aside");
    if (!handle || !aside) return;
    const html = aside.outerHTML;
    if (!html || productCardAsideByHandle.get(handle) === html) return;
    productCardAsideByHandle.set(handle, html);
    changed = true;
  });
  if (changed) persistProductCardAsideCache();
}
function getCachedProductCardAsideHtml(handle) {
  loadProductCardAsideCache();
  const key = String(handle || "").trim();
  if (!key) return "";
  const html = productCardAsideByHandle.get(key) || "";
  // Empty marker from product.js enrich — treat as no aside to inject.
  if (!html || html.startsWith("<!--no-color-aside-->")) return "";
  return html;
}
function hasResolvedProductCardAside(handle) {
  loadProductCardAsideCache();
  const key = String(handle || "").trim();
  return key ? productCardAsideByHandle.has(key) : false;
}
function swatchCssBackgroundForColorName(name) {
  const raw = String(name || "").trim().toLowerCase();
  if (!raw) return "#cccccc";
  const map = {
    black: "black",
    blue: "#1e3a8a",
    red: "#b91c1c",
    white: "white",
    grey: "#9ca3af",
    gray: "#9ca3af",
    silver: "#c0c0c0",
    yellow: "#eab308",
    green: "#15803d",
    orange: "#ea580c",
    pink: "#db2777",
    purple: "#7e22ce",
    brown: "#92400e",
    gold: "#ca8a04",
    navy: "#1e3a5f"
  };
  const color = map[raw] || raw;
  return "linear-gradient(to right, " + color + ", " + color + ")";
}
function buildProductCardAsideFromProductJson(productJson) {
  if (!productJson || typeof productJson !== "object") return "";
  const options = Array.isArray(productJson.options) ? productJson.options : [];
  const colorOption = options.find((opt) => /colou?r/i.test(String(opt?.name || "")));
  if (!colorOption) return "";
  const values = Array.isArray(colorOption.values) ? colorOption.values.slice(0, 4) : [];
  if (!values.length) return "";
  const variants = Array.isArray(productJson.variants) ? productJson.variants : [];
  const handle = String(productJson.handle || "").trim();
  const productId = productJson.id != null ? String(productJson.id) : handle;
  const paramName = "swatch-proxy-" + productId + "-1";
  const parts = [
    '<div class="product-card__aside">',
    '<fieldset class="product-card__swatch-list h-stack gap-0.5" data-option-position="1">',
    '<legend class="sr-only">' + escapeHtml(colorOption.name || "Colour") + "</legend>"
  ];
  values.forEach((value, index) => {
    const label = String(value || "").trim();
    if (!label) return;
    const variant = variants.find((v) => String(v?.option1 || "") === label) || variants[index];
    const inputId = paramName + "-" + index;
    const variantId = variant?.id != null ? String(variant.id) : "";
    const media = variant?.featured_image ? {
      alt: variant.featured_image.alt || label,
      id: variant.featured_image.id,
      position: variant.featured_image.position || index + 1,
      preview_image: {
        aspect_ratio: variant.featured_image.width && variant.featured_image.height ? variant.featured_image.width / variant.featured_image.height : 1,
        height: variant.featured_image.height,
        width: variant.featured_image.width,
        src: variant.featured_image.src
      },
      aspect_ratio: variant.featured_image.width && variant.featured_image.height ? variant.featured_image.width / variant.featured_image.height : 1,
      height: variant.featured_image.height,
      media_type: "image",
      src: variant.featured_image.src,
      width: variant.featured_image.width
    } : null;
    const disabledClass = variant && variant.available === false ? " is-disabled" : "";
    const checked = index === 0 ? ' checked="checked"' : "";
    parts.push(
      '<input class="sr-only" type="radio" name="' + escapeHtml(paramName) + '" id="' + escapeHtml(inputId) + '" value="' + escapeHtml(label) + '"' + checked + ' data-option-position="1"' + (variantId ? ' data-variant-id="' + escapeHtml(variantId) + '"' : "") + (media ? ' data-variant-media="' + escapeHtml(JSON.stringify(media)) + '"' : "") + ">",
      '<label class="color-swatch color-swatch--sm' + disabledClass + ' rounded-full" style="--swatch-background: ' + escapeHtml(swatchCssBackgroundForColorName(label)) + '" for="' + escapeHtml(inputId) + '"><span class="sr-only">' + escapeHtml(label) + "</span></label>"
    );
  });
  if ((colorOption.values || []).length > 4 && handle) {
    parts.push('<a href="/products/' + escapeHtml(handle) + '" data-instant class="color-swatch__view-more rounded-full text-xxs text-subdued">+' + String((colorOption.values || []).length - 4) + "</a>");
  }
  parts.push("</fieldset></div>");
  return parts.join("");
}
async function enrichMissingProxyCardSwatches(listEl, products) {
  if (!listEl) return;
  const missing = (Array.isArray(products) ? products : []).filter((product) => {
    const handle = String(product?.handle || "").trim();
    if (!handle) return false;
    if (hasResolvedProductCardAside(handle)) return false;
    return !listEl.querySelector('product-card[handle="' + CSS.escape(handle) + '"] .product-card__aside');
  });
  if (!missing.length) return;
  // Enrich every card on the current proxy page (page size can be 32+).
  await Promise.all(missing.map(async (product) => {
    const handle = String(product.handle || "").trim();
    try {
      const response = await fetch("/products/" + encodeURIComponent(handle) + ".js", {
        headers: { Accept: "application/json" },
        credentials: "same-origin"
      });
      if (!response.ok) {
        // Remember misses briefly? skip — may be temporary
        return;
      }
      const json = await response.json();
      const asideHtml = buildProductCardAsideFromProductJson(json);
      if (!asideHtml) {
        // Cache empty marker so we don't refetch products without colour options on every page view.
        productCardAsideByHandle.set(handle, "<!--no-color-aside-->");
        persistProductCardAsideCache();
        return;
      }
      productCardAsideByHandle.set(handle, asideHtml);
      persistProductCardAsideCache();
      const card = listEl.querySelector('product-card[handle="' + CSS.escape(handle) + '"]');
      const info = card?.querySelector(".product-card__info");
      if (info && !info.querySelector(".product-card__aside")) {
        info.insertAdjacentHTML("beforeend", asideHtml);
      }
    } catch (_) {
    }
  }));
}
/** Stock label next to card price. Prefers product-level hasPositiveInventory from proxy; else inventory/available. */
function getProductCardStockSettings() {
  const s = window.themeVariables?.settings || {};
  return {
    enabled: s.showStockStatusOnCard !== false,
    inStockLabel: String(s.cardInStockLabel || "In stock"),
    inStockColor: String(s.cardInStockTextColor || "#22c55e"),
    backorderLabel: String(s.cardBackorderLabel || "Available to Backorder"),
    backorderColor: String(s.cardBackorderTextColor || "#ef4444")
  };
}
function buildProductCardStockStatusHtml(product) {
  const stockSettings = getProductCardStockSettings();
  if (!stockSettings.enabled) return "";
  if (!isProductAvailableOnCard(product)) return "";
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  let qty = null;
  let policy = "";
  let hasInStock = false;
  let hasBackorder = false;
  // Backend: product-level flag across all variants. Prefer this over displayed-variant inventoryQuantity.
  if (product?.hasPositiveInventory === true || product?.has_positive_inventory === true) {
    hasInStock = true;
    const qtyRaw = product?.inventoryQuantity ?? product?.inventory_quantity;
    qty = qtyRaw === null || qtyRaw === undefined || qtyRaw === "" ? null : Number(qtyRaw);
    policy = String(product?.inventoryPolicy || product?.inventory_policy || "").toLowerCase();
  } else if (variants.length) {
    for (const variant of variants) {
      if (variant?.available === false) continue;
      const qtyRaw = variant?.inventoryQuantity ?? variant?.inventory_quantity;
      const variantQty = qtyRaw === null || qtyRaw === undefined || qtyRaw === "" ? null : Number(qtyRaw);
      const variantPolicy = String(variant?.inventoryPolicy || variant?.inventory_policy || "").toLowerCase();
      if (Number.isFinite(variantQty) && variantQty > 0) {
        hasInStock = true;
        qty = variantQty;
        policy = variantPolicy;
        break;
      }
      if (variant?.available === true && (!Number.isFinite(variantQty) || variantQty <= 0) && variantPolicy === "continue") {
        hasBackorder = true;
        if (!Number.isFinite(qty)) {
          qty = variantQty;
          policy = variantPolicy;
        }
      } else if (variant?.available === true && !Number.isFinite(variantQty)) {
        hasInStock = true;
        policy = variantPolicy;
        break;
      }
    }
  } else {
    const qtyRaw = product?.inventoryQuantity ?? product?.inventory_quantity;
    qty = qtyRaw === null || qtyRaw === undefined || qtyRaw === "" ? null : Number(qtyRaw);
    policy = String(product?.inventoryPolicy || product?.inventory_policy || "").toLowerCase();
    if (Number.isFinite(qty) && qty > 0) hasInStock = true;
    else if (Number.isFinite(qty) && qty <= 0 && policy === "continue") hasBackorder = true;
    else if (product?.available !== false) hasInStock = true;
  }
  if (hasInStock) {
    const qtyAttr = Number.isFinite(qty) ? ' data-inventory-quantity="' + escapeHtml(String(qty)) + '"' : "";
    const policyAttr = policy ? ' data-inventory-policy="' + escapeHtml(policy) + '"' : "";
    return '<span class="product-card__stock-status product-card__stock-status--in-stock text-xs" style="color: ' + escapeHtml(stockSettings.inStockColor) + ';" data-stock-status="in-stock"' + qtyAttr + policyAttr + ">" + escapeHtml(stockSettings.inStockLabel) + "</span>";
  }
  if (hasBackorder) {
    return '<span class="product-card__stock-status product-card__stock-status--backorder text-xs" style="color: ' + escapeHtml(stockSettings.backorderColor) + ';" data-stock-status="backorder" data-inventory-quantity="' + escapeHtml(String(Number.isFinite(qty) ? qty : 0)) + '" data-inventory-policy="' + escapeHtml(policy || "continue") + '">' + escapeHtml(stockSettings.backorderLabel) + "</span>";
  }
  return "";
}
function buildProductCardPriceWithStockHtml(product, priceText, compareText, hasCompare) {
  if (!priceText && !compareText) {
    const stockOnly = buildProductCardStockStatusHtml(product);
    return stockOnly ? '<div class="product-card__price-with-stock">' + stockOnly + "</div>" : "";
  }
  const priceList = '<price-list class="price-list"><sale-price class="' + (hasCompare ? "text-on-sale" : "text-subdued") + '">' + priceText + "</sale-price>" + (hasCompare ? '<compare-at-price class="text-subdued line-through">' + compareText + "</compare-at-price>" : "") + "</price-list>";
  return '<div class="product-card__price-with-stock">' + priceList + buildProductCardStockStatusHtml(product) + "</div>";
}
function resolveProxyProductCardSurface(listEl, listHtml) {
  let sample = null;
  if (typeof listHtml === "string" && listHtml.includes("product-card")) {
    try {
      sample = new DOMParser().parseFromString(listHtml, "text/html").querySelector("product-card[class*='product-card']");
    } catch (_) {
      sample = null;
    }
  }
  if (!sample) {
    sample = listEl?.querySelector?.("product-card[class*='product-card']") || document.querySelector(".shopify-section--main-collection product-card, .shopify-section--main-search product-card");
  }
  if (sample) {
    const className = String(sample.getAttribute("class") || "").trim();
    const style = String(sample.getAttribute("style") || "").trim();
    // Drop secondary-media / reveal flags; keep surface (bg-custom / blends / text-custom).
    const kept = className
      .split(/\s+/)
      .filter((c) => c && c !== "product-card--show-secondary-media")
      .join(" ");
    if (kept.includes("product-card")) {
      return {
        className: kept.includes("product-card--blends") || kept.includes("bg-custom") ? kept : "product-card bg-custom text-custom",
        style: style || ""
      };
    }
  }
  // Match storefront card surface when sample isn't available (white card on dark collection bg).
  return {
    className: "product-card bg-custom text-custom",
    style: "--background: 255 255 255; --text-color: 26 26 26;"
  };
}
function renderCompatibleProducts(section, products, pagination, options = {}) {
  captureCompatibleCollectionView(section);
  const state = compatibleCollectionViewState.get(section);
  if (!state?.listEl || !state.listEl.isConnected) return false;
  const listEl = state.listEl;
  harvestProductCardAsides(listEl);
  if (state.listHtml) harvestProductCardAsides(state.listHtml);
  const cardSurface = resolveProxyProductCardSurface(listEl, state.listHtml);
  const serverSkuCache = buildServerSkuCache(listEl);
  const items = Array.isArray(products) ? products : [];
  const showCompatibleBadge = options.showCompatibleBadge !== false;
  if (items.length === 0) {
    listEl.innerHTML = buildCompatibleEmptyStateHtml(options);
    renderCompatiblePagination(section, pagination);
    return true;
  }
  listEl.innerHTML = items.map((product) => {
    const handle = String(product?.handle || "").trim();
    const title = escapeHtml(product?.title || "");
    const rawUrl = product?.url || (handle ? '/products/' + handle : '#');
    const url = escapeHtml(rawUrl);
    const imageUrl = product?.image ? escapeHtml(product.image) : "";
    const matchedSkus = Array.isArray(product?.matchedSkus)
      ? product.matchedSkus.map((sku) => normalizeSkuKey(sku)).filter(Boolean)
      : [];
    const skuAttr = escapeHtml(matchedSkus.join(","));
    const priceText = formatShopMoney(product?.price) || (product?._domPriceText ? escapeHtml(String(product._domPriceText)) : "");
    const compareText = formatShopMoney(product?.compareAtPrice);
    const hasCompare = Number(product?.compareAtPrice) > Number(product?.price) && !!compareText;
    const productNoteMap = getCollectionFitmentNoteMap(buildFitmentNoteMapFromMatchedFitment(product?.matchedFitment));
    const matchedSet = matchedSkus.length ? new Set(matchedSkus) : null;
    const fitmentNote = showCompatibleBadge ? pickCollectionDisplayFootnote(matchedSkus, productNoteMap, product, matchedSet) : "";
    const fitmentNoteBadgeHtml = buildFitmentNoteBadgeHtml(fitmentNote, rawUrl);
    const imageHtml = imageUrl
      ? '<div class="product-card__figure">' + fitmentNoteBadgeHtml + '<a href="' + url + '" data-instant><img src="' + imageUrl + '" loading="lazy" class="product-card__image product-card__image--primary aspect-square"></a></div>'
      : "";
    const priceHtml = buildProductCardPriceWithStockHtml(product, priceText, compareText, hasCompare);
    const soldOutBadgeHtml = !isProductAvailableOnCard(product)
      ? '<sold-out-badge class="badge badge--sold-out">Sold out</sold-out-badge>'
      : '';
    const onSaleBadgeHtml = hasCompare
      ? '<on-sale-badge class="badge badge--on-sale">On sale</on-sale-badge>'
      : '';
    const isUniversalFit = isUniversalFitProduct(product);
    let fitBadgeHtml = "";
    if (showCompatibleBadge) {
      fitBadgeHtml = isUniversalFit
        ? '<span class="badge badge--universal-fit">Universal Fit</span>'
        : '<span class="badge badge--compatible">Compatible with your car</span>';
    } else if (isUniversalFit) {
      fitBadgeHtml = '<span class="badge badge--universal-fit">Universal Fit</span>';
    }
    const badgeListHtml = '<div class="product-card__badge-list">' + soldOutBadgeHtml + onSaleBadgeHtml + fitBadgeHtml + '</div>';
    // Keep the SKU line in sync with the server-rendered card (snippets/product-card.liquid).
    // Use the raw (non-normalized) SKU for display; prefer the matched variant SKU from the proxy.
    // For universal products with no matchedSkus, fall back to the server-rendered SKU cached before rerender.
    const rawDisplaySku = (Array.isArray(product?.matchedSkus) && product.matchedSkus.find((sku) => String(sku ?? "").trim())) || product?.sku || serverSkuCache.get(handle) || "";
    const skuHtml = rawDisplaySku
      ? '<span class="product-card__sku text-xs text-subdued">' + escapeHtml(String(rawDisplaySku).trim()) + '</span>'
      : '';
    const asideHtml = getCachedProductCardAsideHtml(handle);
    const handleAttr = handle ? ' handle="' + escapeHtml(handle) + '"' : "";
    const styleAttr = cardSurface.style ? ' style="' + escapeHtml(cardSurface.style) + '"' : "";
    const infoInner = '<div class="v-stack gap-0.5 w-full"><span class="product-card__title"><a href="' + url + '" class="bold" data-instant>' + title + '</a></span>' + skuHtml + priceHtml + "</div>" + asideHtml;
    return '<div class="collection__product-item" data-compatible="' + (showCompatibleBadge ? "true" : "false") + '" data-variant-skus="' + skuAttr + '"><product-card' + handleAttr + ' class="' + escapeHtml(cardSurface.className) + '"' + styleAttr + ">" + badgeListHtml + imageHtml + '<div class="product-card__info">' + infoInner + "</div></product-card></div>";
  }).join("");
  renderCompatiblePagination(section, pagination);
  enrichMissingProxyCardSwatches(listEl, items).catch(() => {});
  return true;
}
function updateVisibleCount(section, forcedCount = null) {
  const countEl = section.querySelector(".collection__results .text-center");
  if (!countEl) return;
  if (!/^\s*\d+ products?\s*$/i.test(countEl.textContent.trim())) return;
  const visibleCount = Number.isFinite(forcedCount)
    ? Math.max(0, Math.floor(forcedCount))
    : section.querySelectorAll(".collection__product-item:not(.collection__product-item--hide)").length;
  countEl.textContent = visibleCount + " product" + (visibleCount === 1 ? "" : "s");
}
function getCollectionHandleFromPath() {
  const parts = location.pathname.split("/").filter(Boolean);
  const idx = parts.indexOf("collections");
  if (idx >= 0 && parts[idx + 1]) {
    try {
      return decodeURIComponent(parts[idx + 1]);
    } catch (_) {
      return parts[idx + 1];
    }
  }
  return "all";
}
function toCollectionRequestUrl(urlLike) {
  if (urlLike instanceof URL) return new URL(urlLike.toString());
  if (typeof urlLike === "string" && urlLike) return new URL(urlLike, location.origin);
  return new URL(location.href);
}
function toPublicCollectionUrl(urlLike) {
  const url = toCollectionRequestUrl(urlLike);
  url.searchParams.delete("section_id");
  return url;
}
function parseFitmentProxyListingResponse(data, vehicleId) {
  const matched = /* @__PURE__ */ new Set();
  const products = Array.isArray(data?.products) ? data.products : [];
  products.forEach((product) => {
    const skus = Array.isArray(product?.matchedSkus) ? product.matchedSkus : [];
    skus.forEach((sku) => {
      const normalized = normalizeSkuKey(sku);
      if (normalized) matched.add(normalized);
    });
  });
  const fitmentNoteBySku = buildFitmentNoteMapFromApiResponse(data);
  cacheFitmentNotesForVehicle(vehicleId, fitmentNoteBySku);
  const fitmentNotesMerged = mergeFitmentNoteMaps(getCachedFitmentNotesForVehicle(vehicleId), fitmentNoteBySku);
  return {
    matched,
    pagination: data?.pagination || null,
    products,
    filters: data?.filters || null,
    fitmentNoteBySku: fitmentNotesMerged
  };
}
function buildFitmentProxyFilterParams(currentUrl, qs) {
  const sortBy = currentUrl.searchParams.get("sort_by") || "";
  const priceMin = currentUrl.searchParams.get("filter.v.price.gte") || currentUrl.searchParams.get("price_min") || "";
  const priceMax = currentUrl.searchParams.get("filter.v.price.lte") || currentUrl.searchParams.get("price_max") || "";
  const inStockOnly = currentUrl.searchParams.get("filter.v.availability") === "1";
  if (sortBy) qs.set("sort_by", sortBy);
  if (priceMin !== "") qs.set("filter.v.price.gte", priceMin);
  if (priceMax !== "") qs.set("filter.v.price.lte", priceMax);
  if (inStockOnly) qs.set("filter.v.availability", "1");
  currentUrl.searchParams.forEach((value, key) => {
    if (key.startsWith("filter.p.") && value !== "") {
      qs.append(key, value);
    }
  });
  writeCategoriesToProxyQuery(qs, readCategoriesFromUrl(currentUrl));
  applyBrakePositionToQueryString(currentUrl, qs);
}
/** Shows a non-destructive request error without clearing active filters. */
function showProxyListingError(message) {
  const text = String(message || "").trim() || "Unable to apply filters. Please try again.";
  console.error("[fitment] proxy listing error:", text);
  try {
    const section = getFitmentListingSection();
    const host = section?.querySelector(".collection__results .v-stack.gap-6") || section?.querySelector(".collection__results") || section;
    if (!host) return;
    let banner = host.querySelector("[data-proxy-listing-error]");
    if (!banner) {
      banner = document.createElement("div");
      banner.setAttribute("data-proxy-listing-error", "1");
      banner.className = "prose";
      banner.style.margin = "0 0 12px";
      host.prepend(banner);
    }
    banner.innerHTML = '<p class="text-subdued">' + escapeHtml(text) + "</p>";
  } catch (_) {
  }
}
/** Clears any temporary proxy listing error banner. */
function clearProxyListingError() {
  document.querySelectorAll("[data-proxy-listing-error]").forEach((el) => el.remove());
}
async function fetchCategoryOnlyCollection(userCookie, signal, limitHint, sourceUrl) {
  const currentUrl = toCollectionRequestUrl(sourceUrl);
  const collectionHandle = getCollectionHandleFromPath();
  const pageVal = Number(currentUrl.searchParams.get("page") || "1");
  const page = Number.isFinite(pageVal) && pageVal > 0 ? Math.floor(pageVal) : 1;
  const qs = new URLSearchParams({
    collection: collectionHandle,
    page: String(page),
    limit: String(Math.max(1, Math.min(100, Number(limitHint) || 24)))
  });
  buildFitmentProxyFilterParams(currentUrl, qs);
  if (currentUrl.searchParams.get("filter.v.availability") === "1") qs.set("inStockOnly", "1");
  if (userCookie) qs.set("uc", userCookie);
  const response = await fetch(APP_PROXY_COLLECTION_PATH + "?" + qs.toString(), {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    signal
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.ok) {
    const error = new Error(data?.message || ("Proxy category collection failed (" + response.status + ")"));
    error.status = response.status;
    error.payload = data;
    throw error;
  }
  return parseFitmentProxyListingResponse(data, null);
}
async function fetchCategoryOnlySearch(userCookie, signal, limitHint, sourceUrl) {
  const currentUrl = toCollectionRequestUrl(sourceUrl);
  const query = getSearchQueryFromUrl(currentUrl);
  if (!query) {
    throw new Error("Search query is required for category search");
  }
  const pageVal = Number(currentUrl.searchParams.get("page") || "1");
  const page = Number.isFinite(pageVal) && pageVal > 0 ? Math.floor(pageVal) : 1;
  const collection = currentUrl.searchParams.get("collection") || "";
  const qs = new URLSearchParams({
    q: query,
    page: String(page),
    limit: String(Math.max(1, Math.min(100, Number(limitHint) || 24)))
  });
  if (collection) qs.set("collection", collection);
  buildFitmentProxyFilterParams(currentUrl, qs);
  if (userCookie) qs.set("uc", userCookie);
  const response = await fetch(APP_PROXY_SEARCH_PATH + "?" + qs.toString(), {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    signal
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.ok) {
    const error = new Error(data?.message || ("Proxy category search failed (" + response.status + ")"));
    error.status = response.status;
    error.payload = data;
    throw error;
  }
  return parseFitmentProxyListingResponse(data, null);
}
async function fetchCategoryFilteredListing(userCookie, signal, limitHint, sourceUrl) {
  if (isFitmentSearchPage(sourceUrl)) {
    return fetchCategoryOnlySearch(userCookie, signal, limitHint, sourceUrl);
  }
  return fetchCategoryOnlyCollection(userCookie, signal, limitHint, sourceUrl);
}
function markProductUniversal(product) {
  return { ...product, isUniversal: true };
}
var searchUniversalCache = { key: "", products: [] };
function getSearchUniversalCacheKey(query, sourceUrl) {
  const url = toCollectionRequestUrl(sourceUrl);
  return [
    query,
    url.searchParams.get("filter.v.availability") || "",
    url.searchParams.get("filter.v.price.gte") || url.searchParams.get("price_min") || "",
    url.searchParams.get("filter.v.price.lte") || url.searchParams.get("price_max") || "",
    readCategoriesFromUrl(url).join(",")
  ].join("|");
}
async function fetchProxySearchPage(qs, signal) {
  const response = await fetch(APP_PROXY_SEARCH_PATH + "?" + qs.toString(), {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    signal
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.ok) {
    throw new Error(data?.message || ("Proxy search failed (" + response.status + ")"));
  }
  return data;
}
async function fetchSearchUniversalProducts(query, userCookie, signal, sourceUrl) {
  const cacheKey = getSearchUniversalCacheKey(query, sourceUrl);
  const knownUniversalHandles = getKnownUniversalHandlesFromSearchPage();
  if (searchUniversalCache.key === cacheKey && Array.isArray(searchUniversalCache.products)) {
    return searchUniversalCache.products;
  }
  const currentUrl = toCollectionRequestUrl(sourceUrl);
  const collection = currentUrl.searchParams.get("collection") || "";
  const universalsByHandle = /* @__PURE__ */ new Map();
  const pageLimit = 100;
  const maxPages = 5;
  for (let page = 1; page <= maxPages; page += 1) {
    const qs = new URLSearchParams({
      q: query,
      page: String(page),
      limit: String(pageLimit)
    });
    if (collection) qs.set("collection", collection);
    buildFitmentProxyFilterParams(currentUrl, qs);
    if (currentUrl.searchParams.get("filter.v.availability") === "1") qs.set("inStockOnly", "1");
    if (userCookie) qs.set("uc", userCookie);
    const data = await fetchProxySearchPage(qs, signal);
    const products = Array.isArray(data?.products) ? data.products : [];
    products.forEach((product) => {
      const handle = String(product?.handle || "").trim();
      if (!handle || universalsByHandle.has(handle)) return;
      // Proxy search often marks true Universal Fit products as isUniversal:false.
      // Trust Shopify card/metafield handles from the current search page as well.
      if (!isUniversalFitProduct(product, knownUniversalHandles)) return;
      universalsByHandle.set(handle, markProductUniversal(product));
    });
    const pages = Number(data?.pagination?.pages || 1);
    if (page >= pages || products.length < pageLimit) break;
  }
  const products = [...universalsByHandle.values()];
  searchUniversalCache = { key: cacheKey, products };
  console.log("[fitment][search-debug] universal catalog matches", {
    query,
    count: products.length,
    knownUniversalHandles: knownUniversalHandles.size
  });
  return products;
}
function mergeSearchVehicleAndUniversalProducts(vehicleResult, universals, page, limit) {
  const vehicleProducts = Array.isArray(vehicleResult?.products) ? vehicleResult.products : [];
  const vehicleTotal = Number(vehicleResult?.pagination?.total);
  const resolvedVehicleTotal = Number.isFinite(vehicleTotal) ? vehicleTotal : vehicleProducts.length;
  const vehicleHandles = new Set(vehicleProducts.map((product) => String(product?.handle || "").trim()).filter(Boolean));
  const uniqueUniversals = (Array.isArray(universals) ? universals : []).filter((product) => {
    const handle = String(product?.handle || "").trim();
    return handle && !vehicleHandles.has(handle);
  });
  const combinedTotal = resolvedVehicleTotal + uniqueUniversals.length;
  const start = (Math.max(1, page) - 1) * limit;
  let pageProducts;
  if (start >= resolvedVehicleTotal) {
    const uniStart = start - resolvedVehicleTotal;
    pageProducts = uniqueUniversals.slice(uniStart, uniStart + limit);
  } else {
    const remaining = Math.max(0, limit - vehicleProducts.length);
    pageProducts = remaining > 0
      ? vehicleProducts.concat(uniqueUniversals.slice(0, remaining))
      : vehicleProducts;
  }
  return {
    ...vehicleResult,
    products: sortCompatibleProductsVehicleFirst(pageProducts),
    pagination: {
      ...(vehicleResult?.pagination || {}),
      page,
      limit,
      total: combinedTotal,
      pages: Math.max(1, Math.ceil(combinedTotal / Math.max(1, limit)))
    }
  };
}
async function fetchFitmentSearch(vehicleId, userCookie, signal, limitHint, sourceUrl) {
  const currentUrl = toCollectionRequestUrl(sourceUrl);
  const query = getSearchQueryFromUrl(currentUrl);
  if (!query) {
    throw new Error("Search query is required for fitment search");
  }
  const pageVal = Number(currentUrl.searchParams.get("page") || "1");
  const page = Number.isFinite(pageVal) && pageVal > 0 ? Math.floor(pageVal) : 1;
  const collection = currentUrl.searchParams.get("collection") || "";
  const limit = Math.max(1, Math.min(100, Number(limitHint) || 24));
  console.log("[fitment][search-debug] fetchFitmentSearch input", {
    vehicleId,
    query,
    collection: collection || null,
    page,
    limitHint: limit,
    sourceUrl: currentUrl.toString()
  });
  const qs = new URLSearchParams({
    q: query,
    vehicleId: String(vehicleId),
    compatible_only: "1",
    page: String(page),
    limit: String(limit)
  });
  if (collection) qs.set("collection", collection);
  buildFitmentProxyFilterParams(currentUrl, qs);
  if (currentUrl.searchParams.get("filter.v.availability") === "1") qs.set("inStockOnly", "1");
  if (userCookie) qs.set("uc", userCookie);
  const response = await fetch(APP_PROXY_SEARCH_PATH + "?" + qs.toString(), {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    signal
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.ok) {
    throw new Error(data?.message || ("Proxy search fitment failed (" + response.status + ")"));
  }
  const vehicleResult = parseFitmentProxyListingResponse(data, vehicleId);
  let universals = [];
  try {
    universals = await fetchSearchUniversalProducts(query, userCookie, signal, currentUrl);
  } catch (error) {
    if (error?.name === "AbortError") throw error;
    console.warn("[fitment][search-debug] universal search merge failed", error);
  }
  const merged = mergeSearchVehicleAndUniversalProducts(vehicleResult, universals, page, limit);
  console.log("[fitment][search-debug] fetchFitmentSearch response", {
    ok: !!data?.ok,
    vehicleReturned: Array.isArray(data?.products) ? data.products.length : 0,
    vehicleTotal: Number(data?.pagination?.total || 0),
    universalMerged: universals.length,
    pageProducts: merged.products.length,
    vehicleSpecific: merged.products.filter((p) => !isUniversalFitProduct(p)).length,
    universal: merged.products.filter((p) => isUniversalFitProduct(p)).length,
    combinedTotal: Number(merged.pagination?.total || 0),
    combinedPages: Number(merged.pagination?.pages || 0)
  });
  return merged;
}
async function fetchFitmentListingProducts(vehicleId, userCookie, signal, limitHint, sourceUrl) {
  if (isFitmentSearchPage(sourceUrl)) {
    return fetchFitmentSearch(vehicleId, userCookie, signal, limitHint, sourceUrl);
  }
  return fetchMatchedSkus(vehicleId, userCookie, signal, limitHint, sourceUrl);
}
async function fetchMatchedSkus(vehicleId, userCookie, signal, limitHint, sourceUrl) {
  const currentUrl = toCollectionRequestUrl(sourceUrl);
  const collectionHandle = getCollectionHandleFromPath();
  const pageVal = Number(currentUrl.searchParams.get("page") || "1");
  const page = Number.isFinite(pageVal) && pageVal > 0 ? Math.floor(pageVal) : 1;
  console.log("[fitment][flow-debug] fetchMatchedSkus input", {
    vehicleId,
    collectionHandle,
    page,
    limitHint,
    sourceUrl: currentUrl.toString()
  });
  const qs = new URLSearchParams({
    vehicleId: String(vehicleId),
    collection: collectionHandle,
    page: String(page),
    limit: String(Math.max(1, Math.min(100, Number(limitHint) || 24)))
  });
  buildFitmentProxyFilterParams(currentUrl, qs);
  if (currentUrl.searchParams.get("filter.v.availability") === "1") qs.set("inStockOnly", "1");
  if (userCookie) qs.set("uc", userCookie);
  const response = await fetch(APP_PROXY_COLLECTION_PATH + "?" + qs.toString(), {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    signal
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || !data?.ok) {
    throw new Error(data?.message || ("Proxy collection fitment failed (" + response.status + ")"));
  }
  console.log("[fitment][flow-debug] fetchMatchedSkus response", {
    ok: !!data?.ok,
    total: Number(data?.pagination?.total || 0),
    pages: Number(data?.pagination?.pages || 0),
    products: Array.isArray(data?.products) ? data.products.length : 0
  });
  return parseFitmentProxyListingResponse(data, vehicleId);
}
function buildFitmentNoteBadgeHtml(note, productUrl) {
  const text = String(note || "").trim();
  if (!text) return "";
  const href = escapeHtml(productUrl || "#");
  return '<div class="fitment-note-badge badge badge--fitment-note"><span class="fitment-note-badge__text">' + escapeHtml(text) + '</span><a href="' + href + '" class="fitment-note-badge__more" data-instant>View more</a></div>';
}
function ensureFitmentNoteBadgeOnCard(record, note) {
  const card = record?.el?.querySelector(".product-card");
  if (!card) return;
  const figure = card.querySelector(".product-card__figure");
  if (!figure) return;
  let badge = figure.querySelector(".fitment-note-badge");
  const text = String(note || "").trim();
  if (!text) {
    badge?.remove();
    return;
  }
  const productUrl = getProductUrlFromCard(record);
  if (!badge) {
    badge = document.createElement("div");
    badge.className = "fitment-note-badge badge badge--fitment-note";
    const textEl = document.createElement("span");
    textEl.className = "fitment-note-badge__text";
    const moreEl = document.createElement("a");
    moreEl.className = "fitment-note-badge__more";
    moreEl.textContent = "View more";
    moreEl.setAttribute("data-instant", "");
    badge.appendChild(textEl);
    badge.appendChild(moreEl);
    figure.appendChild(badge);
  }
  const textEl = badge.querySelector(".fitment-note-badge__text");
  const moreEl = badge.querySelector(".fitment-note-badge__more");
  if (textEl) textEl.textContent = text;
  if (moreEl) moreEl.href = productUrl || "#";
}
function applyFitmentNoteBadgesToRecords(records, noteMap, matchedSet) {
  const mergedMap = getCollectionFitmentNoteMap(noteMap);
  (records || []).forEach((record) => {
    const isCompatible = record.el.getAttribute("data-compatible") === "true";
    if (!isCompatible) {
      ensureFitmentNoteBadgeOnCard(record, "");
      return;
    }
    ensureFitmentNoteBadgeOnCard(record, pickCollectionFootnoteForSkus(record.skus, mergedMap, matchedSet));
  });
}
function collectItemsAndSkus(root) {
  const items = Array.from(root.querySelectorAll(".collection__product-item[data-variant-skus]"));
  const records = items.map((el) => ({ el, skus: parseSkuList(el.getAttribute("data-variant-skus") || "") }));
  return {
    records,
    skuList: [...new Set(records.flatMap((record) => record.skus))]
  };
}
function applyCompatibilityBadgesToRecords(records, matchedSet) {
  let compatibleCount = 0;
  records.forEach((record) => {
    const isCompatible = record.skus.length > 0 && record.skus.some((sku) => matchedSet.has(sku));
    record.el.setAttribute("data-compatible", isCompatible ? "true" : "false");
    record.el.classList.remove("collection__product-item--hide");
    if (isCompatible) compatibleCount += 1;
  });
  console.log("[fitment][badge-debug] applied compatibility to cards", {
    cards: records.length,
    matchedSkuSetSize: matchedSet?.size || 0,
    compatibleCards: compatibleCount,
    incompatibleCards: Math.max(0, records.length - compatibleCount)
  });
}
function applyMatchToRecords(records, matchedSet, appendTarget) {
  records.forEach((record) => {
    const isCompatible = record.skus.length > 0 && record.skus.some((sku) => matchedSet.has(sku));
    record.el.setAttribute("data-compatible", isCompatible ? "true" : "false");
    if (appendTarget) {
      if (isCompatible) {
        record.el.classList.remove("collection__product-item--hide");
        appendTarget.appendChild(record.el);
      }
    } else if (isCompatible) {
      record.el.classList.remove("collection__product-item--hide");
    } else {
      record.el.classList.add("collection__product-item--hide");
    }
  });
}
var compatibleFilterAbortController = null;
var compatibilityBadgeAbortController = null;
var productFitmentNoteAbortController = null;
var relatedProductsCompatibilityAbortController = null;
var productFitmentNoteInitialized = false;
var lastProductPageVariant = null;
var productFitmentPendingSku = null;
var lastAppliedFitmentSku = "";
var lastProductFitmentSyncKey = "";
var productSkuSyncObserver = null;
var productSkuSyncScheduled = false;
var pendingCompatibleCollectionUrl = null;
var relatedProductsOriginalState = /* @__PURE__ */ new WeakMap();
function getRelatedSection(root) {
  if (root instanceof Element && root.classList.contains("shopify-section--product-recommendations")) {
    return root;
  }
  if (root instanceof Element) {
    return root.closest(".shopify-section--product-recommendations");
  }
  return document.querySelector(".shopify-section--product-recommendations");
}
function rememberRelatedSectionState(section) {
  if (!section || relatedProductsOriginalState.has(section)) return;
  const list = section.querySelector("product-list.product-list");
  if (!list) return;
  const items = Array.from(list.querySelectorAll("[data-related-product-item][data-variant-skus]"));
  if (!items.length) return;
  relatedProductsOriginalState.set(section, items.map((item) => item.cloneNode(true)));
}
function rebuildRelatedSectionFromState(section) {
  const list = section?.querySelector("product-list.product-list");
  const snapshot = section ? relatedProductsOriginalState.get(section) : null;
  if (!list || !snapshot || !snapshot.length) return false;
  list.replaceChildren(...snapshot.map((item) => item.cloneNode(true)));
  return true;
}
function collectRelatedItemsAndSkus(root) {
  const section = getRelatedSection(root);
  if (!section) {
    return { section: null, records: [], skuList: [] };
  }
  rememberRelatedSectionState(section);
  rebuildRelatedSectionFromState(section);
  const items = Array.from(section.querySelectorAll("[data-related-product-item][data-variant-skus]"));
  const records = items.map((el) => ({ el, skus: parseSkuList(el.getAttribute("data-variant-skus") || "") }));
  return {
    section,
    records,
    skuList: [...new Set(records.flatMap((record) => record.skus))]
  };
}
function updateRelatedSectionVisibility(section, hasVehicle, compatibleCount, totalCount) {
  if (!section) return;
  const shouldHide = hasVehicle && totalCount > 0 && compatibleCount === 0;
  section.toggleAttribute("hidden", shouldHide);
}
function syncRelatedCarouselControls(section, visibleCount) {
  if (!section) return;
  const carousel = section.querySelector("scroll-carousel");
  const carouselId = carousel?.id || "";
  if (!carouselId) return;
  const hasScrollableContent = visibleCount > 1;
  carousel.classList.toggle("is-scrollable", hasScrollableContent);
  const scrollbar = section.querySelector(".scrollbar");
  if (scrollbar) {
    scrollbar.toggleAttribute("hidden", !hasScrollableContent);
  }
  const buttons = Array.from(section.querySelectorAll(`[aria-controls="${carouselId}"]`));
  buttons.forEach((button) => {
    if (!(button instanceof HTMLButtonElement)) return;
    if (hasScrollableContent) {
      button.removeAttribute("hidden");
      button.disabled = false;
    } else {
      button.setAttribute("hidden", "");
      button.disabled = true;
    }
  });
}
function setRelatedFitmentLoading(section, isLoading) {
  const root = section?.querySelector("[data-related-fitment-root]");
  if (!root) return;
  root.setAttribute("data-related-fitment-state", isLoading ? "pending" : "ready");
}
function initRelatedFitmentLoadingState() {
  if (!getVehicleId()) return;
  document.querySelectorAll(".shopify-section--product-recommendations [data-related-fitment-root]").forEach((root) => {
    const hasItems = root.querySelector("[data-related-product-item]");
    const isDynamic = root.querySelector("product-recommendations");
    if (hasItems || isDynamic) {
      root.setAttribute("data-related-fitment-state", "pending");
    }
  });
}
async function updateRelatedProductsCompatibility(root) {
  const data = collectRelatedItemsAndSkus(root);
  if (!data.records.length) return;
  const section = data.section;
  const vehicleId = getVehicleId();
  setRelatedFitmentLoading(section, !!vehicleId);
  if (!vehicleId) {
    data.records.forEach((record) => {
      record.el.setAttribute("data-compatible", "false");
    });
    applyFitmentNoteBadgesToRecords(data.records, /* @__PURE__ */ new Map());
    updateRelatedSectionVisibility(section, false, data.records.length, data.records.length);
    syncRelatedCarouselControls(section, data.records.length);
    setRelatedFitmentLoading(section, false);
    return;
  }
  const applyMatchedState = (matchedSet, noteMap) => {
    const compatibleRecords = [];
    const incompatibleRecords = [];
    data.records.forEach((record) => {
      const isCompatible = record.skus.length > 0 && record.skus.some((sku) => matchedSet.has(sku));
      if (isCompatible) {
        compatibleRecords.push(record);
      } else {
        incompatibleRecords.push(record);
      }
    });
    if (compatibleRecords.length === 0) {
      data.records.forEach((record) => {
        record.el.setAttribute("data-compatible", "false");
      });
      applyFitmentNoteBadgesToRecords(data.records, /* @__PURE__ */ new Map());
      updateRelatedSectionVisibility(section, false, data.records.length, data.records.length);
      syncRelatedCarouselControls(section, data.records.length);
      setRelatedFitmentLoading(section, false);
      return;
    }
    incompatibleRecords.forEach((record) => record.el.remove());
    compatibleRecords.forEach((record) => {
      record.el.setAttribute("data-compatible", "true");
    });
    applyFitmentNoteBadgesToRecords(compatibleRecords, noteMap || /* @__PURE__ */ new Map(), matchedSet);
    updateRelatedSectionVisibility(section, true, compatibleRecords.length, data.records.length);
    syncRelatedCarouselControls(section, compatibleRecords.length);
    setRelatedFitmentLoading(section, false);
  };
  const cachedMatched = getCachedMatchedSkusForVehicle(vehicleId);
  if (cachedMatched && cachedMatched.size > 0) {
    applyMatchedState(cachedMatched, getCachedFitmentNotesForVehicle(vehicleId));
    return;
  }
  if (relatedProductsCompatibilityAbortController) {
    relatedProductsCompatibilityAbortController.abort();
  }
  relatedProductsCompatibilityAbortController = new AbortController();
  const signal = relatedProductsCompatibilityAbortController.signal;
  try {
    const userCookie = getUserCookie();
    const serviceSkus = await fetchVehicleSkusFromFitmentService(vehicleId, userCookie, signal).catch(() => null);
    if (signal.aborted) return;
    if (serviceSkus?.skus && serviceSkus.skus.size > 0) {
      applyMatchedState(serviceSkus.skus, serviceSkus.noteBySku);
      return;
    }
    const result = await fetchMatchedSkus(vehicleId, userCookie, signal, Math.max(24, data.records.length), location.href);
    if (signal.aborted) return;
    applyMatchedState(result?.matched || /* @__PURE__ */ new Set(), result?.fitmentNoteBySku);
  } catch (error) {
    if (signal.aborted) return;
    console.error("[fitment] related compatibility filtering failed:", error);
    data.records.forEach((record) => {
      record.el.setAttribute("data-compatible", "false");
    });
    applyFitmentNoteBadgesToRecords(data.records, /* @__PURE__ */ new Map());
    updateRelatedSectionVisibility(section, false, data.records.length, data.records.length);
    syncRelatedCarouselControls(section, data.records.length);
    setRelatedFitmentLoading(section, false);
  }
}
function getProductSkuBlock() {
  return document.querySelector('.shopify-section--main-product [data-block-type="sku"]');
}
function getProductFitmentBadgeMount() {
  const section = document.querySelector(".shopify-section--main-product");
  return section?.querySelector("product-rerender[allow-partial-rerender]") || section;
}
function ensureProductFitmentBadgeHostPosition() {
  const mount = getProductFitmentBadgeMount();
  if (!mount) return null;
  let host = mount.querySelector("[data-vs-pdp-fitment-badges-root]");
  if (!host) {
    host = document.createElement("div");
    host.className = "product-info__fitment-badges-root";
    host.setAttribute("data-vs-pdp-fitment-badges-root", "");
  }
  const skuBlock = mount.querySelector('[data-block-type="sku"]');
  if (skuBlock) {
    skuBlock.insertAdjacentElement("afterend", host);
  } else if (!host.isConnected) {
    mount.appendChild(host);
  }
  return host;
}
function updateProductFitmentBadgeDisplay(footnote, longFootnote, isCompatible = false) {
  const host = ensureProductFitmentBadgeHostPosition();
  if (!host) return;
  let groupEl = host.querySelector("[data-vs-pdp-fitment-badges]");
  if (!groupEl) {
    groupEl = document.createElement("div");
    groupEl.className = "product-info__fitment-badges";
    groupEl.setAttribute("data-vs-pdp-fitment-badges", "");
    host.appendChild(groupEl);
  }
  let compatibleEl = groupEl.querySelector("[data-vs-compatible-badge]");
  if (isCompatible) {
    if (!compatibleEl) {
      compatibleEl = document.createElement("span");
      compatibleEl.className = "badge badge--compatible product-info__compatible-badge";
      compatibleEl.setAttribute("data-vs-compatible-badge", "");
      groupEl.appendChild(compatibleEl);
    }
    compatibleEl.textContent = getCompatibleBadgeLabel();
  } else {
    compatibleEl?.remove();
  }
  let noteEl = groupEl.querySelector("[data-vs-long-footnote]");
  const html = buildProductFitmentBadgeHtml(footnote, longFootnote);
  if (!html) {
    noteEl?.remove();
    if (!groupEl.children.length) groupEl.remove();
    return;
  }
  if (!noteEl) {
    noteEl = document.createElement("span");
    noteEl.className = "badge badge--fitment-note-pdp product-info__long-footnote";
    noteEl.setAttribute("data-vs-long-footnote", "");
    groupEl.appendChild(noteEl);
  }
  noteEl.innerHTML = html;
}
function updateProductLongFootnoteDisplay(note) {
  const text = String(note || "").trim();
  updateProductFitmentBadgeDisplay(text, text, false);
}
function getVariantSkuFromProductPage() {
  const skuEl = getProductSkuBlock()?.querySelector("variant-sku, .product-info__sku");
  if (!skuEl) return "";
  const match = String(skuEl.textContent || "").match(/SKU:\s*([^\n]+)/i);
  return match ? normalizeSkuKey(match[1]) : "";
}
function isSkuCompatibleOnProductPage(skuKey, vehicleId) {
  if (!skuKey || !vehicleId) return false;
  if (isSkuMatchedForVehicle(skuKey, vehicleId)) return true;
  const notes = getCachedFitmentNotesForVehicle(vehicleId);
  return notes?.has(skuKey) || false;
}
function isSkuMatchedForVehicle(skuKey, vehicleId) {
  if (!skuKey || !vehicleId) return false;
  const matched = getCachedMatchedSkusForVehicle(vehicleId);
  return matched?.has(skuKey) || false;
}
function applyProductFitmentBadgeFromMapDirect(skuKey, noteMap, variant) {
  const entry = pickFitmentEntryForSkus([skuKey], noteMap || /* @__PURE__ */ new Map());
  if (!entry) return false;
  const footnote = alignFootnoteToVariantSide(pickPdpFootnoteFromEntry(entry), variant);
  const longFootnote = alignLongFootnoteForPdp(pickLongFootnoteForPdp(entry), variant);
  updateProductFitmentBadgeDisplay(footnote, longFootnote, true);
  lastAppliedFitmentSku = skuKey;
  return true;
}
function scheduleProductSkuFitmentSync() {
  if (productSkuSyncScheduled) return;
  productSkuSyncScheduled = true;
  requestAnimationFrame(() => {
    productSkuSyncScheduled = false;
    syncProductFitmentBadgeWithDisplayedSku();
  });
}
function syncProductFitmentBadgeWithDisplayedSku() {
  if (window.themeVariables?.settings?.pageType !== "product") return;
  const skuKey = getVariantSkuFromProductPage();
  const vehicleId = getVehicleId();
  ensureProductFitmentBadgeHostPosition();
  if (!skuKey || !vehicleId) {
    updateProductFitmentBadgeDisplay("", "", false);
    productFitmentPendingSku = null;
    lastAppliedFitmentSku = "";
    lastProductFitmentSyncKey = "";
    return;
  }
  const variant = { sku: skuKey };
  const syncKey = skuKey + "::" + getVariantSideLabel(variant);
  if (syncKey === lastProductFitmentSyncKey) return;
  lastProductFitmentSyncKey = syncKey;
  const noteMap = getCachedFitmentNotesForVehicle(vehicleId);
  if (applyProductFitmentBadgeFromMapDirect(skuKey, noteMap, variant)) {
    productFitmentPendingSku = null;
    return;
  }
  const isCompatible = isSkuCompatibleOnProductPage(skuKey, vehicleId);
  if (!isCompatible) {
    updateProductFitmentBadgeDisplay("", "", false);
    productFitmentPendingSku = null;
    lastAppliedFitmentSku = "";
    return;
  }
  updateProductFitmentBadgeDisplay("", "", true);
  if (productFitmentPendingSku === skuKey) return;
  productFitmentPendingSku = skuKey;
  if (productFitmentNoteAbortController) productFitmentNoteAbortController.abort();
  productFitmentNoteAbortController = new AbortController();
  const signal = productFitmentNoteAbortController.signal;
  refreshProductFitmentNoteForSku(skuKey, signal, variant).finally(() => {
    if (productFitmentPendingSku === skuKey && !signal.aborted) {
      productFitmentPendingSku = null;
    }
  });
}
function bindProductSkuDisplaySync() {
  const rerender = document.querySelector(".shopify-section--main-product product-rerender[allow-partial-rerender]");
  if (!rerender || rerender.dataset.vsSkuSyncBound === "1") return;
  rerender.dataset.vsSkuSyncBound = "1";
  productSkuSyncObserver?.disconnect();
  productSkuSyncObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      const target = mutation.target;
      if (mutation.type !== "characterData" && mutation.type !== "childList") continue;
      if (target?.closest?.('[data-block-type="sku"]') || target?.closest?.("variant-sku") || target?.matches?.('[data-block-type="sku"]') || target?.closest?.("variant-picker")) {
        scheduleProductSkuFitmentSync();
        return;
      }
      if (mutation.type === "childList" && [...mutation.addedNodes].some((node) => node.nodeType === 1 && (node.matches?.('[data-block-type="sku"]') || node.matches?.("variant-picker") || node.querySelector?.('[data-block-type="sku"], variant-picker')))) {
        scheduleProductSkuFitmentSync();
        return;
      }
    }
  });
  productSkuSyncObserver.observe(rerender, { childList: true, subtree: true, characterData: true });
  scheduleProductSkuFitmentSync();
}
function applyProductFitmentBadgeFromMap(skuKey, noteMap, variant) {
  return applyProductFitmentBadgeFromMapDirect(skuKey, noteMap, variant);
}
function realignVisibleProductFitmentBadge(variant) {
  const noteEl = document.querySelector("[data-vs-long-footnote]");
  const currentText = String(noteEl?.textContent || "").trim();
  if (!currentText) return false;
  const aligned = alignFootnoteToVariantSide(currentText, variant);
  if (!aligned || aligned === currentText) return false;
  updateProductFitmentBadgeDisplay(aligned, aligned, true);
  return true;
}
function applyProductFitmentBadgeForVariant(fallbackVariant) {
  lastProductPageVariant = resolveActiveProductVariant(fallbackVariant);
  scheduleProductSkuFitmentSync();
}
async function refreshProductFitmentNoteForSku(skuKey, signal, variant) {
  const vehicleId = getVehicleId();
  if (!vehicleId || !skuKey) return;
  const resolvedVariant = variant || { sku: skuKey };
  const cached = getCachedFitmentNotesForVehicle(vehicleId);
  if (applyProductFitmentBadgeFromMapDirect(skuKey, cached, resolvedVariant)) {
    lastProductFitmentSyncKey = skuKey + "::" + getVariantSideLabel(resolvedVariant);
    return;
  }
  const userCookie = getUserCookie();
  const serviceResult = await fetchVehicleSkusFromFitmentService(vehicleId, userCookie, signal).catch(() => null);
  if (signal?.aborted) return;
  if (applyProductFitmentBadgeFromMapDirect(skuKey, serviceResult?.noteBySku, resolvedVariant)) {
    lastProductFitmentSyncKey = skuKey + "::" + getVariantSideLabel(resolvedVariant);
    return;
  }
  const result = await fetchMatchedSkus(vehicleId, userCookie, signal, 100, location.href).catch(() => null);
  if (signal?.aborted) return;
  if (applyProductFitmentBadgeFromMapDirect(skuKey, result?.fitmentNoteBySku, resolvedVariant)) {
    lastProductFitmentSyncKey = skuKey + "::" + getVariantSideLabel(resolvedVariant);
    return;
  }
  if (isSkuCompatibleOnProductPage(skuKey, vehicleId)) {
    updateProductFitmentBadgeDisplay("", "", true);
  }
}
function handleProductVariantFitmentChange(event) {
  const form = getProductMainForm();
  if (!form || event?.detail?.formId && event.detail.formId !== form.id) return;
  lastProductPageVariant = resolveActiveProductVariant(event?.detail?.variant || lastProductPageVariant);
  scheduleProductSkuFitmentSync();
}
function initProductPageFitmentNote() {
  if (window.themeVariables?.settings?.pageType !== "product") return;
  const section = document.querySelector(".shopify-section--main-product");
  if (!section) return;
  const form = getProductMainForm();
  if (form && !productFitmentNoteInitialized) {
    productFitmentNoteInitialized = true;
    document.addEventListener("variant:change", handleProductVariantFitmentChange);
    form.addEventListener("product:rerender", () => {
      ensureProductFitmentBadgeHostPosition();
      scheduleProductSkuFitmentSync();
    });
  }
  bindProductSkuDisplaySync();
  if (!getVehicleId()) {
    updateProductFitmentBadgeDisplay("", "", false);
    return;
  }
  ensureProductFitmentBadgeHostPosition();
  scheduleProductSkuFitmentSync();
}
async function applyFitmentNotesToCollectionSection(sourceUrl, noteMapHint) {
  const section = getFitmentListingSection();
  if (!section) return;
  const records = collectItemsAndSkus(section).records;
  if (!records.length) return;
  const vehicleId = getVehicleId();
  const currentUrl = toCollectionRequestUrl(sourceUrl || location.href);
  if (!vehicleId) {
    applyFitmentNoteBadgesToRecords(records, /* @__PURE__ */ new Map());
    return;
  }
  let noteMap = mergeFitmentNoteMaps(getCachedFitmentNotesForVehicle(vehicleId), noteMapHint);
  const cachedMatched = getCachedMatchedSkusForVehicle(vehicleId);
  applyFitmentNoteBadgesToRecords(records, noteMap, cachedMatched);
  const missingOnCompatible = records.some((record) => {
    if (record.el.getAttribute("data-compatible") !== "true") return false;
    return !pickCollectionFootnoteForSkus(record.skus, noteMap, cachedMatched);
  });
  if (!missingOnCompatible) {
    console.log("[fitment][footnote-debug] applied from cache/hint", {
      vehicleId,
      cards: records.length,
      noteMapSize: noteMap.size,
      url: currentUrl.toString()
    });
    return;
  }
  if (compatibilityBadgeAbortController) {
    compatibilityBadgeAbortController.abort();
  }
  compatibilityBadgeAbortController = new AbortController();
  const signal = compatibilityBadgeAbortController.signal;
  try {
    const userCookie = getUserCookie();
    const limitHint = getCompatiblePageLimit(section, records.length || 24);
    const result = await fetchMatchedSkus(vehicleId, userCookie, signal, limitHint, currentUrl);
    if (signal.aborted) return;
    noteMap = mergeFitmentNoteMaps(noteMap, result?.fitmentNoteBySku);
    applyFitmentNoteBadgesToRecords(records, noteMap, result?.matched);
    console.log("[fitment][footnote-debug] refreshed after fetch", {
      vehicleId,
      cards: records.length,
      noteMapSize: noteMap.size,
      missingBeforeFetch: missingOnCompatible,
      url: currentUrl.toString()
    });
  } catch (error) {
    if (!signal.aborted) console.error("[fitment] fitment note badge refresh failed:", error);
  }
}
async function updateCompatibilityBadges(sourceUrl) {
  const section = getFitmentListingSection();
  if (!section) {
    console.log("[fitment][badge-debug] no collection/search section found");
    return;
  }
  const currentUrl = toCollectionRequestUrl(sourceUrl || location.href);
  const hasTagFilter = !!(currentUrl.searchParams.get("filter.v.tag") || "").startsWith(COMPATIBLE_TAG_PREFIX);
  const hasCompatibleOnly = currentUrl.searchParams.get(COMPATIBLE_ONLY_KEY) === "1";
  if (hasTagFilter || hasCompatibleOnly) {
    console.log("[fitment][footnote-debug] filter mode: refresh footnote badges only", {
      hasTagFilter,
      hasCompatibleOnly,
      url: currentUrl.toString()
    });
    await applyFitmentNotesToCollectionSection(currentUrl);
    return;
  }
  const currentData = collectItemsAndSkus(section);
  if (!currentData.records.length) {
    console.log("[fitment][badge-debug] no cards with data-variant-skus on page");
    return;
  }
  const vehicleId = getVehicleId();
  console.log("[fitment][badge-debug] update start", {
    vehicleId,
    cards: currentData.records.length,
    uniqueCardSkus: currentData.skuList.length,
    url: currentUrl.toString()
  });
  if (!vehicleId) {
    applyCompatibilityBadgesToRecords(currentData.records, /* @__PURE__ */ new Set());
    applyFitmentNoteBadgesToRecords(currentData.records, /* @__PURE__ */ new Map());
    return;
  }
  const cachedMatched = getCachedMatchedSkusForVehicle(vehicleId);
  const cachedNotes = getCachedFitmentNotesForVehicle(vehicleId);
  if (cachedMatched && cachedMatched.size > 0) {
    console.log("[fitment][badge-debug] using local cached SKUs", {
      vehicleId,
      cachedSkuSize: cachedMatched.size
    });
    applyCompatibilityBadgesToRecords(currentData.records, cachedMatched);
    if (cachedNotes && cachedNotes.size > 0) {
      applyFitmentNoteBadgesToRecords(currentData.records, cachedNotes, cachedMatched);
      return;
    }
  }
  if (compatibilityBadgeAbortController) {
    compatibilityBadgeAbortController.abort();
  }
  compatibilityBadgeAbortController = new AbortController();
  const signal = compatibilityBadgeAbortController.signal;
  try {
    const userCookie = getUserCookie();
    const serviceSkus = await fetchVehicleSkusFromFitmentService(vehicleId, userCookie, signal).catch((error) => {
      if (!signal.aborted) {
        console.warn("[fitment][badge-debug] fitment service sku fallback failed, using proxy", error);
      }
      return null;
    });
    if (signal.aborted) return;
    if (serviceSkus?.skus && serviceSkus.skus.size > 0) {
      console.log("[fitment][badge-debug] using fitment service SKUs", {
        vehicleId,
        serviceSkuSize: serviceSkus.skus.size
      });
      applyCompatibilityBadgesToRecords(currentData.records, serviceSkus.skus);
      applyFitmentNoteBadgesToRecords(currentData.records, serviceSkus.noteBySku || /* @__PURE__ */ new Map(), serviceSkus.skus);
      return;
    }
    if (isFitmentSearchPage(currentUrl)) {
      console.log("[fitment][badge-debug] skipping collection proxy fallback on search page");
      return;
    }
    const limitHint = getCompatiblePageLimit(section, currentData.records.length || 24);
    const result = await fetchMatchedSkus(vehicleId, userCookie, signal, limitHint, currentUrl);
    if (signal.aborted) return;
    console.log("[fitment][badge-debug] proxy response for badge update", {
      vehicleId,
      matchedSkus: result?.matched?.size || 0,
      products: Array.isArray(result?.products) ? result.products.length : 0,
      paginationTotal: Number(result?.pagination?.total || 0)
    });
    const sampleCards = currentData.records.slice(0, 10).map((record) => {
      const hasMatch = record.skus.some((sku) => result?.matched?.has(sku));
      return {
        title: record.el.querySelector(".product-card__title a")?.textContent?.trim() || "",
        skus: record.skus.slice(0, 3),
        matched: hasMatch
      };
    });
    console.log("[fitment][badge-debug] sample card matching", sampleCards);
    applyCompatibilityBadgesToRecords(currentData.records, result?.matched || /* @__PURE__ */ new Set());
    applyFitmentNoteBadgesToRecords(currentData.records, result?.fitmentNoteBySku || /* @__PURE__ */ new Map(), result?.matched);
  } catch (error) {
    if (!signal.aborted) console.error("[fitment] compatibility badge update failed:", error);
  }
}
async function applyCompatibleOnlyFallback(sourceUrl) {
  const currentUrl = toCollectionRequestUrl(sourceUrl || pendingCompatibleCollectionUrl || location.href);
  const hasTagFilter = !!(currentUrl.searchParams.get("filter.v.tag") || "").startsWith(COMPATIBLE_TAG_PREFIX);
  const hasCompatibleOnly = currentUrl.searchParams.get(COMPATIBLE_ONLY_KEY) === "1";
  const hasCategories = hasActiveCategories(currentUrl);
  const isCompatibleMode = hasTagFilter || hasCompatibleOnly;
  console.log("[fitment][flow-debug] applyCompatibleOnlyFallback start", {
    url: currentUrl.toString(),
    hasTagFilter,
    hasCompatibleOnly,
    hasCategories,
    priceMin: currentUrl.searchParams.get("filter.v.price.gte") || "",
    priceMax: currentUrl.searchParams.get("filter.v.price.lte") || "",
    availability: currentUrl.searchParams.get("filter.v.availability") || ""
  });
  const section = getFitmentListingSection();
  if (!section) return;
  if (!isCompatibleMode && !hasCategories) {
    clearProxyListingError();
    setProxyLoading(section, false);
    restoreCompatibleCollectionView(section);
    section.querySelectorAll(".collection__product-item--hide").forEach((el) => el.classList.remove("collection__product-item--hide"));
    updateVisibleCount(section);
    syncProxyModeActiveFacets(currentUrl);
    await updateCompatibilityBadges(currentUrl);
    return;
  }
  if (isFitmentSearchPage(currentUrl) && !getSearchQueryFromUrl(currentUrl)) {
    setProxyLoading(section, false);
    restoreCompatibleCollectionView(section);
    console.warn("[fitment] compatible_only on search requires a search query (q)");
    return;
  }
  setProxyLoading(section, true);
  const vehicleId = getVehicleId();
  if (isCompatibleMode && !vehicleId) {
    setProxyLoading(section, false);
    restoreCompatibleCollectionView(section);
    console.warn("[fitment] compatible_only is active but no vehicleId found in vs_garage_v1");
    return;
  }
  if (compatibleFilterAbortController) {
    compatibleFilterAbortController.abort();
  }
  compatibleFilterAbortController = new AbortController();
  const signal = compatibleFilterAbortController.signal;
  const userCookie = getUserCookie();
  try {
    captureCompatibleCollectionView(section);
    const currentData = collectItemsAndSkus(section);
    const limitHint = getCompatiblePageLimit(section, currentData.records.length || 24);
    const result = isCompatibleMode
      ? await fetchFitmentListingProducts(vehicleId, userCookie, signal, limitHint, currentUrl)
      : await fetchCategoryFilteredListing(userCookie, signal, limitHint, currentUrl);
    if (signal.aborted) return;
    clearProxyListingError();
    if (result?.pagination?.limit) rememberCompatiblePageLimit(result.pagination.limit);
    const normalizedCategories = Array.isArray(result?.filters?.categories) ? result.filters.categories : null;
    if (normalizedCategories) {
      const publicUrl = toPublicCollectionUrl(currentUrl);
      writeCategoriesToSearchParams(publicUrl.searchParams, normalizedCategories);
      window.history.replaceState({ path: publicUrl.toString() }, "", publicUrl.toString());
      syncFacetFormsFromUrl(publicUrl);
    }
    const filteredProducts = isCompatibleMode
      ? filterProductsForBrakePosition(result?.products || [], currentUrl)
      : result?.products || [];
    const orderedProducts = isCompatibleMode
      ? sortCompatibleProductsVehicleFirst(filteredProducts)
      : filteredProducts;
    const rendered = renderCompatibleProducts(section, orderedProducts, result?.pagination || null, {
      showCompatibleBadge: isCompatibleMode,
      emptyHeading: isCompatibleMode ? "" : "No products found",
      showHelp: isCompatibleMode
    });
    console.log("[fitment][flow-debug] applyCompatibleOnlyFallback rendered", {
      rendered,
      totalCompatible: Number(result?.pagination?.total || 0),
      productsReturned: Array.isArray(result?.products) ? result.products.length : 0,
      productsAfterBrakePositionFilter: filteredProducts.length,
      productsAfterVehicleFirstSort: orderedProducts.length,
      vehicleSpecificFirst: orderedProducts.filter((p) => !isUniversalFitProduct(p)).length,
      universalAfter: orderedProducts.filter((p) => isUniversalFitProduct(p)).length,
      brakePositions: readBrakePositionFromUrl(currentUrl),
      categories: readCategoriesFromUrl(currentUrl),
      isCompatibleMode
    });
    if (!rendered) {
      if (isCompatibleMode) {
        applyMatchToRecords(currentData.records, result.matched);
        applyFitmentNoteBadgesToRecords(currentData.records, result?.fitmentNoteBySku || /* @__PURE__ */ new Map(), result?.matched);
      }
      setPaginationParams(section);
    }
    const totalCompatible = Number(result?.pagination?.total);
    if (isCompatibleMode && readBrakePositionFromUrl(currentUrl).length > 0) {
      updateVisibleCount(section, filteredProducts.length);
    } else if (Number.isFinite(totalCompatible)) updateVisibleCount(section, totalCompatible);
    else updateVisibleCount(section);
    syncProxyModeActiveFacets(location.href);
    if (isCompatibleMode) {
      await applyFitmentNotesToCollectionSection(currentUrl, result?.fitmentNoteBySku);
    }
  } catch (error) {
    if (signal.aborted) return;
    console.error("[fitment] collection compatibility filter failed:", error);
    const status = Number(error?.status || 0);
    const message = error?.payload?.message || error?.message || "";
    if (status === 400 || /invalid category/i.test(String(message))) {
      showProxyListingError(message || "invalid category handle(s)");
      syncProxyModeActiveFacets(currentUrl);
      return;
    }
    showProxyListingError(message || "Unable to apply filters. Please try again.");
  } finally {
    setProxyLoading(section, false);
  }
}
document.addEventListener("DOMContentLoaded", () => {
  console.log("[fitment][badge-debug] DOMContentLoaded", {
    vehicleId: getVehicleId(),
    badgeNodes: document.querySelectorAll(".collection__product-item .badge--compatible").length
  });
  syncVehicleUiForGarage();
  bindMainSearchFormCompatiblePreserve();
  document.querySelectorAll('form[is="facet-form"]').forEach((form) => {
    syncBrakePositionFromUrl(form);
  });
  toggleBrakePositionFacetVisibility();
  updateCompatibilityBadges();
  applyCompatibleOnlyFallback();
  syncProxyModeActiveFacets();
  initProductPageFitmentNote();
  initRelatedFitmentLoadingState();
  updateRelatedProductsCompatibility();
});
window.addEventListener("vs:garage-changed", refreshFitmentForGarageChange);
window.addEventListener("storage", (event) => {
  if (event.key === GARAGE_STORAGE_KEY || event.key === SELECTED_VEHICLE_STORAGE_KEY) {
    refreshFitmentForGarageChange();
  }
});
window.addEventListener("focus", syncVehicleUiForGarage);
document.addEventListener("visibilitychange", () => {
  if (!document.hidden) {
    syncVehicleUiForGarage();
  }
});
document.addEventListener("facet:update", (event) => setTimeout(async () => {
  pendingCompatibleCollectionUrl = event?.detail?.url ? toCollectionRequestUrl(event.detail.url) : null;
  if (!pendingCompatibleCollectionUrl) return;
  const publicUrl = toPublicCollectionUrl(pendingCompatibleCollectionUrl);
  window.history.replaceState({ path: publicUrl.toString() }, "", publicUrl.toString());
  const hasTagFilter = !!(pendingCompatibleCollectionUrl.searchParams.get("filter.v.tag") || "").startsWith(COMPATIBLE_TAG_PREFIX);
  const hasCompatibleOnly = pendingCompatibleCollectionUrl.searchParams.get(COMPATIBLE_ONLY_KEY) === "1";
  const hasCategories = hasActiveCategories(pendingCompatibleCollectionUrl);
  console.log("[fitment][flow-debug] facet:update", {
    incomingUrl: pendingCompatibleCollectionUrl.toString(),
    publicUrl: publicUrl.toString(),
    hasTagFilter,
    hasCompatibleOnly,
    hasCategories,
    priceMin: pendingCompatibleCollectionUrl.searchParams.get("filter.v.price.gte") || "",
    priceMax: pendingCompatibleCollectionUrl.searchParams.get("filter.v.price.lte") || "",
    availability: pendingCompatibleCollectionUrl.searchParams.get("filter.v.availability") || ""
  });
  if (!hasTagFilter && !hasCompatibleOnly && !hasCategories) {
    window.location.href = publicUrl.toString();
    return;
  }
  const currentPage = new URL(location.href).searchParams.get("page") || "1";
  const nextPage = pendingCompatibleCollectionUrl.searchParams.get("page") || "1";
  if ((hasCompatibleOnly || hasTagFilter) && nextPage !== currentPage) {
    window.location.href = publicUrl.toString();
    return;
  }
  syncFacetFormsFromUrl(pendingCompatibleCollectionUrl);
  syncProxyModeActiveFacets(pendingCompatibleCollectionUrl);
  await applyCompatibleOnlyFallback(pendingCompatibleCollectionUrl);
}, 0));
var openElements = /*  */ new Set();
var FacetForm = class extends HTMLFormElement {
  constructor() {
    super();
    this._isDirty = false;
    this.addEventListener("change", this._onFormChanged);
    this.addEventListener("submit", this._onFormSubmitted);
  }
  connectedCallback() {
    syncCompatibleFromUrl(this);
    syncBrakePositionFromUrl(this);
    toggleBrakePositionFacetVisibility();
    const compatibleCheckbox = this.querySelector("[data-compatible-my-car]");
    if (compatibleCheckbox) {
      compatibleCheckbox.addEventListener("change", (event) => {
        // Keep URL-based update so sparse mobile forms do not wipe other active filters.
        event.stopPropagation();
        const checked = compatibleCheckbox.checked;
        setCompatibleFormValues(this, checked);
        document.querySelectorAll('form[is="facet-form"] [data-compatible-my-car]').forEach((otherCheckbox) => {
          if (otherCheckbox === compatibleCheckbox) return;
          otherCheckbox.checked = checked;
          const otherForm = otherCheckbox.closest("form");
          if (otherForm) setCompatibleFormValues(otherForm, checked);
        });
        const nextUrl = new URL(location.href);
        if (checked) nextUrl.searchParams.set(COMPATIBLE_ONLY_KEY, "1");
        else nextUrl.searchParams.delete(COMPATIBLE_ONLY_KEY);
        nextUrl.searchParams.delete("filter.v.tag");
        nextUrl.searchParams.delete("page");
        if (!checked) {
          nextUrl.searchParams.delete(BRAKE_POSITION_KEY);
          clearBrakePositionCheckboxes();
        }
        writeCategoriesToSearchParams(nextUrl.searchParams, readCategoriesFromUrl(location.href));
        const sectionId = this.getAttribute("section-id");
        if (sectionId) nextUrl.searchParams.set("section_id", sectionId);
        toggleBrakePositionFacetVisibility();
        window.history.replaceState({ path: nextUrl.toString() }, "", nextUrl.toString());
        syncProxyModeActiveFacets(nextUrl);
        this._isDirty = false;
        this.dispatchEvent(new CustomEvent("facet:update", {
          bubbles: true,
          detail: { url: nextUrl }
        }));
      });
    }
    this.querySelectorAll("[data-brake-position-front], [data-brake-position-rear]").forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        syncBrakePositionAcrossForms(this);
        const nextUrl = applyBrakePositionToUrl(location.href, this);
        nextUrl.searchParams.delete("page");
        window.history.replaceState({ path: nextUrl.toString() }, "", nextUrl.toString());
        syncProxyModeActiveFacets(nextUrl);
        this._isDirty = true;
        if (HTMLFormElement.prototype.requestSubmit) {
          this.requestSubmit();
        } else {
          this.dispatchEvent(new Event("submit", { cancelable: true }));
        }
      });
    });
    Array.from(this.querySelectorAll("details, facet-dialog")).forEach((disclosureElement) => {
      if (disclosureElement.open) {
        openElements.add(disclosureElement.id);
      }
      disclosureElement.addEventListener("toggle", () => {
        if (disclosureElement.open) {
          openElements.add(disclosureElement.id);
        } else {
          openElements.delete(disclosureElement.id);
        }
      });
    });
  }
  _buildUrl() {
    const compatibleCheckbox = this.querySelector("[data-compatible-my-car]");
    if (compatibleCheckbox) setCompatibleFormValues(this, compatibleCheckbox.checked);
    const searchParams = new URLSearchParams(new FormData(this)), url = new URL(this.action);
    url.search = "";
    searchParams.forEach((value, key) => url.searchParams.append(key, value));
    ["page", "filter.v.price.gte", "filter.v.price.lte", "filter.v.tag", "compatible_only", BRAKE_POSITION_KEY, CATEGORIES_KEY].forEach((optionToClear) => {
      if (url.searchParams.get(optionToClear) === "") {
        url.searchParams.delete(optionToClear);
      }
    });
    // Canonicalize categories as repeated params from the checked boxes.
    const selectedCategories = Array.from(this.querySelectorAll('input[type="checkbox"][name="' + CATEGORIES_KEY + '"]:checked')).map((input) => String(input.value || "").trim()).filter(Boolean);
    writeCategoriesToSearchParams(url.searchParams, selectedCategories);
    url.searchParams.delete(BRAKE_POSITION_KEY);
    if (compatibleCheckbox?.checked) {
      const nextUrl = applyBrakePositionToUrl(url, this);
      const position = nextUrl.searchParams.get(BRAKE_POSITION_KEY);
      if (position) url.searchParams.set(BRAKE_POSITION_KEY, position);
    }
    url.searchParams.set("section_id", this.getAttribute("section-id"));
    return url;
  }
  _onFormChanged(event) {
    this._isDirty = true;
    if (this.hasAttribute("update-on-change")) {
      if (HTMLFormElement.prototype.requestSubmit) {
        this.requestSubmit();
      } else {
        this.dispatchEvent(new Event("submit", { cancelable: true }));
      }
    } else {
      cachedFetch(this._buildUrl().toString());
    }
  }
  _onFormSubmitted(event) {
    event.preventDefault();
    if (!this._isDirty) {
      return;
    }
    const compatibleCheckbox = this.querySelector("[data-compatible-my-car]");
    if (compatibleCheckbox) setCompatibleFormValues(this, compatibleCheckbox.checked);
    this.dispatchEvent(new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url: this._buildUrl()
      }
    }));
    this._isDirty = false;
  }
};
if (!window.customElements.get("facet-form")) {
  window.customElements.define("facet-form", FacetForm, { extends: "form" });
}

// js/common/facets/facet-link.js
var FacetLink = class extends HTMLAnchorElement {
  constructor() {
    super();
    this.addEventListener("click", this._onFacetUpdate);
  }
  _onFacetUpdate(event) {
    event.preventDefault();
    const sectionId = event.target.closest(".shopify-section").id.replace("shopify-section-", ""), url = new URL(this.href);
    const current = new URL(location.href);
    const tagVal = current.searchParams.get("filter.v.tag");
    const compatibleOnly = current.searchParams.get("compatible_only");
    const position = current.searchParams.get(BRAKE_POSITION_KEY);
    const categories = readCategoriesFromUrl(current);
    if (tagVal) url.searchParams.set("filter.v.tag", tagVal);
    else url.searchParams.delete("filter.v.tag");
    if (compatibleOnly) url.searchParams.set("compatible_only", compatibleOnly);
    else url.searchParams.delete("compatible_only");
    if (position) url.searchParams.set(BRAKE_POSITION_KEY, position);
    else url.searchParams.delete(BRAKE_POSITION_KEY);
    // Preserve categories unless the destination link already encodes its own category state.
    if (!url.searchParams.has(CATEGORIES_KEY) && categories.length > 0) {
      writeCategoriesToSearchParams(url.searchParams, categories);
    }
    url.searchParams.set("section_id", sectionId);
    this.dispatchEvent(new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url
      }
    }));
  }
};
if (!window.customElements.get("facet-link")) {
  window.customElements.define("facet-link", FacetLink, { extends: "a" });
}

// js/common/facets/facet-sort-by.js
var FacetSortBy = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("listbox:change", this._onValueChanged);
  }
  _onValueChanged(event) {
    const url = new URL(location.href), sectionId = event.target.closest(".shopify-section").id.replace("shopify-section-", "");
    url.searchParams.set("sort_by", event.detail.value);
    url.searchParams.set("section_id", sectionId);
    url.searchParams.delete("page");
    this.dispatchEvent(new CustomEvent("facet:update", {
      bubbles: true,
      detail: {
        url
      }
    }));
  }
};
if (!window.customElements.get("facet-sort-by")) {
  window.customElements.define("facet-sort-by", FacetSortBy);
}

// js/common/feedback/pill-loader.js
import { animate as animate6, timeline as timeline4, stagger as stagger4 } from "vendor";
var PillLoader = class extends HTMLElement {
  static get observedAttributes() {
    return ["aria-busy"];
  }
  connectedCallback() {
    this.innerHTML = `
      <div class="loader-dots">
        <span></span>
        <span></span>
        <span></span>
      </div>
      
      <svg class="loader-checkmark" fill="none" width="9" height="8" viewBox="0 0 9 8">
        <path d="M1 3.5 3.3 6 8 1" stroke="currentColor" stroke-width="2"/>
      </svg>
    `;
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (newValue === "true") {
      timeline4([
        [this, { opacity: [0, 1], visibility: "visible", transform: ["translateY(5px)", "translateY(0)"] }, { duration: 0.15 }],
        [this.firstElementChild, { opacity: 1, transform: ["translateY(0)"] }, { duration: 0.15, at: "<" }],
        [this.lastElementChild, { opacity: 0 }, { duration: 0, at: "<" }]
      ]);
      animate6(this.firstElementChild.querySelectorAll("span"), { opacity: [1, 0.1] }, { duration: 0.35, delay: stagger4(0.35 / 3), direction: "alternate", repeat: Infinity });
    } else {
      timeline4([
        [this.firstElementChild, { opacity: 0, transform: ["translateY(0)", "translateY(-2px)"] }, { duration: 0.15 }],
        [this.lastElementChild, { opacity: 1, transform: ["translateY(2px)", "translateY(0)"] }, { duration: 0.15 }],
        [this, { opacity: 0, transform: ["translateY(0)", "translateY(-5px)"], visibility: "hidden" }, { duration: 0.15, at: "+0.8" }]
      ]);
    }
  }
};
if (!window.customElements.get("pill-loader")) {
  window.customElements.define("pill-loader", PillLoader);
}

// js/common/feedback/progress-bar.js
var ProgressBar = class extends HTMLElement {
  static get observedAttributes() {
    return ["aria-valuenow", "aria-valuemax"];
  }
  set valueMax(value) {
    this.setAttribute("aria-valuemax", value);
  }
  set valueNow(value) {
    this.setAttribute("aria-valuenow", value);
  }
  attributeChangedCallback() {
    this.style.setProperty("--progress", `${Math.min(1, this.getAttribute("aria-valuenow") / this.getAttribute("aria-valuemax"))}`);
  }
};
if (!window.customElements.get("progress-bar")) {
  window.customElements.define("progress-bar", ProgressBar);
}

// js/common/form/price-range.js
var PriceRange = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.rangeLowerBound = this.querySelector('input[type="range"]:first-child');
    this.rangeHigherBound = this.querySelector('input[type="range"]:last-child');
    this.textInputLowerBound = this.querySelector('input[name="filter.v.price.gte"]');
    this.textInputHigherBound = this.querySelector('input[name="filter.v.price.lte"]');
    this.textInputLowerBound.addEventListener("focus", () => this.textInputLowerBound.select(), { signal: this._abortController.signal });
    this.textInputHigherBound.addEventListener("focus", () => this.textInputHigherBound.select(), { signal: this._abortController.signal });
    this.textInputLowerBound.addEventListener("change", (event) => {
      event.preventDefault();
      event.target.value = Math.max(Math.min(parseInt(event.target.value), parseInt(this.textInputHigherBound.value || event.target.max) - 1), event.target.min);
      this.rangeLowerBound.value = event.target.value;
      this.rangeLowerBound.parentElement.style.setProperty("--range-min", `${parseInt(this.rangeLowerBound.value) / parseInt(this.rangeLowerBound.max) * 100}%`);
    }, { signal: this._abortController.signal });
    this.textInputHigherBound.addEventListener("change", (event) => {
      event.preventDefault();
      event.target.value = Math.min(Math.max(parseInt(event.target.value), parseInt(this.textInputLowerBound.value || event.target.min) + 1), event.target.max);
      this.rangeHigherBound.value = event.target.value;
      this.rangeHigherBound.parentElement.style.setProperty("--range-max", `${parseInt(this.rangeHigherBound.value) / parseInt(this.rangeHigherBound.max) * 100}%`);
    }, { signal: this._abortController.signal });
    this.rangeLowerBound.addEventListener("change", (event) => {
      event.stopPropagation();
      this.textInputLowerBound.value = event.target.value;
      this.textInputLowerBound.dispatchEvent(new Event("change", { bubbles: true }));
    }, { signal: this._abortController.signal });
    this.rangeHigherBound.addEventListener("change", (event) => {
      event.stopPropagation();
      this.textInputHigherBound.value = event.target.value;
      this.textInputHigherBound.dispatchEvent(new Event("change", { bubbles: true }));
    }, { signal: this._abortController.signal });
    this.rangeLowerBound.addEventListener("input", (event) => {
      event.target.value = Math.min(parseInt(event.target.value), parseInt(this.textInputHigherBound.value || event.target.max) - 1);
      event.target.parentElement.style.setProperty("--range-min", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
      this.textInputLowerBound.value = event.target.value;
    }, { signal: this._abortController.signal });
    this.rangeHigherBound.addEventListener("input", (event) => {
      event.target.value = Math.max(parseInt(event.target.value), parseInt(this.textInputLowerBound.value || event.target.min) + 1);
      event.target.parentElement.style.setProperty("--range-max", `${parseInt(event.target.value) / parseInt(event.target.max) * 100}%`);
      this.textInputHigherBound.value = event.target.value;
    }, { signal: this._abortController.signal });
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
};
if (!window.customElements.get("price-range")) {
  window.customElements.define("price-range", PriceRange);
}

// js/common/form/quantity-selector.js
var QuantitySelector = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    this.inputElement = this.querySelector("input");
    this.querySelector("button:first-of-type").addEventListener("click", this.stepDown.bind(this), { signal: this._abortController.signal });
    this.querySelector("button:last-of-type").addEventListener("click", this.stepUp.bind(this), { signal: this._abortController.signal });
  }
  stepDown() {
    this.inputElement.stepDown();
    this.inputElement.dispatchEvent(new Event("change", { bubbles: true }));
  }
  stepUp() {
    this.inputElement.stepUp();
    this.inputElement.dispatchEvent(new Event("change", { bubbles: true }));
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
};
var QuantityInput = class extends HTMLInputElement {
  constructor() {
    super();
    this.addEventListener("input", this._onValueInput);
    this.addEventListener("change", this._onValueChanged);
    this.addEventListener("focus", this.select);
  }
  disconnectedCallback() {
    this._abortController?.abort();
  }
  get quantity() {
    return parseInt(this.value);
  }
  set quantity(quantity) {
    const isNumeric = (typeof quantity === "number" || typeof quantity === "string" && quantity.trim() !== "") && !isNaN(quantity);
    if (quantity === "") {
      return;
    }
    if (!isNumeric || quantity < 0) {
      quantity = parseInt(quantity) || 1;
    }
    this.value = Math.max(this.min || 1, Math.min(quantity, this.max || Number.MAX_VALUE)).toString();
    if (!this.checkValidity()) {
      this.stepDown();
    }
  }
  _onValueInput() {
    this.quantity = this.value;
    this.style.setProperty("--quantity-input-characters-count", `${this.value.toString().length}ch`);
  }
  _onValueChanged() {
    if (this.value === "") {
      this.quantity = this.min || 1;
    }
    if (!this.checkValidity()) {
      this.stepDown();
    }
    this.style.setProperty("--quantity-input-characters-count", `${this.value.toString().length}ch`);
  }
};
if (!window.customElements.get("quantity-selector")) {
  window.customElements.define("quantity-selector", QuantitySelector);
}
if (!window.customElements.get("quantity-input")) {
  window.customElements.define("quantity-input", QuantityInput, { extends: "input" });
}

// js/common/form/resizable-textarea.js
var ResizableTextarea = class extends HTMLTextAreaElement {
  constructor() {
    super();
    this.addEventListener("input", this._onInput);
  }
  _onInput() {
    this.style.height = "auto";
    this.style.height = this.scrollHeight + 2 + "px";
  }
};
if (!window.customElements.get("resizable-textarea")) {
  window.customElements.define("resizable-textarea", ResizableTextarea, { extends: "textarea" });
}

// js/common/list/listbox.js
var _accessibilityInitialized, _hiddenInput, _Listbox_instances, onOptionClicked_fn, onInputChanged_fn, onKeyDown_fn;
var Listbox = class extends HTMLElement {
  constructor() {
    super();
    __privateAdd(this, _Listbox_instances);
    __privateAdd(this, _accessibilityInitialized, false);
    __privateAdd(this, _hiddenInput);
    this.addEventListener("keydown", __privateMethod(this, _Listbox_instances, onKeyDown_fn));
  }
  static get observedAttributes() {
    return ["aria-activedescendant"];
  }
  connectedCallback() {
    if (!__privateGet(this, _accessibilityInitialized)) {
      this.setAttribute("role", "listbox");
      __privateSet(this, _hiddenInput, this.querySelector('input[type="hidden"]'));
      __privateGet(this, _hiddenInput)?.addEventListener("change", __privateMethod(this, _Listbox_instances, onInputChanged_fn).bind(this));
      Array.from(this.querySelectorAll('[role="option"]')).forEach((option) => {
        option.addEventListener("click", __privateMethod(this, _Listbox_instances, onOptionClicked_fn).bind(this));
        option.id = "option-" + (crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4));
        if (option.getAttribute("aria-selected") === "true") {
          this.setAttribute("aria-activedescendant", option.id);
        }
      });
      __privateSet(this, _accessibilityInitialized, true);
    }
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "aria-activedescendant" && oldValue !== null && newValue !== oldValue) {
      Array.from(this.querySelectorAll('[role="option"]')).forEach((option) => {
        if (option.id === newValue) {
          option.setAttribute("aria-selected", "true");
          if (__privateGet(this, _hiddenInput) && __privateGet(this, _hiddenInput).value !== option.value) {
            __privateGet(this, _hiddenInput).value = option.value;
            __privateGet(this, _hiddenInput).dispatchEvent(new Event("change", { bubbles: true }));
          }
          if (this.hasAttribute("aria-owns")) {
            this.getAttribute("aria-owns").split(" ").forEach((boundId) => {
              document.getElementById(boundId).textContent = option.getAttribute("title") || option.innerText || option.value;
            });
          }
          option.dispatchEvent(new CustomEvent("listbox:change", {
            bubbles: true,
            detail: {
              value: option.value
            }
          }));
        } else {
          option.setAttribute("aria-selected", "false");
        }
      });
    }
  }
};
_accessibilityInitialized = new WeakMap();
_hiddenInput = new WeakMap();
_Listbox_instances = new WeakSet();
onOptionClicked_fn = function(event) {
  this.setAttribute("aria-activedescendant", event.currentTarget.id);
  event.currentTarget.dispatchEvent(new CustomEvent("listbox:select", {
    bubbles: true,
    detail: {
      value: event.currentTarget.value
    }
  }));
};
onInputChanged_fn = function(event) {
  this.setAttribute("aria-activedescendant", this.querySelector(`[role="option"][value="${CSS.escape(event.target.value)}"]`).id);
};
onKeyDown_fn = function(event) {
  if (event.key === "ArrowUp") {
    event.target.previousElementSibling?.focus();
    event.preventDefault();
  } else if (event.key === "ArrowDown") {
    event.target.nextElementSibling?.focus();
    event.preventDefault();
  }
};
if (!window.customElements.get("x-listbox")) {
  window.customElements.define("x-listbox", Listbox);
}

// js/common/media/image.js
function imageLoaded(imageOrArray) {
  if (!imageOrArray) {
    return Promise.resolve();
  }
  imageOrArray = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);
  return Promise.all(imageOrArray.map((image) => {
    return new Promise((resolve) => {
      if (image.tagName === "IMG" && image.complete || !image.offsetParent) {
        resolve();
      } else {
        image.onload = () => resolve();
      }
    });
  }));
}
function generateSrcset(imageObjectOrString, widths = []) {
  let imageUrl, maxWidth;
  if (typeof imageObjectOrString === "string") {
    imageUrl = new URL(imageObjectOrString.startsWith("//") ? `https:${imageObjectOrString}` : imageObjectOrString);
    maxWidth = parseInt(imageUrl.searchParams.get("width"));
  } else {
    imageUrl = new URL(imageObjectOrString["src"].startsWith("//") ? `https:${imageObjectOrString["src"]}` : imageObjectOrString["src"]);
    maxWidth = imageObjectOrString["width"];
  }
  return widths.filter((width) => width <= maxWidth).map((width) => {
    imageUrl.searchParams.set("width", width.toString());
    return `${imageUrl.href} ${width}w`;
  }).join(", ");
}
function createMediaImg(media, widths = [], properties = {}) {
  const image = new Image(media["preview_image"]["width"], media["preview_image"]["height"]), src = media["preview_image"]["src"], featuredMediaUrl = new URL(src.startsWith("//") ? `https:${src}` : src);
  for (const propertyKey in properties) {
    image.setAttribute(propertyKey, properties[propertyKey]);
  }
  image.alt = media["alt"];
  image.src = featuredMediaUrl.href;
  image.srcset = generateSrcset(media["preview_image"], widths);
  return image;
}

// js/common/product/gift-card-recipient.js
var _recipientCheckbox, _recipientOtherProperties, _recipientSendOnProperty, _offsetProperty, _recipientFieldsContainer, _GiftCardRecipient_instances, synchronizeProperties_fn, formatDate_fn;
var GiftCardRecipient = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _GiftCardRecipient_instances);
    __privateAdd(this, _recipientCheckbox);
    __privateAdd(this, _recipientOtherProperties, []);
    __privateAdd(this, _recipientSendOnProperty);
    __privateAdd(this, _offsetProperty);
    __privateAdd(this, _recipientFieldsContainer);
  }
  connectedCallback() {
    const properties = Array.from(this.querySelectorAll('[name*="properties"]')), checkboxPropertyName = "properties[__shopify_send_gift_card_to_recipient]";
    __privateSet(this, _recipientCheckbox, properties.find((input) => input.name === checkboxPropertyName));
    __privateSet(this, _recipientOtherProperties, properties.filter((input) => input.name !== checkboxPropertyName));
    __privateSet(this, _recipientFieldsContainer, this.querySelector(".gift-card-recipient__fields"));
    __privateSet(this, _offsetProperty, this.querySelector('[name="properties[__shopify_offset]"]'));
    if (__privateGet(this, _offsetProperty)) {
      __privateGet(this, _offsetProperty).value = (/* @__PURE__ */ new Date()).getTimezoneOffset().toString();
    }
    __privateSet(this, _recipientSendOnProperty, this.querySelector('[name="properties[Send on]"]'));
    const minDate = /* @__PURE__ */ new Date();
    const maxDate = /* @__PURE__ */ new Date();
    maxDate.setDate(minDate.getDate() + 90);
    __privateGet(this, _recipientSendOnProperty)?.setAttribute("min", __privateMethod(this, _GiftCardRecipient_instances, formatDate_fn).call(this, minDate));
    __privateGet(this, _recipientSendOnProperty)?.setAttribute("max", __privateMethod(this, _GiftCardRecipient_instances, formatDate_fn).call(this, maxDate));
    __privateGet(this, _recipientCheckbox)?.addEventListener("change", __privateMethod(this, _GiftCardRecipient_instances, synchronizeProperties_fn).bind(this));
    __privateMethod(this, _GiftCardRecipient_instances, synchronizeProperties_fn).call(this);
  }
};
_recipientCheckbox = new WeakMap();
_recipientOtherProperties = new WeakMap();
_recipientSendOnProperty = new WeakMap();
_offsetProperty = new WeakMap();
_recipientFieldsContainer = new WeakMap();
_GiftCardRecipient_instances = new WeakSet();
synchronizeProperties_fn = function() {
  __privateGet(this, _recipientOtherProperties).forEach((property) => property.disabled = !__privateGet(this, _recipientCheckbox).checked);
  __privateGet(this, _recipientFieldsContainer).toggleAttribute("hidden", !__privateGet(this, _recipientCheckbox).checked);
};
formatDate_fn = function(date) {
  const offset = date.getTimezoneOffset();
  const offsetDate = new Date(date.getTime() - offset * 60 * 1e3);
  return offsetDate.toISOString().split("T")[0];
};
if (!window.customElements.get("gift-card-recipient")) {
  window.customElements.define("gift-card-recipient", GiftCardRecipient);
}

// js/common/product/product-card.js
import { Delegate as Delegate3 } from "vendor";
var ProductCard = class extends HTMLElement {
  constructor() {
    super();
    this._delegate = new Delegate3(this);
  }
  connectedCallback() {
    this._delegate.on("change", '.product-card__swatch-list [type="radio"], .product-card__variant-list [type="radio"]', this._onSwatchChanged.bind(this));
    this._delegate.on("pointerover", '.product-card__swatch-list [type="radio"] + label, .product-card__variant-list [type="radio"] + label', this._onSwatchHovered.bind(this), true);
  }
  disconnectedCallback() {
    this._delegate.off();
  }
  async _onSwatchHovered(event, target) {
    const control = target.control;
    const primaryMediaElement = this.querySelector(".product-card__image--primary");
    if (control.hasAttribute("data-variant-media")) {
      this._createImageElement(JSON.parse(control.getAttribute("data-variant-media")), primaryMediaElement.className, primaryMediaElement.sizes);
    }
  }
  async _onSwatchChanged(event, target) {
    if (target.hasAttribute("data-variant-id")) {
      this.querySelectorAll(`a[href^="${Shopify.routes.root}products/${this.getAttribute("handle")}"`).forEach((link) => {
        const url = new URL(link.href);
        url.searchParams.set("variant", target.getAttribute("data-variant-id"));
        link.href = `${url.pathname}${url.search}${url.hash}`;
      });
    }
    if (!target.hasAttribute("data-variant-media")) {
      return;
    }
    const newMedia = JSON.parse(target.getAttribute("data-variant-media")), primaryMediaElement = this.querySelector(".product-card__image--primary"), newPrimaryMediaElement = this._createImageElement(newMedia, primaryMediaElement.className, primaryMediaElement.sizes);
    if (primaryMediaElement.src !== newPrimaryMediaElement.src) {
      await primaryMediaElement.animate({ opacity: [1, 0] }, { duration: 150, easing: "ease-in", fill: "forwards" }).finished;
      await new Promise((resolve) => newPrimaryMediaElement.complete ? resolve() : newPrimaryMediaElement.onload = () => resolve());
      primaryMediaElement.replaceWith(newPrimaryMediaElement);
      newPrimaryMediaElement.animate({ opacity: [0, 1] }, { duration: 150, easing: "ease-in" });
    }
  }
  _createImageElement(media, classes, sizes) {
    return createMediaImg(media, [200, 300, 400, 500, 600, 700, 800, 1e3, 1200, 1400, 1600, 1800], { class: classes, sizes });
  }
};
if (!window.customElements.get("product-card")) {
  window.customElements.define("product-card", ProductCard);
}

// js/common/product/product-form.js
var ProductForm = class extends HTMLFormElement {
  #submitting = false;
  constructor() {
    super();
    this.addEventListener("submit", this._onSubmit);
  }
  connectedCallback() {
    this.id.disabled = false;
  }
  async _onSubmit(event) {
    event.preventDefault();
    if (this.#submitting) {
      return;
    }
    if (!this.checkValidity()) {
      this.reportValidity();
      return;
    }
    // V-Sport: OOS confirmation gate — must confirm before loader starts
    if (!this._vsportOosConfirmed) {
      const section = this.closest('.shopify-section, [id^="shopify-section"]');
      const badge = section && section.querySelector('.vsport-backorder-badge[data-oos-confirm="true"]');
      if (badge) {
        window.__vsportShowOosModal(this);
        // custom-button sets aria-busy="true" on submit immediately.
        // When we gate the submit (Cancel path), we must reset it, otherwise the loader stays active.
        this.querySelectorAll('[type="submit"][aria-busy="true"]').forEach((el) => {
          el.removeAttribute('aria-busy');
        });
        return;
      }
    }
    this._vsportOosConfirmed = false;
    const submitButtons = Array.from(this.elements).filter((button) => button.type === "submit");
    submitButtons.forEach((submitButton) => {
      submitButton.setAttribute("aria-busy", "true");
    });
    let sectionsToBundle = ["variant-added"];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } }));
    const formData = new FormData(this);
    formData.set("sections", sectionsToBundle.join(","));
    formData.set("sections_url", `${Shopify.routes.root}variants/${this.id.value}`);
    this.#submitting = true;
    const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
      body: formData,
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest"
        // Needed for Shopify to check inventory
      }
    });
    submitButtons.forEach((submitButton) => {
      submitButton.removeAttribute("aria-busy");
    });
    this.#submitting = false;
    const responseJson = await response.json();
    if (response.ok) {
      if (window.themeVariables.settings.cartType === "page" || window.themeVariables.settings.pageType === "cart") {
        return window.location.href = `${Shopify.routes.root}cart`;
      }
      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent["sections"] = responseJson["sections"];
      this.dispatchEvent(new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          items: responseJson.hasOwnProperty("items") ? responseJson["items"] : [responseJson],
          cart: cartContent
        }
      }));
      document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
          cart: cartContent
        }
      }));
    } else {
      this.dispatchEvent(new CustomEvent("cart:error", {
        bubbles: true,
        detail: {
          error: responseJson["description"]
        }
      }));
      document.dispatchEvent(new CustomEvent("cart:refresh"));
    }
  }
};
if (!window.customElements.get("product-form")) {
  window.customElements.define("product-form", ProductForm, { extends: "form" });
}

// js/common/product/product-form-listeners.js
var BuyButtons = class extends HTMLElement {
  constructor() {
    super();
    this._onCartErrorListener = this._onCartError.bind(this);
  }
  connectedCallback() {
    this._productForm = document.forms[this.getAttribute("form")];
    this._productForm?.addEventListener("cart:error", this._onCartErrorListener);
  }
  disconnectedCallback() {
    this._productForm?.removeEventListener("cart:error", this._onCartErrorListener);
  }
  _onCartError(event) {
    const errorBanner = document.createElement("div");
    errorBanner.classList.add("banner", "banner--error", "justify-center");
    errorBanner.setAttribute("role", "alert");
    errorBanner.style.gridColumn = "1/-1";
    errorBanner.style.marginBottom = "1rem";
    errorBanner.innerHTML = `
      <svg role="presentation" focusable="false" width="18" height="18" class="offset-icon icon icon-error" style="--icon-height: 18px" viewBox="0 0 18 18">
        <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="currentColor"></path>
        <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
      </svg>
      
      <p>${event.detail.error}</p>
    `;
    this.before(errorBanner);
    setTimeout(async () => {
      await errorBanner.animate({ opacity: [1, 0] }, { duration: 250, fill: "forwards" }).finished;
      errorBanner.remove();
    }, 5e3);
  }
};
if (!window.customElements.get("buy-buttons")) {
  window.customElements.define("buy-buttons", BuyButtons);
}

// js/common/product/product-gallery.js
import { PhotoSwipeLightbox } from "vendor";
var ProductGallery = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    const form = document.forms[this.getAttribute("form")];
    form?.addEventListener("product:rerender", this._onSectionRerender.bind(this), { signal: this._abortController.signal });
    form?.addEventListener("variant:change", this._onVariantChanged.bind(this), { signal: this._abortController.signal });
    this._carousels = Array.from(this.querySelectorAll("media-carousel"));
    this._pageDots = Array.from(this.querySelectorAll("page-dots"));
    this._viewInSpaceButton = this.querySelector("[data-shopify-xr]");
    this._customCursor = this.querySelector(".product-gallery__cursor");
    this.addEventListener("carousel:change", this._onCarouselChanged);
    if (this._viewInSpaceButton) {
      this.addEventListener("carousel:settle", this._updateViewInSpaceButton);
    }
    if (this.hasAttribute("allow-zoom")) {
      this.addEventListener("lightbox:open", (event) => this.openZoom(event.detail.index));
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get filteredIndexes() {
    return JSON.parse(this.getAttribute("filtered-indexes")).map((index) => parseInt(index) - 1);
  }
  get photoswipe() {
    if (this._photoswipe) {
      return this._photoswipe;
    }
    const photoswipe = new PhotoSwipeLightbox({
      pswpModule: () => import("photoswipe"),
      bgOpacity: 1,
      maxZoomLevel: parseInt(this.getAttribute("allow-zoom")) || 3,
      closeTitle: window.themeVariables.strings.closeGallery,
      zoomTitle: window.themeVariables.strings.zoomGallery,
      errorMsg: window.themeVariables.strings.errorGallery,
      // UX
      arrowPrev: false,
      arrowNext: false,
      counter: false,
      zoom: false,
      closeSVG: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 56 56">
        <path d="M56 28C56 12.536 43.464 0 28 0S0 12.536 0 28s12.536 28 28 28 28-12.536 28-28Z" fill="#fff"/>
        <path d="M55.5 28C55.5 12.812 43.188.5 28 .5S.5 12.812.5 28 12.812 55.5 28 55.5 55.5 43.188 55.5 28Z" stroke="#252627" stroke-opacity=".12"/>
        <path d="m22.344 22.343 11.313 11.314m-11.313 0 11.313-11.313" stroke="#252627" stroke-width="2"/>
      </svg>`
    });
    photoswipe.addFilter("thumbEl", (thumbEl, data) => data["thumbnailElement"]);
    photoswipe.on("uiRegister", () => {
      photoswipe.pswp.ui.registerElement({
        name: "bottom-bar",
        order: 5,
        appendTo: "wrapper",
        html: `
          <div class="pagination">
            <button class="pagination__item group" rel="prev">
              <span class="animated-arrow animated-arrow--reverse"></span>
            </button>
            
            <span class="pagination__current text-sm">
              <span class="pagination__current-page">1</span> / <span class="pagination__page-count"></span>
            </span>
            
            <button class="pagination__item group" rel="next">
              <span class="animated-arrow"></span>
            </button>
          </div>
        `,
        onInit: (el, pswp) => {
          el.querySelector(".pagination__page-count").innerText = pswp.getNumItems();
          el.querySelector('[rel="prev"]')?.addEventListener("click", () => pswp.prev());
          el.querySelector('[rel="next"]')?.addEventListener("click", () => pswp.next());
          pswp.on("change", () => {
            el.querySelector(".pagination__current-page").innerText = pswp.currIndex + 1;
          });
        }
      });
    });
    photoswipe.init();
    return this._photoswipe = photoswipe;
  }
  /**
   * Open the zoom by dynamically creating a data source based on the filtered items
   */
  openZoom(index = 0) {
    const dataSource = Array.from(this.querySelectorAll('.product-gallery__media[data-media-type="image"]:not([hidden]) > img')).map((image) => {
      return {
        thumbnailElement: image,
        src: image.src,
        srcset: image.srcset,
        msrc: image.currentSrc || image.src,
        width: parseInt(image.getAttribute("width")),
        height: parseInt(image.getAttribute("height")),
        alt: image.alt,
        thumbCropped: true
      };
    });
    this.photoswipe.loadAndOpen(index, dataSource);
  }
  _updateViewInSpaceButton(event) {
    if (event.detail.slide.getAttribute("data-media-type") === "model") {
      this._viewInSpaceButton.setAttribute("data-shopify-model3d-id", event.detail.slide.getAttribute("data-media-id"));
    } else {
      this._viewInSpaceButton.setAttribute("data-shopify-model3d-id", this._viewInSpaceButton.getAttribute("data-shopify-model3d-default-id"));
    }
  }
  _onCarouselChanged(event) {
    if (this._customCursor) {
      this._customCursor.toggleAttribute("hidden", event.detail.slide.getAttribute("data-media-type") !== "image");
    }
  }
  _onVariantChanged(event) {
    if (!event.detail.variant) {
      return;
    }
    let newMediaPosition;
    if (event.detail.previousVariant === null) {
      newMediaPosition = event.detail.variant["featured_media"]["position"] - 1;
    } else if (event.detail.variant["featured_media"] && event.detail.previousVariant["featured_media"] && event.detail.previousVariant["featured_media"]["id"] !== event.detail.variant["featured_media"]["id"] || !event.detail.previousVariant["featured_media"] && event.detail.variant["featured_media"]) {
      newMediaPosition = event.detail.variant["featured_media"]["position"] - 1;
    }
    if (newMediaPosition !== void 0) {
      this._carousels.forEach((carousel) => carousel.select(newMediaPosition, { animate: false }));
    }
  }
  _onSectionRerender(event) {
    const galleryMarkup = deepQuerySelector(event.detail.htmlFragment, `${this.tagName}[form="${this.getAttribute("form")}"]`);
    if (!galleryMarkup) {
      return;
    }
    if (galleryMarkup.filteredIndex !== this.filteredIndexes) {
      this.setAttribute("filtered-indexes", galleryMarkup.getAttribute("filtered-indexes"));
      const filteredIndexes = this.filteredIndexes;
      this._carousels.forEach((carousel) => carousel.dispatchEvent(new CustomEvent("carousel:filter", { detail: { filteredIndexes } })));
      this._pageDots.forEach((pageDots) => pageDots.dispatchEvent(new CustomEvent("control:filter", { detail: { filteredIndexes } })));
    }
  }
};
var MediaCarousel = class extends ScrollCarousel {
  connectedCallback() {
    super.connectedCallback();
    this._onGestureChangedListener = this._onGestureChanged.bind(this);
    this.addEventListener("gesturestart", this._onGestureStart, { capture: false, signal: this._abortController.signal });
    this.addEventListener("carousel:settle", this._onMediaSettled, { signal: this._abortController.signal });
    this.addEventListener("click", this._onGalleryClick);
  }
  _onMediaSettled(event) {
    const media = event.detail.slide;
    this.items.filter((item) => ["video", "external_video", "model"].includes(item.getAttribute("data-media-type"))).forEach((item) => item.firstElementChild.pause());
    switch (media.getAttribute("data-media-type")) {
      case "external_video":
      case "video":
        if (this.hasAttribute("autoplay")) {
          media.firstElementChild.play();
        }
        break;
      case "model":
        if (window.matchMedia("(min-width: 1000px)").matches) {
          media.firstElementChild.play();
        }
        break;
    }
  }
  _onGalleryClick(event) {
    if (event.target.matches("button, a[href], button :scope, a[href] :scope") || !window.matchMedia("screen and (pointer: fine)").matches) {
      return;
    }
    if (this.selectedSlide.getAttribute("data-media-type") !== "image") {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect(), offsetX = event.clientX - rect.left;
    offsetX > this.clientWidth / 2 ? this.next() : this.previous();
  }
  _onGestureStart(event) {
    event.preventDefault();
    this.addEventListener("gesturechange", this._onGestureChangedListener, { capture: false, signal: this._abortController.signal });
  }
  _onGestureChanged(event) {
    event.preventDefault();
    if (event.scale > 1.5) {
      const visibleImages = this.visibleItems.filter((item) => item.getAttribute("data-media-type") === "image"), openIndex = visibleImages.indexOf(this.selectedSlide);
      this.dispatchEvent(new CustomEvent("lightbox:open", { bubbles: true, detail: { index: openIndex } }));
      this.removeEventListener("gesturechange", this._onGestureChangedListener);
    }
  }
};
var ProductZoomButton = class extends HTMLButtonElement {
  constructor() {
    super();
    this.addEventListener("click", this._onButtonClicked);
  }
  _onButtonClicked() {
    let media = this.closest(".product-gallery__media"), openIndex;
    if (media) {
      const visibleImages = this.closest("media-carousel").visibleItems.filter((item) => item.getAttribute("data-media-type") === "image");
      openIndex = visibleImages.indexOf(media);
    } else {
      const carousel = this.closest(".product-gallery__media-list-wrapper").querySelector("media-carousel"), visibleImages = carousel.visibleItems.filter((item) => item.getAttribute("data-media-type") === "image");
      openIndex = visibleImages.indexOf(carousel.selectedSlide);
    }
    this.dispatchEvent(new CustomEvent("lightbox:open", {
      bubbles: true,
      detail: {
        index: openIndex
      }
    }));
  }
};
if (!window.customElements.get("product-gallery")) {
  window.customElements.define("product-gallery", ProductGallery);
}
if (!window.customElements.get("product-zoom-button")) {
  window.customElements.define("product-zoom-button", ProductZoomButton, { extends: "button" });
}
if (!window.customElements.get("media-carousel")) {
  window.customElements.define("media-carousel", MediaCarousel);
}

// js/common/product/product-loader.js
var ProductLoader = class {
  static loadedProducts = {};
  static load(productHandle) {
    if (!productHandle) {
      return;
    }
    if (this.loadedProducts[productHandle]) {
      return this.loadedProducts[productHandle];
    }
    this.loadedProducts[productHandle] = new Promise(async (resolve, reject) => {
      const response = await fetch(`${Shopify.routes.root}products/${productHandle}.js`);
      if (response.ok) {
        const responseAsJson = await response.json();
        resolve(responseAsJson);
      } else {
        reject(`
          Attempted to load information for product with handle ${productHandle}, but this product is in "draft" mode. You won't be able to
          switch between variants or access to per-variant information. To fully preview this product, change temporarily its status
          to "active".
        `);
      }
    });
    return this.loadedProducts[productHandle];
  }
};

// js/common/product/quick-add.js
var ProductQuickAdd = class extends HTMLElement {
  #scopeFromPassed = false;
  #scopeToReached = false;
  #intersectionObserver = new IntersectionObserver(this._onObserved.bind(this));
  connectedCallback() {
    this._scopeFrom = document.getElementById(this.getAttribute("form"));
    this._scopeTo = document.querySelector(".footer");
    if (!this._scopeFrom || !this._scopeTo) {
      return;
    }
    this.#intersectionObserver.observe(this._scopeFrom);
    this.#intersectionObserver.observe(this._scopeTo);
  }
  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
  }
  _onObserved(entries) {
    entries.forEach((entry) => {
      if (entry.target === this._scopeFrom) {
        this.#scopeFromPassed = entry.boundingClientRect.bottom < 0;
      }
      if (entry.target === this._scopeTo) {
        this.#scopeToReached = entry.isIntersecting;
      }
    });
    this.classList.toggle("is-visible", this.#scopeFromPassed && !this.#scopeToReached);
  }
};
if (!window.customElements.get("product-quick-add")) {
  window.customElements.define("product-quick-add", ProductQuickAdd);
}

// js/common/product/product-rerender.js
var _abortController2, _ProductRerender_instances, onRerender_fn;
var ProductRerender = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _ProductRerender_instances);
    __privateAdd(this, _abortController2);
  }
  connectedCallback() {
    __privateSet(this, _abortController2, new AbortController());
    if (!this.id || !this.hasAttribute("observe-form")) {
      console.warn('The <product-rerender> requires an ID to identify the element to re-render, and an "observe-form" attribute referencing to the form to monitor.');
    }
    document.forms[this.getAttribute("observe-form")].addEventListener("product:rerender", __privateMethod(this, _ProductRerender_instances, onRerender_fn).bind(this), { signal: __privateGet(this, _abortController2).signal });
  }
  disconnectedCallback() {
    __privateGet(this, _abortController2).abort();
  }
};
_abortController2 = new WeakMap();
_ProductRerender_instances = new WeakSet();
onRerender_fn = function(event) {
  const matchingElement = deepQuerySelector(event.detail.htmlFragment, `#${this.id}`);
  if (!matchingElement) {
    return;
  }
  const focusedElement = document.activeElement;
  if (!this.hasAttribute("allow-partial-rerender") || event.detail.productChange) {
    this.replaceWith(matchingElement);
  } else {
    const blockTypes = ["sku", "badges", "price", "payment-terms", "variant-picker", "quantity-selector", "volume-pricing", "inventory", "buy-buttons", "pickup-availability", "liquid"];
    blockTypes.forEach((blockType) => {
      this.querySelectorAll(`[data-block-type="${blockType}"]`).forEach((element) => {
        const matchingBlock = matchingElement.querySelector(`[data-block-type="${blockType}"][data-block-id="${element.getAttribute("data-block-id")}"]`);
        if (matchingBlock) {
          matchingBlock.querySelectorAll("noscript").forEach((noscript) => {
            noscript.remove();
          });
          if (blockType === "buy-buttons") {
            element.querySelector("buy-buttons").replaceWith(matchingBlock.querySelector("buy-buttons"));
          } else if (blockType === "quantity-selector") {
            const existingQuantity = element.querySelector(".quantity-selector__input")?.quantity;
            element.replaceWith(matchingBlock);
            const newSelector = matchingBlock.querySelector(".quantity-selector__input");
            if (newSelector) {
              newSelector.quantity = existingQuantity;
            }
          } else {
            element.replaceWith(matchingBlock);
          }
        }
      });
    });
  }
  if (focusedElement.id) {
    const element = document.getElementById(focusedElement.id);
    if (this.contains(element)) {
      element.focus();
    }
  }
};
if (!window.customElements.get("product-rerender")) {
  window.customElements.define("product-rerender", ProductRerender);
}

// js/common/product/quick-buy-drawer.js
import { animate as animate7, timeline as timeline5 } from "vendor";
var QuickBuyDrawer = class extends Drawer {
  constructor() {
    super();
    this._hasLoaded = false;
    this.addEventListener("variant:add", this._onVariantAdded.bind(this));
  }
  async show() {
    this.style.display = "block";
    if (!this._hasLoaded) {
      [this, ...this.controls].forEach((control) => control.setAttribute("aria-busy", "true"));
      const responseContent = await (await fetch(`${window.Shopify.routes.root}products/${this.getAttribute("handle")}`)).text();
      [this, ...this.controls].forEach((control) => control.setAttribute("aria-busy", "false"));
      const quickBuyContent = new DOMParser().parseFromString(responseContent, "text/html").getElementById("quick-buy-content").content;
      Array.from(quickBuyContent.querySelectorAll("noscript")).forEach((noScript) => noScript.remove());
      this.replaceChildren(quickBuyContent);
      Shopify.PaymentButton?.init();
      this._hasLoaded = true;
    }
    return super.show();
  }
  async hide() {
    return super.hide()?.then(() => {
      this.style.display = "none";
    });
  }
  _onVariantAdded(event) {
    event.detail.blockCartDrawerOpening = true;
    const contentShadow = this.shadowRoot.querySelector('[part="content"]'), fromHeight = contentShadow.clientHeight;
    animate7(contentShadow.children, { opacity: 0, visibility: "hidden" }, { duration: 0.15 });
    this.replaceChildren(...new DOMParser().parseFromString(event.detail.cart["sections"]["variant-added"], "text/html").querySelector(".shopify-section").children);
    requestAnimationFrame(async () => {
      await timeline5([
        [contentShadow, { height: [`${fromHeight}px`, `${contentShadow.clientHeight}px`] }, { duration: 0.35, easing: [0.86, 0, 0.07, 1] }],
        [contentShadow.children, { opacity: [0, 1], visibility: "visible" }, { duration: 0.15 }]
      ]).finished;
      contentShadow.style.height = null;
    });
    this._hasLoaded = false;
  }
};
if (!window.customElements.get("quick-buy-drawer")) {
  window.customElements.define("quick-buy-drawer", QuickBuyDrawer);
}

// js/common/product/variant-picker.js
import { Delegate as Delegate4 } from "vendor";
var CACHE_EVICTION_TIME = 1e3 * 60 * 5;
var _preloadedHtml, _delegate, _intersectionObserver, _form, _selectedVariant, _VariantPicker_instances, getActiveOptionValues_fn, getOptionValuesFromOption_fn, onOptionChanged_fn, onOptionPreload_fn, onIntersection_fn, renderForCombination_fn, createHashKeyForHtml_fn;
var _VariantPicker = class _VariantPicker extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _VariantPicker_instances);
    __privateAdd(this, _delegate, new Delegate4(document.body));
    __privateAdd(this, _intersectionObserver, new IntersectionObserver(__privateMethod(this, _VariantPicker_instances, onIntersection_fn).bind(this)));
    __privateAdd(this, _form);
    __privateAdd(this, _selectedVariant);
  }
  async connectedCallback() {
    __privateSet(this, _selectedVariant, JSON.parse(this.querySelector("script[data-variant]")?.textContent || "{}"));
    __privateSet(this, _form, document.forms[this.getAttribute("form-id")]);
    __privateGet(this, _delegate).on("change", `input[data-option-position][form="${this.getAttribute("form-id")}"]`, __privateMethod(this, _VariantPicker_instances, onOptionChanged_fn).bind(this));
    __privateGet(this, _delegate).on("pointerenter", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked) + label`, __privateMethod(this, _VariantPicker_instances, onOptionPreload_fn).bind(this), true);
    __privateGet(this, _delegate).on("touchstart", `input[data-option-position][form="${this.getAttribute("form-id")}"]:not(:checked) + label`, __privateMethod(this, _VariantPicker_instances, onOptionPreload_fn).bind(this), true);
    __privateGet(this, _intersectionObserver).observe(this);
  }
  disconnectedCallback() {
    __privateGet(this, _delegate).off();
    __privateGet(this, _intersectionObserver).unobserve(this);
  }
  get selectedVariant() {
    return __privateGet(this, _selectedVariant);
  }
  get productHandle() {
    return this.getAttribute("handle");
  }
  get updateUrl() {
    return this.hasAttribute("update-url");
  }
  /**
   * Select a variant using a list of option values. The list of option values might lead to no variant (for instance)
   * in the case of a combination that does not exist
   */
  async selectCombination({ optionValues, productChange }) {
    const previousVariant = this.selectedVariant;
    const newContent = document.createRange().createContextualFragment(await __privateMethod(this, _VariantPicker_instances, renderForCombination_fn).call(this, optionValues));
    if (!productChange) {
      const newVariantPicker = deepQuerySelector(newContent, `${this.tagName}[form-id="${this.getAttribute("form-id")}"]`);
      const newVariant = JSON.parse(newVariantPicker.querySelector("script[data-variant]")?.textContent || "{}");
      __privateSet(this, _selectedVariant, newVariant);
      __privateGet(this, _form).id.value = __privateGet(this, _selectedVariant)?.id;
      __privateGet(this, _form).id.dispatchEvent(new Event("change", { bubbles: true }));
      if (this.updateUrl && __privateGet(this, _selectedVariant)?.id) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("variant", __privateGet(this, _selectedVariant).id);
        window.history.replaceState({ path: newUrl.toString() }, "", newUrl.toString());
      }
    }
    __privateGet(this, _form).dispatchEvent(new CustomEvent("product:rerender", {
      detail: {
        htmlFragment: newContent,
        productChange
      }
    }));
    if (!productChange) {
      __privateGet(this, _form).dispatchEvent(new CustomEvent("variant:change", {
        bubbles: true,
        detail: {
          formId: __privateGet(this, _form).id,
          variant: __privateGet(this, _selectedVariant),
          previousVariant
        }
      }));
    }
    Shopify?.PaymentButton?.init();
  }
};
_preloadedHtml = new WeakMap();
_delegate = new WeakMap();
_intersectionObserver = new WeakMap();
_form = new WeakMap();
_selectedVariant = new WeakMap();
_VariantPicker_instances = new WeakSet();
/**
 * Get the option values for the active combination
 */
getActiveOptionValues_fn = function() {
  return Array.from(__privateGet(this, _form).elements).filter((item) => item.matches("input[data-option-position]:checked")).sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position"))).map((input) => input.value);
};
/**
 * Get the option values for a given input
 */
getOptionValuesFromOption_fn = function(input) {
  const optionValues = [input, ...Array.from(__privateGet(this, _form).elements).filter((item) => item.matches(`input[data-option-position]:not([name="${input.name}"]):checked`))].sort((a, b) => parseInt(a.getAttribute("data-option-position")) - parseInt(b.getAttribute("data-option-position"))).map((input2) => input2.value);
  return optionValues;
};
onOptionChanged_fn = async function(event) {
  if (!event.target.name.includes("option")) {
    return;
  }
  this.selectCombination({
    optionValues: __privateMethod(this, _VariantPicker_instances, getActiveOptionValues_fn).call(this),
    productChange: event.target.hasAttribute("data-product-url")
  });
};
/**
 * To improve the user experience, we preload a variant whenever the user hovers over a specific option
 */
onOptionPreload_fn = function(event, target) {
  __privateMethod(this, _VariantPicker_instances, renderForCombination_fn).call(this, __privateMethod(this, _VariantPicker_instances, getOptionValuesFromOption_fn).call(this, target.control));
};
/**
 * When the variant picker is intersecting the viewport, we preload the options to improve the user experience
 * so that switching variants is nearly instant
 */
onIntersection_fn = function(entries) {
  const prerenderOptions = () => {
    Array.from(__privateGet(this, _form).elements).filter((item) => item.matches("input[data-option-position]:not(:checked)")).forEach((input) => {
      __privateMethod(this, _VariantPicker_instances, renderForCombination_fn).call(this, __privateMethod(this, _VariantPicker_instances, getOptionValuesFromOption_fn).call(this, input));
    });
  };
  if (entries[0].isIntersecting) {
    if (window.requestIdleCallback) {
      window.requestIdleCallback(prerenderOptions, { timeout: 2e3 });
    } else {
      prerenderOptions();
    }
  }
};
renderForCombination_fn = async function(optionValues) {
  const optionValuesAsString = optionValues.join(",");
  const hashKey = __privateMethod(this, _VariantPicker_instances, createHashKeyForHtml_fn).call(this, optionValuesAsString);
  let productUrl = `${Shopify.routes.root}products/${this.productHandle}`;
  for (const optionValue of optionValues) {
    const inputForOptionValue = Array.from(__privateGet(this, _form).elements).find((item) => item.matches(`input[value="${optionValue}"]`));
    if (inputForOptionValue?.dataset.productUrl) {
      productUrl = inputForOptionValue.dataset.productUrl;
      break;
    }
  }
  if (!__privateGet(_VariantPicker, _preloadedHtml).has(hashKey)) {
    const sectionQueryParam = this.getAttribute("context") === "quick_buy" ? "" : `&section_id=${this.getAttribute("section-id")}`;
    const promise = new Promise(async (resolve) => {
      resolve(await (await fetch(`${productUrl}?option_values=${optionValuesAsString}${sectionQueryParam}`)).text());
    });
    __privateGet(_VariantPicker, _preloadedHtml).set(hashKey, { htmlPromise: promise, timestamp: Date.now() });
    if (__privateGet(_VariantPicker, _preloadedHtml).size > 100) {
      __privateGet(_VariantPicker, _preloadedHtml).delete(Array.from(__privateGet(_VariantPicker, _preloadedHtml).keys())[0]);
    }
  }
  return __privateGet(_VariantPicker, _preloadedHtml).get(hashKey).htmlPromise;
};
createHashKeyForHtml_fn = function(optionValuesAsString) {
  return `${optionValuesAsString}-${this.getAttribute("section-id")}`;
};
__privateAdd(_VariantPicker, _preloadedHtml, /* @__PURE__ */ new Map());
var VariantPicker = _VariantPicker;
if (!window.customElements.get("variant-picker")) {
  window.customElements.define("variant-picker", VariantPicker);
}

// js/common/media/base-media.js
import { inView as inView4 } from "vendor";
var BaseMedia = class extends HTMLElement {
  static get observedAttributes() {
    return ["playing"];
  }
  connectedCallback() {
    this._abortController = new AbortController();
    if (this.hasAttribute("autoplay")) {
      inView4(this, this.play.bind(this), { margin: "0px 0px 0px 0px" });
    }
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get playing() {
    return this.hasAttribute("playing");
  }
  get player() {
    return this._playerProxy = this._playerProxy || new Proxy(this._playerTarget(), {
      get: (target, prop) => {
        return async () => {
          target = await target;
          this._playerHandler(target, prop);
        };
      }
    });
  }
  play() {
    if (!this.playing) {
      this.player.play();
    }
  }
  pause() {
    if (this.playing) {
      this.player.pause();
    }
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "playing") {
      if (oldValue === null && newValue === "") {
        this.dispatchEvent(new CustomEvent("media:play", { bubbles: true }));
        if (this.hasAttribute("group")) {
          Array.from(document.querySelectorAll(`[group="${this.getAttribute("group")}"]`)).filter((item) => item !== this).forEach((itemToPause) => {
            itemToPause.pause();
          });
        }
      } else if (newValue === null) {
        this.dispatchEvent(new CustomEvent("media:pause", { bubbles: true }));
      }
    }
  }
};

// js/common/media/model.js
var ModelMedia = class extends BaseMedia {
  connectedCallback() {
    super.connectedCallback();
    this.player;
  }
  _playerTarget() {
    return new Promise((resolve) => {
      this.setAttribute("loaded", "");
      window.Shopify.loadFeatures([
        {
          name: "shopify-xr",
          version: "1.0",
          onLoad: this._setupShopifyXr.bind(this)
        },
        {
          name: "model-viewer-ui",
          version: "1.0",
          onLoad: () => {
            const modelViewer = this.querySelector("model-viewer");
            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_play", () => this.setAttribute("playing", ""));
            modelViewer.addEventListener("shopify_model_viewer_ui_toggle_pause", () => this.removeAttribute("playing"));
            this.setAttribute("can-play", "");
            resolve(new window.Shopify.ModelViewerUI(modelViewer, { focusOnPlay: true }));
          }
        }
      ]);
    });
  }
  _playerHandler(target, prop) {
    target[prop]();
  }
  async _setupShopifyXr() {
    if (!window.ShopifyXR) {
      document.addEventListener("shopify_xr_initialized", this._setupShopifyXr.bind(this));
    } else {
      const models = (await ProductLoader.load(this.getAttribute("handle")))["media"].filter((media) => media["media_type"] === "model");
      window.ShopifyXR.addModels(models);
      window.ShopifyXR.setupXRElements();
    }
  }
};
if (!window.customElements.get("model-media")) {
  window.customElements.define("model-media", ModelMedia);
}

// js/common/media/video.js
var onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});
var VideoMedia = class extends BaseMedia {
  connectedCallback() {
    super.connectedCallback();
    if (!this.hasAttribute("autoplay")) {
      this.addEventListener("click", this.play, { once: true, signal: this._abortController.signal });
      this.nextElementSibling?.querySelector(".video-play-button")?.addEventListener("click", this.play.bind(this), { once: true, signal: this._abortController.signal });
    }
    if (this.hasAttribute("show-play-button") && !this.shadowRoot) {
      this.attachShadow({ mode: "open" }).appendChild(document.createRange().createContextualFragment(`
        <slot></slot>
        
        <svg part="play-button" fill="none" width="48" height="48" viewBox="0 0 48 48">
          <path d="M48 24C48 10.745 37.255 0 24 0S0 10.745 0 24s10.745 24 24 24 24-10.745 24-24Z" fill="${window.themeVariables.settings.pageBackground}"/>
          <path d="M18.578 32.629a.375.375 0 0 1-.578-.316V15.687c0-.297.328-.476.578-.316l12.931 8.314c.23.147.23.483 0 .63L18.578 32.63Z" fill="${window.themeVariables.settings.textColor}"/>
          <path d="M24 .5C36.979.5 47.5 11.021 47.5 24S36.979 47.5 24 47.5.5 36.979.5 24 11.021.5 24 .5Z" stroke="${window.themeVariables.settings.textColor}" stroke-opacity=".12"/>
        </svg>
      `));
    }
  }
  play({ restart = false } = {}) {
    if (restart && !this.hasAttribute("host")) {
      this.querySelector("video").currentTime = 0;
    }
    super.play();
  }
  _playerTarget() {
    if (this.hasAttribute("host")) {
      this.setAttribute("loaded", "");
      return new Promise(async (resolve) => {
        const templateElement = this.querySelector("template");
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
        }
        const muteVideo = this.hasAttribute("autoplay") || window.matchMedia("screen and (max-width: 999px)").matches;
        const script = document.createElement("script");
        script.type = "text/javascript";
        if (this.getAttribute("host") === "youtube") {
          if (!window.YT || !window.YT.Player) {
            script.src = "https://www.youtube.com/iframe_api";
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          await onYouTubePromise;
          this.setAttribute("can-play", "");
          const player = new YT.Player(this.querySelector("iframe"), {
            events: {
              "onReady": () => {
                if (muteVideo) {
                  player.mute();
                }
                resolve(player);
              },
              "onStateChange": (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute("playing", "");
                } else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                  this.removeAttribute("playing");
                }
              }
            }
          });
        }
        if (this.getAttribute("host") === "vimeo") {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = "https://player.vimeo.com/api/player.js";
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          const player = new Vimeo.Player(this.querySelector("iframe"));
          if (muteVideo) {
            player.setMuted(true);
          }
          this.setAttribute("can-play", "");
          player.on("play", () => {
            this.setAttribute("playing", "");
          });
          player.on("pause", () => this.removeAttribute("playing"));
          player.on("ended", () => this.removeAttribute("playing"));
          resolve(player);
        }
      });
    } else {
      const videoElement = this.querySelector("video");
      this.setAttribute("loaded", "");
      this.setAttribute("can-play", "");
      videoElement.addEventListener("play", () => {
        this.setAttribute("playing", "");
        this.removeAttribute("suspended");
      });
      videoElement.addEventListener("pause", () => {
        if (!videoElement.seeking && videoElement.paused) {
          this.removeAttribute("playing");
        }
      });
      return videoElement;
    }
  }
  _playerHandler(target, prop) {
    if (this.getAttribute("host") === "youtube") {
      prop === "play" ? target.playVideo() : target.pauseVideo();
    } else {
      if (prop === "play" && !this.hasAttribute("host")) {
        target.play().catch((error) => {
          if (error.name === "NotAllowedError") {
            this.setAttribute("suspended", "");
            target.controls = true;
            const replacementImageSrc = target.previousElementSibling?.currentSrc;
            if (replacementImageSrc) {
              target.poster = replacementImageSrc;
            }
          }
        });
      } else {
        target[prop]();
      }
    }
  }
};
if (!window.customElements.get("video-media")) {
  window.customElements.define("video-media", VideoMedia);
}

// js/common/navigation/accordion-disclosure.js
import { timeline as timeline6 } from "vendor";

// js/common/navigation/animated-details.js
var AnimatedDetails = class extends HTMLDetailsElement {
  constructor() {
    super();
    this.summaryElement = this.firstElementChild;
    this.contentElement = this.lastElementChild;
    this._open = this.hasAttribute("open");
    this.summaryElement.addEventListener("click", this._onSummaryClicked.bind(this));
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", () => this.open = true);
      this.addEventListener("shopify:block:deselect", () => this.open = false);
    }
  }
  set open(value) {
    if (value !== this._open) {
      this._open = value;
      if (this.isConnected) {
        this._transition(value);
      } else {
        value ? this.setAttribute("open", "") : this.removeAttribute("open");
      }
    }
  }
  get open() {
    return this._open;
  }
  _onSummaryClicked(event) {
    event.preventDefault();
    this.open = !this.open;
  }
  _transition(value) {
  }
};

// js/common/navigation/accordion-disclosure.js
var AccordionDisclosure = class extends AnimatedDetails {
  static get observedAttributes() {
    return ["open"];
  }
  constructor() {
    super();
    this.setAttribute("aria-expanded", this._open ? "true" : "false");
  }
  set open(value) {
    super.open = value;
    this.setAttribute("aria-expanded", value ? "true" : "false");
  }
  get open() {
    return super.open;
  }
  async _transition(value) {
    this.style.overflow = "hidden";
    if (value) {
      this.setAttribute("open", "");
      await timeline6([
        [this, { height: [`${this.summaryElement.clientHeight}px`, `${this.scrollHeight}px`] }, { duration: 0.25, easing: "ease" }],
        [this.contentElement, { opacity: [0, 1], transform: ["translateY(0)", "translateY(-4px)"] }, { duration: 0.15, at: "-0.1" }]
      ]).finished;
    } else {
      await timeline6([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [this, { height: [`${this.clientHeight}px`, `${this.summaryElement.clientHeight}px`] }, { duration: 0.25, at: "<", easing: "ease" }]
      ]).finished;
      this.removeAttribute("open");
    }
    this.style.height = "auto";
    this.style.overflow = "visible";
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "open") {
      this.setAttribute("aria-expanded", newValue === "" ? "true" : "false");
    }
  }
};
if (!window.customElements.get("accordion-disclosure")) {
  window.customElements.define("accordion-disclosure", AccordionDisclosure, { extends: "details" });
}

// js/common/navigation/tabs.js
import { animate as animate8 } from "vendor";
var Tabs = class extends HTMLElement {
  static get observedAttributes() {
    return ["selected-index"];
  }
  constructor() {
    super();
    if (!this.shadowRoot) {
      this.attachShadow({ mode: "open" }).appendChild(this.querySelector("template").content.cloneNode(true));
    }
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => this.selectedIndex = this.buttons.indexOf(event.target));
    }
    this.addEventListener("keydown", this._handleKeyboard);
  }
  connectedCallback() {
    this._abortController = new AbortController();
    this.buttons = Array.from(this.shadowRoot.querySelector('slot[name="title"]').assignedNodes(), (item) => item.matches("button") && item || item.querySelector("button"));
    this.panels = Array.from(this.shadowRoot.querySelector('slot[name="content"]').assignedNodes());
    this.buttons.forEach((button, index) => button.addEventListener("click", () => this.selectedIndex = index, { signal: this._abortController.signal }));
    this.selectedIndex = this.selectedIndex;
    this._setupAccessibility();
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  get animationDuration() {
    return this.hasAttribute("animation-duration") ? parseFloat(this.getAttribute("animation-duration")) : 0.15;
  }
  get selectedIndex() {
    return parseInt(this.getAttribute("selected-index")) || 0;
  }
  set selectedIndex(index) {
    this.setAttribute("selected-index", Math.min(Math.max(index, 0), this.buttons.length - 1).toString());
    this.style.setProperty("--selected-index", this.selectedIndex.toString());
    this.style.setProperty("--item-count", this.buttons.length.toString());
  }
  attributeChangedCallback(name, oldValue, newValue) {
    this.buttons.forEach((button, index) => button.setAttribute("aria-selected", index === parseInt(newValue) ? "true" : "false"));
    if (name === "selected-index" && oldValue !== null && oldValue !== newValue) {
      this._transition(this.panels[parseInt(oldValue)], this.panels[parseInt(newValue)]);
    }
  }
  _setupAccessibility() {
    const componentID = crypto.randomUUID ? crypto.randomUUID() : Math.floor(Math.random() * 1e4);
    this.buttons.forEach((button, index) => {
      button.setAttribute("role", "tab");
      button.setAttribute("aria-controls", `tab-panel-${componentID}-${index}`);
      button.id = `tab-${componentID}-${index}`;
    });
    this.panels.forEach((panel, index) => {
      panel.setAttribute("role", "tabpanel");
      panel.setAttribute("aria-labelledby", `tab-${componentID}-${index}`);
      panel.id = `tab-panel-${componentID}-${index}`;
      panel.hidden = index !== this.selectedIndex;
    });
  }
  /**
   * As per https://www.w3.org/WAI/ARIA/apg/example-index/tabs/tabs-automatic.html, when a tab is currently focused,
   * left and right arrow should switch the tab
   */
  _handleKeyboard(event) {
    const index = this.buttons.indexOf(document.activeElement);
    if (index === -1 || !["ArrowLeft", "ArrowRight"].includes(event.key)) {
      return;
    }
    if (event.key === "ArrowLeft") {
      this.selectedIndex = (this.selectedIndex - 1 + this.buttons.length) % this.buttons.length;
    } else {
      this.selectedIndex = (this.selectedIndex + 1 + this.buttons.length) % this.buttons.length;
    }
    this.buttons[this.selectedIndex].focus();
  }
  /**
   * Perform a custom transition (can be overridden in subclasses). To "from" and "to" are hash representing the panel
   */
  async _transition(fromPanel, toPanel) {
    await animate8(fromPanel, { opacity: [1, 0] }, { duration: this.animationDuration }).finished;
    fromPanel.hidden = true;
    toPanel.hidden = false;
    await animate8(toPanel, { opacity: [0, 1] }, { duration: this.animationDuration }).finished;
  }
};
if (!window.customElements.get("x-tabs")) {
  window.customElements.define("x-tabs", Tabs);
}

function pickFitmentFootnoteForProduct(product) {
  const matchedSkus = Array.isArray(product?.matchedSkus) ? product.matchedSkus.map((sku) => normalizeSkuKey(sku)).filter(Boolean) : [];
  const matchedSet = matchedSkus.length ? new Set(matchedSkus) : null;
  const noteMap = getCollectionFitmentNoteMap(buildFitmentNoteMapFromMatchedFitment(product?.matchedFitment));
  return pickCollectionDisplayFootnote(matchedSkus, noteMap, product, matchedSet);
}
function buildPredictiveFitmentProductHtml(product) {
  const rawUrl = product?.url || (product?.handle ? "/products/" + String(product.handle) : "#");
  const url = escapeHtml(rawUrl);
  const title = escapeHtml(product?.title || "");
  const imageUrl = product?.image ? escapeHtml(product.image) : "";
  const figureHtml = imageUrl
    ? '<div class="predictive-search-result__figure"><img src="' + imageUrl + '" alt="" class="predictive-search-result__image" width="128" height="128" loading="lazy"></div>'
    : '<div class="predictive-search-result__figure predictive-search-result__figure--placeholder"></div>';
  const priceText = formatShopMoney(product?.price);
  const compareText = formatShopMoney(product?.compareAtPrice);
  const hasCompare = Number(product?.compareAtPrice) > Number(product?.price) && !!compareText;
  const priceHtml = buildProductCardPriceWithStockHtml(product, priceText, compareText, hasCompare);
  const footnote = pickFitmentFootnoteForProduct(product);
  const footnoteBadgeHtml = footnote
    ? '<span class="badge badge--fitment-note">' + escapeHtml(footnote) + "</span>"
    : "";
  const compatibleBadgeHtml = '<span class="badge badge--compatible">' + escapeHtml(getCompatibleBadgeLabel()) + "</span>";
  return '<a href="' + url + '" data-instant class="predictive-search-result predictive-search-result--fitment group">' + figureHtml + '<div class="predictive-search-result__content v-stack gap-2 justify-items-start"><div class="predictive-search-result__badge-list">' + compatibleBadgeHtml + footnoteBadgeHtml + '</div><span class="bold w-full"><span class="reversed-link">' + title + "</span></span>" + priceHtml + "</div></a>";
}
function buildFitmentPredictiveSearchResultsSlot(products, query) {
  const items = Array.isArray(products) ? products : [];
  const slot = document.createElement("div");
  slot.setAttribute("slot", "results");
  if (!items.length) {
    slot.innerHTML = '<div class="empty-state"><p class="h6">No compatible products found.</p></div>';
    return slot;
  }
  const listHtml = items.map((product) => buildPredictiveFitmentProductHtml(product)).join("");
  const count = items.length;
  const terms = escapeHtml(query);
  slot.innerHTML = '<span class="sr-only" role="status">' + count + " compatible product" + (count === 1 ? "" : "s") + " for " + terms + '</span><div class="v-stack gap-4 sm:gap-5 predictive-search__fitment-results">' + listHtml + "</div>";
  return slot;
}
function isCompatibleOnlyActive(urlLike) {
  const url = toCollectionRequestUrl(urlLike || location.href);
  if (url.searchParams.get(COMPATIBLE_ONLY_KEY) === "1") return true;
  const tagVal = url.searchParams.get("filter.v.tag") || "";
  if (tagVal && tagVal.startsWith(COMPATIBLE_TAG_PREFIX)) return true;
  return !!document.querySelector("[data-compatible-my-car]:checked");
}
function syncFitmentSearchFormParams(form, useCompatible) {
  if (!form) return;
  let compatibleInput = form.querySelector('input[name="compatible_only"]');
  if (useCompatible && getVehicleId()) {
    if (!compatibleInput) {
      compatibleInput = document.createElement("input");
      compatibleInput.type = "hidden";
      compatibleInput.name = "compatible_only";
      form.appendChild(compatibleInput);
    }
    compatibleInput.value = "1";
  } else if (compatibleInput) {
    compatibleInput.remove();
  }
}
function bindMainSearchFormCompatiblePreserve() {
  document.querySelectorAll("form.main-search-form").forEach((form) => {
    if (form.dataset.vsCompatibleBound === "1") return;
    form.dataset.vsCompatibleBound = "1";
    form.addEventListener("submit", () => {
      // Keep an already-on compatible filter when re-submitting from the search page form.
      syncFitmentSearchFormParams(form, isCompatibleOnlyActive() && !!getVehicleId());
    });
  });
}
async function decoratePredictiveSearchFitmentBadges(root) {
  if (!root) return;
  const vehicleId = getVehicleId();
  const badges = root.querySelectorAll(".predictive-search-result__compatible-badge");
  if (!vehicleId) {
    badges.forEach((badge) => {
      badge.hidden = true;
    });
    return;
  }
  let matched = getCachedMatchedSkusForVehicle(vehicleId);
  if (!matched) {
    try {
      const result = await fetchVehicleSkusFromFitmentService(vehicleId, getUserCookie());
      matched = result?.skus || null;
    } catch (_) {
      matched = null;
    }
  }
  root.querySelectorAll(".predictive-search-result[data-variant-skus]").forEach((el) => {
    if (el.getAttribute("data-universal-fit") === "true") return;
    const badge = el.querySelector(".predictive-search-result__compatible-badge");
    if (!badge) return;
    if (!matched) {
      badge.hidden = true;
      return;
    }
    const skus = String(el.getAttribute("data-variant-skus") || "")
      .split(",")
      .map((sku) => normalizeSkuKey(sku))
      .filter(Boolean);
    badge.hidden = !skus.some((sku) => matched.has(sku));
  });
}
// js/common/search/predictive-search.js
import { animate as animate9 } from "vendor";
var PredictiveSearch = class extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(document.createRange().createContextualFragment(`<slot name="idle"></slot>`));
  }
  connectedCallback() {
    this._searchForm = this.closest("form");
    this._queryInput = this._searchForm.elements["q"];
    this._onGarageChanged = () => {
      if (this._queryInput?.value?.trim()) this._onInputChanged();
    };
    this._searchForm.addEventListener("submit", this._onFormSubmitted.bind(this));
    this._searchForm.addEventListener("reset", this._onSearchCleared.bind(this));
    this._queryInput.addEventListener("input", debounce(this._onInputChanged.bind(this), this.autoCompleteDelay));
    window.addEventListener("vs:garage-changed", this._onGarageChanged);
  }
  disconnectedCallback() {
    window.removeEventListener("vs:garage-changed", this._onGarageChanged);
  }
  /**
   * Return the delay in ms before we send the autocomplete request. Using a value too low can cause the results to
   * refresh too often, so we recommend to keep the default one
   */
  get autoCompleteDelay() {
    return 280;
  }
  /**
   * Check if the store supports the predictive API (some languages do not). When not supported, the predictive
   * search is simply disabled and only the standard search is used
   */
  supportsPredictiveApi() {
    return JSON.parse(document.getElementById("shopify-features").innerHTML)["predictiveSearch"];
  }
  /**
   * Check if the input is not empty, and if so, trigger the predictive search
   */
  _onInputChanged() {
    if (this._queryInput.value === "") {
      return this._onSearchCleared();
    }
    this._abortController?.abort();
    this._abortController = new AbortController();
    try {
      // Search must NOT default to compatible-only when a vehicle is in garage.
      // Use normal Shopify predictive search; user can enable compatible filter on /search.
      syncFitmentSearchFormParams(this._searchForm, false);
      return this.supportsPredictiveApi() ? this._doPredictiveSearch() : this._doFallbackSearch();
    } catch (e) {
      if (e.name !== "AbortError") {
        throw e;
      }
    }
  }
  /**
   * Prevent the form submission if the query is empty
   */
  _onFormSubmitted(event) {
    if (this._queryInput.value === "") {
      return event.preventDefault();
    }
    // Do not auto-enable compatible on Enter. If the user already turned it on
    // (search page URL / facet checkbox), preserve it across submit.
    const keepCompatible = isCompatibleOnlyActive() && !!getVehicleId();
    syncFitmentSearchFormParams(this._searchForm, keepCompatible);
  }
  async _doFitmentPredictiveSearch() {
    await this._transitionToSlot("loading");
    const vehicleId = getVehicleId();
    const query = this._queryInput.value.trim();
    const userCookie = getUserCookie();
    const sourceUrl = new URL(location.href);
    sourceUrl.searchParams.set("q", query);
    try {
      const result = await fetchFitmentSearch(vehicleId, userCookie, this._abortController.signal, 10, sourceUrl);
      const slot = buildFitmentPredictiveSearchResultsSlot(result?.products || [], query);
      const existing = this.querySelector('[slot="results"]');
      if (existing) existing.replaceWith(slot);
      else this.appendChild(slot);
      syncFitmentSearchFormParams(this._searchForm, true);
      return this._transitionToSlot("results");
    } catch (error) {
      if (error?.name === "AbortError") throw error;
      console.warn("[fitment][search-debug] predictive fitment search failed, using Shopify search", error);
      syncFitmentSearchFormParams(this._searchForm, false);
      return this.supportsPredictiveApi() ? this._doPredictiveSearch() : this._doFallbackSearch();
    }
  }
  /**
   * Do the actual predictive search
   */
  async _doPredictiveSearch() {
    await this._transitionToSlot("loading");
    const queryParams = `q=${encodeURIComponent(this._queryInput.value)}&section_id=${this.getAttribute("section-id")}&resources[limit]=10&resources[limit_scope]=each`;
    const nodeElement = new DOMParser().parseFromString(await (await cachedFetch(`${window.Shopify.routes.root}search/suggest?${queryParams}`, { signal: this._abortController.signal })).text(), "text/html");
    this.querySelector('[slot="results"]').replaceWith(document.importNode(nodeElement.querySelector('[slot="results"]'), true));
    await decoratePredictiveSearchFitmentBadges(this.querySelector('[slot="results"]'));
    return this._transitionToSlot("results");
  }
  /**
   * For merchants using store that do not support the predictive search, we have to fallback to standard search. Unfortunately,
   * the standard search is less convenient, and we have to simulate one request for each desired resource type
   */
  async _doFallbackSearch() {
    await this._transitionToSlot("loading");
    const queryParams = `q=${this._queryInput.value}&section_id=${this.getAttribute("section-id")}&resources[limit]=10&resources[limit_scope]=each`;
    const nodeElement = new DOMParser().parseFromString(await (await cachedFetch(`${window.Shopify.routes.root}search?${queryParams}`, { signal: this._abortController.signal })).text(), "text/html");
    this.querySelector('[slot="results"]').replaceWith(document.importNode(nodeElement.querySelector('[slot="results"]'), true));
    await decoratePredictiveSearchFitmentBadges(this.querySelector('[slot="results"]'));
    return this._transitionToSlot("results");
  }
  /**
   * If any search is pending, we abort them, and transition to the idle slot
   */
  _onSearchCleared() {
    this._abortController?.abort();
    syncFitmentSearchFormParams(this._searchForm, false);
    this._queryInput.focus();
    return this._transitionToSlot("idle");
  }
  /**
   * Transition between different slot. To prevent useless animation, the slot does not transition if we go to the same slot
   */
  async _transitionToSlot(toSlotName) {
    if (this.shadowRoot.firstElementChild.name === toSlotName) {
      return;
    }
    await animate9(this.shadowRoot.firstElementChild.assignedNodes(), { opacity: [1, 0] }, { duration: 0.1 }).finished;
    this.shadowRoot.firstElementChild.setAttribute("name", toSlotName);
    return animate9(this.shadowRoot.firstElementChild.assignedNodes(), { opacity: [0, 1], transform: ["translateY(5px)", "translateY(0)"] }, { duration: 0.1 }).finished;
  }
};
if (!window.customElements.get("predictive-search")) {
  window.customElements.define("predictive-search", PredictiveSearch);
}

// js/common/search/search-drawer.js
var SearchDrawer = class extends Drawer {
  get shouldAppendToBody() {
    return false;
  }
  get openFrom() {
    return window.matchMedia(`${window.themeVariables.breakpoints["sm-max"]}`).matches ? "top" : this.getAttribute("open-from") || "right";
  }
  _onToggleClicked(event) {
    if (!window.matchMedia("(max-width: 1149px)").matches) {
      return;
    }
    event?.preventDefault();
    this.open ? this.hide() : this.show();
  }
};
if (!window.customElements.get("search-drawer")) {
  window.customElements.define("search-drawer", SearchDrawer);
}

// js/common/search/search-dropdown.js
var SearchDropdown = class extends DialogElement {
  connectedCallback() {
    super.connectedCallback();
    this._desktopMedia = window.matchMedia("(min-width: 1150px)");
    this.querySelector(".search-dropdown__overlay")?.addEventListener("click", this.hide.bind(this), { signal: this._abortController.signal });
    this._desktopMedia.addEventListener("change", () => {
      if (!this._desktopMedia.matches && this.open) {
        this.hide();
      }
    }, { signal: this._abortController.signal });
  }
  _onToggleClicked(event) {
    if (!this._desktopMedia.matches) {
      return;
    }
    event?.preventDefault();
    this.open ? this.hide() : this.show();
  }
  get shouldLock() {
    return this._desktopMedia?.matches ?? false;
  }
  get shouldAppendToBody() {
    return false;
  }
  get initialFocus() {
    return this.querySelector('input[type="search"]') || false;
  }
  _showTransition(animate19 = true) {
    this.style.display = "block";
    const overlay = this.querySelector(".search-dropdown__overlay");
    const panel = this.querySelector(".search-dropdown__panel");
    const animationControls = animate9([overlay, panel], { opacity: [0, 1] }, { duration: 0.15 });
    animate19 ? animationControls.play() : animationControls.finish();
    return animationControls.finished;
  }
  _hideTransition() {
    const overlay = this.querySelector(".search-dropdown__overlay");
    const panel = this.querySelector(".search-dropdown__panel");
    const animationControls = animate9([overlay, panel], { opacity: [1, 0] }, { duration: 0.15 });
    return animationControls.finished.then(() => {
      this.style.display = "none";
    });
  }
};
if (!window.customElements.get("search-dropdown")) {
  window.customElements.define("search-dropdown", SearchDropdown);
}

// js/common/text/section-header.js
import { animate as animate10, inView as inView5 } from "vendor";
var _SectionHeader_instances, reveal_fn2;
var SectionHeader = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _SectionHeader_instances);
  }
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      inView5(this, __privateMethod(this, _SectionHeader_instances, reveal_fn2).bind(this), { margin: "0px 0px -100px 0px" });
    }
  }
};
_SectionHeader_instances = new WeakSet();
reveal_fn2 = function() {
  const heading = this.querySelector('h2[reveal-on-scroll="true"]'), headingKeyframe = getHeadingKeyframe(heading);
  animate10(...headingKeyframe);
};
if (!window.customElements.get("section-header")) {
  window.customElements.define("section-header", SectionHeader);
}

// js/common/ui/marquee-text.js
var MarqueeText = class extends HTMLElement {
  constructor() {
    super();
    if (window.ResizeObserver) {
      new ResizeObserver(this._calculateDuration.bind(this)).observe(this);
    }
  }
  _calculateDuration(entries) {
    const scrollingSpeed = parseInt(this.getAttribute("scrolling-speed") || 5), contentWidth = entries[0].contentRect.width, slowFactor = 1 + (Math.min(1600, contentWidth) - 375) / (1600 - 375);
    this.style.setProperty("--marquee-animation-duration", `${(scrollingSpeed * slowFactor * entries[0].target.querySelector("span").clientWidth / contentWidth).toFixed(3)}s`);
  }
};
if (!window.customElements.get("marquee-text")) {
  window.customElements.define("marquee-text", MarqueeText);
}

// js/sections/announcement-bar.js
import { timeline as timeline7 } from "vendor";
var AnnouncementBar = class extends EffectCarousel {
  _transitionTo(fromSlide, toSlide) {
    timeline7([
      [fromSlide, { transform: ["translateY(0)", "translateY(-5px)"], opacity: [1, 0], visibility: ["visible", "hidden"] }, { duration: 0.2 }],
      [toSlide, { transform: ["translateY(5px)", "translateY(0)"], opacity: [0, 1], visibility: ["hidden", "visible"] }, { duration: 0.2 }]
    ]);
  }
};
if (!window.customElements.get("announcement-bar")) {
  window.customElements.define("announcement-bar", AnnouncementBar);
}

// js/sections/before-after-image.js
var SplitCursor = class extends HTMLElement {
  _abortController;
  connectedCallback() {
    this._parentSection = this.closest(".shopify-section");
    this._dragging = false;
    this._offsetX = this._currentX = 0;
    this._abortController = new AbortController();
    this._parentSection.addEventListener("pointerdown", this._onPointerDown.bind(this), { signal: this._abortController.signal });
    this._parentSection.addEventListener("pointermove", this._onPointerMove.bind(this), { signal: this._abortController.signal });
    this._parentSection.addEventListener("pointerup", this._onPointerUp.bind(this), { signal: this._abortController.signal });
    this.addEventListener("keydown", this._onKeyboardNavigation, { signal: this._abortController.signal });
    this._recalculateOffset();
    window.addEventListener("resize", this._recalculateOffset.bind(this), { signal: this._abortController.signal });
  }
  disconnectedCallback() {
    this._abortController?.abort();
  }
  get minOffset() {
    return -this.offsetLeft - (document.dir === "rtl" ? this.clientWidth : 0);
  }
  get maxOffset() {
    return this.offsetParent.clientWidth + this.minOffset;
  }
  _onPointerDown(event) {
    if (event.target === this || this.contains(event.target)) {
      this._initialX = event.clientX - this._offsetX;
      this._dragging = true;
    }
  }
  _onPointerMove(event) {
    if (!this._dragging) {
      return;
    }
    this._currentX = Math.min(Math.max(event.clientX - this._initialX, this.minOffset), this.maxOffset);
    this._offsetX = this._currentX;
    this._parentSection.style.setProperty("--clip-path-offset", `${this._currentX.toFixed(1)}px`);
  }
  _onPointerUp() {
    this._dragging = false;
  }
  _recalculateOffset() {
    this._parentSection.style.setProperty("--clip-path-offset", `${Math.min(Math.max(this.minOffset, this._currentX.toFixed(1)), this.maxOffset)}px`);
  }
  _onKeyboardNavigation(event) {
    if (!event.target.classList.contains("before-after__cursor") || !this.hasAttribute("vertical") && event.code !== "ArrowLeft" && event.code !== "ArrowRight" || this.hasAttribute("vertical") && event.code !== "ArrowUp" && event.code !== "ArrowDown") {
      return;
    }
    event.preventDefault();
    let currentPosition = parseInt(this._parentSection.style.getPropertyValue("--clip-path-offset"));
    let newPosition;
    if (this.hasAttribute("vertical")) {
      newPosition = event.code === "ArrowUp" ? currentPosition - 1 : currentPosition + 1;
    } else {
      newPosition = event.code === "ArrowLeft" ? currentPosition - 1 : currentPosition + 1;
    }
    this._currentX = Math.min(Math.max(newPosition, this.minOffset), this.maxOffset);
    this._offsetX = this._currentX;
    this._parentSection.style.setProperty("--clip-path-offset", `${this._currentX.toFixed(1)}px`);
  }
};
if (!window.customElements.get("split-cursor")) {
  window.customElements.define("split-cursor", SplitCursor);
}

// js/sections/collection-list.js
import { timeline as timeline8, inView as inView6 } from "vendor";
var CollectionList = class extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      inView6(this, this._reveal.bind(this), { margin: "-100px" });
    }
  }
  _reveal() {
    const toReveal = Array.from(this.querySelectorAll("[reveal-js]"));
    timeline8([
      [toReveal, { opacity: 1 }, { duration: 0 }],
      [toReveal.map((item) => item.querySelector("img, svg")), { opacity: [0, 1], transform: ["scale(1.03)", "auto"] }, { duration: 0.2 }],
      [toReveal.map((item) => item.querySelector(".collection-card__content-wrapper")), { opacity: [0, 1] }, { duration: 0.2 }]
    ]);
  }
};
if (!window.customElements.get("collection-list")) {
  window.customElements.define("collection-list", CollectionList);
}

// js/sections/customer-login.js
import { Delegate as Delegate5 } from "vendor";
var AccountLogin = class extends HTMLElement {
  connectedCallback() {
    this.recoverForm = this.querySelector("#recover");
    this.loginForm = this.querySelector("#login");
    if (window.location.hash === "#recover") {
      this._switchForms();
    }
    new Delegate5(this).on("click", '[href="#recover"], [href="#login"]', this._switchForms.bind(this));
  }
  _switchForms(event) {
    if (event) {
      event.preventDefault();
    }
    this.recoverForm.hidden = !this.recoverForm.hidden;
    this.loginForm.hidden = !this.loginForm.hidden;
  }
};
if (!window.customElements.get("account-login")) {
  window.customElements.define("account-login", AccountLogin);
}

// js/sections/header.js
import { animate as animate11, timeline as timeline9, stagger as stagger5, Delegate as Delegate6 } from "vendor";
var reduceMenuAnimation = window.themeVariables.settings.reduceMenuAnimation;
var StoreHeader = class extends HTMLElement {
  connectedCallback() {
    if (this.hasAttribute("hide-on-scroll") && window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      this._lastScrollTop = 0;
      this._accumulatedScroll = 0;
      this._isVisible = true;
      this._hasSwitchedToSticky = false;
      window.addEventListener("scroll", throttle(this._onScroll.bind(this)));
    }
    this.addEventListener("toggle", this._checkTransparency.bind(this), { capture: true });
    this._setupTransparentHeader();
    if (this.hasAttribute("sticky")) {
      window.addEventListener("pageshow", (event) => {
        if (event.persisted) {
          this.classList.remove("is-filled");
          this._setupTransparentHeader();
        }
      });
      if (Shopify.designMode) {
        document.addEventListener("shopify:section:load", this._setupTransparentHeader.bind(this));
        document.addEventListener("shopify:section:unload", this._setupTransparentHeader.bind(this));
        document.addEventListener("shopify:section:reorder", this._setupTransparentHeader.bind(this));
      }
    }
  }
  async hide() {
    if (this._isVisible) {
      this._isVisible = false;
      document.documentElement.style.setProperty("--header-is-visible", "0");
      await animate11(this, { transform: ["translateY(0)", "translateY(-100%)"] }, { duration: 0.2, easing: "ease" }).finished;
      if (this._isVisible === false) {
        this.closest(".shopify-section").style.visibility = "hidden";
      }
    }
  }
  show() {
    if (!this._isVisible) {
      this.closest(".shopify-section").style.visibility = "visible";
      document.documentElement.style.setProperty("--header-is-visible", "1");
      animate11(this, { transform: ["translateY(-100%)", "translateY(0)"] }, { duration: 0.2, easing: "ease" });
      this._accumulatedScroll = 0;
      this._isVisible = true;
    }
  }
  _onScroll() {
    if (window.scrollY < 0) {
      return;
    }
    this._accumulatedScroll = Math.max(0, this._accumulatedScroll + (window.scrollY - this._lastScrollTop));
    if (window.scrollY < this._lastScrollTop) {
      this.show();
    } else if (this._accumulatedScroll > parseInt(this.getAttribute("hide-on-scroll")) && this.querySelectorAll("[open]").length === 0) {
      this.hide();
    }
    this._lastScrollTop = window.scrollY;
  }
  _checkTransparency() {
    let hasFallbackScrollDetection = false;
    if (CSS.supports("selector(:has(> *))") && this.hasAttribute("sticky") && !this.hasAttribute("hide-on-scroll") && this.hasAttribute("allow-transparency")) {
      const offsetBeforeChanging = 500;
      if (this.querySelectorAll("[open]").length > 0) {
        this.classList.add("is-filled");
      } else if (window.scrollY >= offsetBeforeChanging && !this._hasSwitchedToSticky) {
        this._hasSwitchedToSticky = true;
        this.classList.add("is-filled");
        animate11(this, { transform: ["translateY(-100%)", "translateY(0)"] }, { duration: 0.15, easing: "ease" });
      } else if (window.scrollY < offsetBeforeChanging) {
        if (this._hasSwitchedToSticky) {
          this._hasSwitchedToSticky = false;
          animate11(this, { transform: ["translateY(0)", "translateY(-100%)"] }, { duration: 0.15, easing: "ease" }).finished.then(() => {
            this.style.transform = null;
            this.classList.remove("is-filled");
          });
        } else if (this.getAnimations().length === 0) {
          this.classList.remove("is-filled");
        }
      }
    } else {
      hasFallbackScrollDetection = this.hasAttribute("sticky") && window.scrollY > 20;
      this.classList.toggle("is-filled", !this.hasAttribute("allow-transparency") || this.querySelectorAll("[open]").length > 0 || hasFallbackScrollDetection);
    }
  }
  _setupTransparentHeader() {
    if (document.querySelector(".shopify-section:first-child [allow-transparent-header]")) {
      this.setAttribute("allow-transparency", "");
      this.addEventListener("mouseenter", this._checkTransparency.bind(this));
      this.addEventListener("mouseleave", this._checkTransparency.bind(this));
      if (this.hasAttribute("sticky")) {
        window.addEventListener("scroll", throttle(this._checkTransparency.bind(this)));
      }
      this._checkTransparency();
    } else {
      this.removeAttribute("allow-transparency");
    }
  }
};
var DropdownDisclosure = class _DropdownDisclosure extends AnimatedDetails {
  constructor() {
    super();
    this._detectClickOutsideListener = this._detectClickOutside.bind(this);
    this._detectEscKeyboardListener = this._detectEscKeyboard.bind(this);
    this._detectFocusOutListener = this._detectFocusOut.bind(this);
    this._detectHoverListener = this._detectHover.bind(this);
    this._hoverTimer = null;
    this.addEventListener("mouseover", this._detectHoverListener.bind(this));
    this.addEventListener("mouseout", this._detectHoverListener.bind(this));
  }
  get trigger() {
    return !window.matchMedia("screen and (pointer: fine)").matches ? "click" : this.getAttribute("trigger");
  }
  get mouseOverDelayTolerance() {
    return 250;
  }
  /**
   * When the summary is clicked and that the mode is hover, we follow the link (if set) that may have been specified
   * on the parent. If the mode is click, then we deferred to the default behavior of the "AnimatedDetails" parent
   */
  _onSummaryClicked(event) {
    if (this.trigger === "hover") {
      event.preventDefault();
      if (event.currentTarget.hasAttribute("data-url")) {
        window.location.href = event.currentTarget.getAttribute("data-url");
      }
    } else {
      super._onSummaryClicked(event);
    }
  }
  async _transition(value) {
    if (value) {
      this.setAttribute("open", "");
      document.addEventListener("click", this._detectClickOutsideListener);
      document.addEventListener("keydown", this._detectEscKeyboardListener);
      document.addEventListener("focusout", this._detectFocusOutListener);
      const openSiblings = Array.from(this.closest("ul").querySelectorAll("[open]")).filter((item) => item !== this);
      openSiblings.forEach((sibling) => sibling.open = false);
      await this._transitionIn(openSiblings.length > 0);
    } else {
      document.removeEventListener("click", this._detectClickOutsideListener);
      document.removeEventListener("keydown", this._detectEscKeyboardListener);
      document.removeEventListener("focusout", this._detectFocusOutListener);
      await this._transitionOut();
      this.removeAttribute("open");
    }
  }
  _transitionIn(hasOpenSiblings = false) {
    const timelineSequence = [[this.contentElement, { opacity: 1 }, { duration: 0.2 }]];
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches && !reduceMenuAnimation) {
      timelineSequence.push([this.contentElement.querySelectorAll(":scope > ul > li"), { opacity: [0, 1], transform: ["translateY(-10px)", "translateY(0)"] }, { delay: stagger5(0.025), at: "-0.1", duration: 0.2 }]);
    }
    return timeline9(timelineSequence, { delay: hasOpenSiblings > 0 ? 0.1 : 0 }).finished;
  }
  _transitionOut() {
    return timeline9([[this.contentElement, { opacity: 0 }, { duration: 0.2 }]]).finished;
  }
  /**
   * When dropdown menu is configured to open on click, we add a listener to detect click outside and automatically
   * close the navigation.
   */
  _detectClickOutside(event) {
    if (this.trigger !== "click") {
      return;
    }
    if (!this.contains(event.target) && !(event.target.closest("details") instanceof _DropdownDisclosure)) {
      this.open = false;
    }
  }
  /**
   * On desktop device, if the mode is set to hover, we open/close the dropdown on hover
   */
  _detectHover(event) {
    if (this.trigger !== "hover") {
      return;
    }
    if (event.type === "mouseover") {
      this.open = true;
      clearTimeout(this._hoverTimer);
    } else {
      this._hoverTimer = setTimeout(() => this.open = false, this.mouseOverDelayTolerance);
    }
  }
  /**
   * Detect if we hit the "Escape" key to automatically close the dropdown
   */
  _detectEscKeyboard(event) {
    if (event.code === "Escape") {
      const targetMenu = event.target.closest("details[open]");
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }
  /**
   * Close the dropdown automatically when the dropdown is focused out
   */
  _detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }
};
var MegaMenuDisclosure = class extends DropdownDisclosure {
  constructor() {
    super();
    this.addEventListener("pointerover", this._preloadImages.bind(this));
  }
  get mouseOverDelayTolerance() {
    return 500;
  }
  _transitionIn(hasOpenSiblings) {
    const timelineSequence = [[this.contentElement, { opacity: 1 }, { duration: 0.2 }], "mega-menu"], contentDelay = hasOpenSiblings ? 0.1 : 0;
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches && !reduceMenuAnimation) {
      this.contentElement.querySelectorAll(".mega-menu__promo").forEach((promo) => {
        timelineSequence.push([promo, { opacity: [0, 1] }, { duration: 0.25, delay: contentDelay, at: "mega-menu" }], "mega-menu-promo");
      });
      this.contentElement.querySelectorAll(".mega-menu__nav > li").forEach((column) => {
        timelineSequence.push([column.querySelectorAll(":scope > :first-child, :scope li"), { opacity: [0, 1], transform: ["translateY(-10px)", "translateY(0)"] }, { easing: "ease", delay: stagger5(0.025, { start: contentDelay }), at: "mega-menu", duration: 0.2 }]);
      });
    }
    return timeline9(timelineSequence).finished;
  }
  /**
   * When the toggle is hovered we preload the mega-menu images to improve perceived performance
   */
  _preloadImages() {
    Array.from(this.querySelectorAll('img[loading="lazy"]')).forEach((image) => image.setAttribute("loading", "eager"));
  }
};
var MegaMenuPromoCarousel = class extends EffectCarousel {
  connectedCallback() {
    super.connectedCallback();
    if (this.nextElementSibling) {
      this.addEventListener("carousel:select", (event) => this._updateControlsColor(event.detail.slide));
    }
    this._updateControlsColor(this.items[this.selectedIndex]);
  }
  _updateControlsColor(slide) {
    const extractFrom = slide.classList.contains("content-over-media") ? slide : slide.firstElementChild;
    this.nextElementSibling.style.setProperty("--text-color", extractFrom.style.getPropertyValue("--text-color"));
  }
};
var NavigationDrawer = class extends Drawer {
  constructor() {
    super();
    const delegate = new Delegate6(this);
    delegate.on("click", "button[data-panel]", this._onPanelButtonClick.bind(this));
    this._isTransitioning = false;
    this.addEventListener("dialog:after-hide", () => {
      this.reinitializeDrawer();
    });
  }
  get openFrom() {
    return window.matchMedia("(max-width: 699px)").matches ? this.getAttribute("mobile-opening") : super.openFrom;
  }
  // Used for navigation mobile and navigation desktop set to drawer
  switchToPanel(panelIndex, linkListIndex = null) {
    const panels = this.querySelectorAll(".panel");
    let panelToHideTransform, panelToShowTransform, panelToHide = linkListIndex !== null ? panels[parseInt(panelIndex) - 1] : panels[parseInt(panelIndex) + 1], panelToShow = panels[panelIndex], linkLists = panelToShow.querySelectorAll(".panel__wrapper"), timelineSequence = [];
    if (document.dir === "rtl") {
      panelToHideTransform = linkListIndex !== null ? ["translateX(0%)", "translateX(100%)"] : ["translateX(0%)", "translateX(-100%)"];
      panelToShowTransform = linkListIndex !== null ? ["translateX(-100%)", "translateX(0%)"] : ["translateX(100%)", "translateX(0%)"];
    } else {
      panelToHideTransform = linkListIndex !== null ? ["translateX(0%)", "translateX(-100%)"] : ["translateX(0%)", "translateX(100%)"];
      panelToShowTransform = linkListIndex !== null ? ["translateX(100%)", "translateX(0%)"] : ["translateX(-100%)", "translateX(0%)"];
    }
    timelineSequence.push(
      [panelToHide, { transform: panelToHideTransform, opacity: [1, 0], visibility: ["visible", "hidden"] }, { duration: 0.3, opacity: { at: "+0.3" }, transform: { at: "+0.3" } }],
      "panelHidden",
      [panelToShow, { opacity: [0, 1], visibility: ["hidden", "visible"], transform: panelToShowTransform }, { at: "<", transform: { duration: 0.3 } }]
    );
    if (linkListIndex !== null) {
      timelineSequence.push(this.switchLinkList(linkLists, linkListIndex));
    }
    timeline9(timelineSequence);
  }
  // Used when mega menu is set to drawer
  showPanel(panelIndex, linkListIndex = null) {
    const panels = this.querySelectorAll(".panel");
    let timelineSequence = [], panelToShow = panels[panelIndex], linkLists = panelToShow.querySelectorAll(".panel__wrapper");
    if (!panelToShow.hasAttribute("open") && !this._isTransitioning) {
      this._isTransitioning = true;
      timelineSequence.push(
        [this, { width: [this.offsetWidth + "px", (this.offsetWidth - parseInt(window.getComputedStyle(this).getPropertyValue("padding"))) * 2 + "px"] }, { duration: 0.2 }],
        [panelToShow, { opacity: [0, 1], visibility: ["hidden", "visible"] }, { at: "<" }]
      );
      timelineSequence.push(this.switchLinkList(linkLists, linkListIndex));
    }
    if (this.previousLinkIndex && this.previousLinkIndex !== linkListIndex) {
      timelineSequence.push([linkLists[this.previousLinkIndex], { opacity: [1, 0], visibility: ["visible", "hidden"] }, { duration: 0.2 }]);
    }
    timeline9(timelineSequence).finished.then(() => {
      if (this.previousLinkIndex && this.previousLinkIndex !== linkListIndex) {
        linkLists[this.previousLinkIndex].removeAttribute("style");
      }
      if (panelToShow.hasAttribute("open") && this.previousLinkIndex !== linkListIndex) {
        timeline9([this.switchLinkList(linkLists, linkListIndex)]);
      }
      this.previousLinkIndex = linkListIndex;
      panelToShow.setAttribute("open", "");
      this._isTransitioning = false;
    });
  }
  switchLinkList(linkLists, linkListIndex) {
    Array.from(linkLists).forEach((item) => {
      item.setAttribute("hidden", "");
    });
    linkLists[linkListIndex].removeAttribute("hidden");
    return [linkLists[linkListIndex].querySelectorAll("li"), { opacity: [0, 1], visibility: ["hidden", "visible"], transform: ["translateY(-10px)", "translateY(0)"] }, { easing: "ease", duration: 0.2, at: "-0.15", delay: stagger5(0.025, { start: 0.1 }) }];
  }
  // Set navigation drawer to initial state when drawer closed
  reinitializeDrawer() {
    if (this.hasAttribute("mega-menu") && window.matchMedia("(min-width:1150px)").matches) {
      this.style.removeProperty("width");
      this.setExpanded();
    } else {
      const firstPanel = this.querySelector(".panel:first-child");
      firstPanel.style.opacity = "1";
      firstPanel.style.visibility = "visible";
      firstPanel.style.transform = "translateX(0%)";
    }
    Array.from(this.querySelectorAll(".panel:not(:first-child)")).forEach((item) => {
      if (this.hasAttribute("mega-menu")) {
        item.removeAttribute("open");
      }
      item.style.opacity = "0";
      item.style.visibility = "hidden";
      Array.from(item.querySelectorAll(".panel__wrapper")).forEach((list) => list.setAttribute("hidden", ""));
    });
  }
  setExpanded(target) {
    Array.from(this.querySelectorAll("button[data-panel]")).forEach((item) => {
      item.setAttribute("aria-expanded", "false");
    });
    if (target) {
      target.setAttribute("aria-expanded", "true");
    }
  }
  _onPanelButtonClick(event, target) {
    if (this.hasAttribute("mega-menu") && window.matchMedia("(min-width:1150px)").matches) {
      this.setExpanded(target);
      this.showPanel(...target.getAttribute("data-panel").split("-"));
    } else {
      this.switchToPanel(...target.getAttribute("data-panel").split("-"));
    }
  }
};
if (!window.customElements.get("store-header")) {
  window.customElements.define("store-header", StoreHeader);
}
if (!window.customElements.get("dropdown-disclosure")) {
  window.customElements.define("dropdown-disclosure", DropdownDisclosure, { extends: "details" });
}
if (!window.customElements.get("mega-menu-disclosure")) {
  window.customElements.define("mega-menu-disclosure", MegaMenuDisclosure, { extends: "details" });
}
if (!window.customElements.get("mega-menu-promo-carousel")) {
  window.customElements.define("mega-menu-promo-carousel", MegaMenuPromoCarousel);
}
if (!window.customElements.get("navigation-drawer")) {
  window.customElements.define("navigation-drawer", NavigationDrawer);
}

// js/sections/feature-chart.js
import { animate as motionAnimate3, scroll } from "vendor";
var FeatureChart = class extends HTMLElement {
  connectedCallback() {
    this.viewButtonElement = this.querySelector('[data-action="toggle-rows"]');
    this.featureChartTable = this.querySelector(".feature-chart__table");
    this.featureChartRows = Array.from(this.featureChartTable.childNodes);
    this.featureProductRow = this.querySelector(".feature-chart__table-row--product");
    this.featureChartSticky = this.querySelector(".feature-chart__table-row--sticky");
    if (this.viewButtonElement) {
      this.viewButtonElement.addEventListener("click", this._toggleRows.bind(this));
    }
    if (this.featureChartSticky) {
      this.featureChartSticky.style.width = `${this.featureChartTable.scrollWidth}px`;
      this.featureChartTable.addEventListener("scroll", (event) => {
        this.featureChartSticky.style.marginLeft = -1 * event.target.scrollLeft + "px";
      });
      new ResizeObserver((entries) => {
        this.featureChartSticky.style.width = `${entries[0].contentRect.width}px`;
      }).observe(this.featureChartTable);
      const offset = getComputedStyle(this).scrollPaddingTop;
      scroll(({ y }) => {
        if (y.current >= y.targetOffset + this.featureProductRow.clientHeight / 2 && y.progress < 0.85) {
          this.featureChartSticky.classList.add("is-visible");
        } else {
          this.featureChartSticky.classList.remove("is-visible");
        }
      }, {
        target: this.featureChartTable,
        offset: [`${offset} start`, `end ${offset}`]
      });
    }
  }
  _toggleRows() {
    if (this.classList.contains("is-expanded")) {
      this._hideRows();
    } else {
      this._showRows();
    }
  }
  async _showRows() {
    const fromHeight = this.featureChartTable.clientHeight;
    this.featureChartRows.forEach((row) => {
      row.hidden = false;
    });
    this.viewButtonElement.querySelector(".feature-chart__toggle-text").innerText = this.viewButtonElement.getAttribute("data-view-less");
    this.classList.add("is-expanded");
    await motionAnimate3(this.featureChartTable, { height: [`${fromHeight}px`, `${this.featureChartTable.clientHeight}px`] }).finished;
    this.featureChartTable.style.height = "auto";
  }
  async _hideRows() {
    let fromHeight = this.featureChartTable.clientHeight, toHeight = 0;
    this.featureChartRows.slice(0, parseInt(this.getAttribute("max-rows"))).forEach((row) => {
      toHeight += row.clientHeight;
    });
    this.viewButtonElement.querySelector(".feature-chart__toggle-text").innerText = this.viewButtonElement.getAttribute("data-view-more");
    this.classList.remove("is-expanded");
    await motionAnimate3(this.featureChartTable, { height: [`${fromHeight}px`, `${toHeight}px`] }).finished;
    this.featureChartRows.slice(parseInt(this.getAttribute("max-rows"))).forEach((row) => row.hidden = true);
    this.featureChartTable.style.height = "auto";
  }
};
if (!window.customElements.get("feature-chart")) {
  window.customElements.define("feature-chart", FeatureChart);
}

// js/sections/image-banner.js
import { scroll as scroll2, timeline as timeline10, animate as animate12, inView as inView7 } from "vendor";
var ImageBanner = class extends HTMLElement {
  connectedCallback() {
    if (this.parallax && window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      this._setupParallax();
    }
    inView7(this, async () => {
      await imageLoaded(Array.from(this.querySelectorAll(":scope > :is(img, video, iframe, svg, video-media)")));
      const headings = Array.from(this.querySelectorAll('[reveal-on-scroll="true"]'));
      timeline10([
        [this, { opacity: [0, 1] }, { duration: 0.25 }],
        ...headings.map((heading) => [...getHeadingKeyframe(heading)])
      ]);
    });
  }
  get parallax() {
    return this.hasAttribute("parallax") ? parseFloat(this.getAttribute("parallax")) : false;
  }
  _setupParallax() {
    const media = Array.from(this.querySelectorAll(":scope > :is(img, video, iframe, svg, video-media, picture)")), [scale, translate] = [1 + this.parallax, this.parallax * 100 / (1 + this.parallax)];
    scroll2(
      animate12(media, { transform: [`scale(${scale}) translateY(-${translate}%)`, `scale(${scale}) translateY(0)`] }, { easing: "linear" }),
      {
        target: this,
        offset: ["start end", "end start"]
      }
    );
  }
};
if (!window.customElements.get("image-banner")) {
  window.customElements.define("image-banner", ImageBanner);
}

// js/sections/image-link-blocks.js
var ImageLinkBlocks = class extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.children);
    new ScrollArea(this);
    this.addEventListener("control:prev", this._prev);
    this.addEventListener("control:next", this._next);
  }
  _prev() {
    this.scrollBy({ left: (document.dir === "rtl" ? 1 : -1) * this.items[0].clientWidth, behavior: "smooth" });
  }
  _next() {
    this.scrollBy({ left: (document.dir === "rtl" ? -1 : 1) * this.items[0].clientWidth, behavior: "smooth" });
  }
};
if (!window.customElements.get("image-link-blocks")) {
  window.customElements.define("image-link-blocks", ImageLinkBlocks);
}

// js/sections/images-with-text-scrolling.js
import { animate as animate13, timeline as timeline11, inView as inView8 } from "vendor";
var ImagesWithTextScrolling = class extends HTMLElement {
  connectedCallback() {
    inView8(this, this._reveal.bind(this));
    if (this.hasAttribute("scrolling-experience")) {
      this._imageToTransitionItems = Array.from(this.querySelectorAll(".images-scrolling-desktop__media-wrapper > :not(:first-child)"));
      this._mainImage = this.querySelector(".images-scrolling-desktop__media-wrapper > :first-child");
      this._contentItems = Array.from(this.querySelectorAll(".images-scrolling__content"));
      if (this._imageToTransitionItems.length > 0) {
        window.addEventListener("scroll", throttle(this._onScroll.bind(this)));
      }
    }
  }
  _reveal() {
    Array.from(this.querySelectorAll('[reveal-on-scroll="true"]')).forEach((heading) => {
      animate13(...getHeadingKeyframe(heading));
    });
  }
  _onScroll() {
    const imageRect = this._mainImage.getBoundingClientRect(), imageBottom = imageRect.bottom, imageEffect = this.getAttribute("scrolling-experience");
    for (const [index, contentItem] of this._contentItems.entries()) {
      const contentItemRect = contentItem.getBoundingClientRect(), image = this._imageToTransitionItems[index - 1], content = this._contentItems[index];
      if (contentItemRect.top < imageBottom - imageRect.height * 0.1 && contentItemRect.bottom > imageBottom) {
        if (image && !image.classList.contains("is-visible")) {
          image.classList.add("is-visible");
          if (imageEffect === "fade") {
            timeline11([
              [image, { opacity: [null, 1] }, { duration: 0.2 }],
              [content, { opacity: [null, 1] }, { duration: 0.45, at: "<" }]
            ]);
          } else {
            timeline11([
              [image, { opacity: [null, 1], clipPath: ["inset(100% 0 0 0)", "inset(0 0 0 0)"] }, { duration: 0.35, easing: [0.99, 0.01, 0.5, 0.94], opacity: { duration: 0 } }],
              [content, { opacity: [null, 1] }, { duration: 0.45, at: "<" }]
            ]);
          }
        }
        break;
      }
      if (contentItemRect.top > imageBottom - imageRect.height * 0.1) {
        if (image && image.classList.contains("is-visible")) {
          image.classList.remove("is-visible");
          if (imageEffect === "fade") {
            timeline11([
              [image, { opacity: [null, 0] }, { duration: 0.2 }],
              [content, { opacity: [null, 0] }, { duration: 0.2, at: "<" }]
            ]);
          } else {
            timeline11([
              [image, { opacity: [null, 1], clipPath: ["inset(0 0 0 0)", "inset(100% 0 0 0)"] }, { duration: 0.35, easing: [0.99, 0.01, 0.5, 0.94], opacity: { duration: 0 } }],
              [content, { opacity: [null, 0] }, { duration: 0.2, at: "<" }]
            ]);
          }
        }
        break;
      }
    }
  }
};
if (!window.customElements.get("images-with-text-scrolling")) {
  window.customElements.define("images-with-text-scrolling", ImagesWithTextScrolling);
}

// js/sections/impact-text.js
import { animate as animate14, inView as inView9 } from "vendor";
var ImpactText = class extends HTMLElement {
  connectedCallback() {
    if (!window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      return;
    }
    inView9(this, ({ target }) => this._onBecameVisible(target), { margin: "-100px" });
  }
  async _onBecameVisible(target) {
    animate14(target, { opacity: 1, transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.3 });
    if (this.hasAttribute("count-up")) {
      const itemToSearch = this.childElementCount === 0 ? this : this.firstElementChild, matches = itemToSearch.textContent.trim().match(/\d+(?:[,. ]\d+)*/);
      if (!matches) {
        return;
      }
      itemToSearch.innerHTML = itemToSearch.textContent.replace(/\d+(?:[,. ]\d+)*/, `<span>${matches[0]}</span>`);
      const numberSpan = itemToSearch.querySelector("span");
      numberSpan.style.textAlign = matches[0] === matches["input"] ? null : "end";
      if (!itemToSearch.classList.contains("text-gradient")) {
        numberSpan.style.display = "inline-block";
        numberSpan.style.minWidth = `${numberSpan.clientWidth}px`;
      }
      const toReplace = matches[0].replace(/[,\. ]+/, ""), charactersMatches = [...matches[0].matchAll(/[,\. ]+/g)];
      await animate14((progress) => {
        let formattedString = Math.round(progress * parseInt(toReplace)).toString();
        charactersMatches.forEach((character) => {
          if (formattedString.length >= matches[0].length - character.index) {
            formattedString = formattedString.slice(0, character.index) + character[0] + formattedString.slice(character.index);
          }
        });
        numberSpan.textContent = progress === 1 ? matches[0] : formattedString;
      }, { duration: parseFloat(this.getAttribute("count-up")), easing: [0.16, 1, 0.3, 1] }).finished;
      numberSpan.style.minWidth = null;
    }
  }
};
if (!window.customElements.get("impact-text")) {
  window.customElements.define("impact-text", ImpactText);
}

// js/sections/media-grid.js
import { timeline as timeline12, inView as inView10 } from "vendor";
var MediaGrid = class extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.children);
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      inView10(this, this._onBecameVisible.bind(this));
    }
  }
  _onBecameVisible() {
    const contentElements = this.querySelectorAll(".content-over-media > :not(img, video, iframe, svg, video-media, native-video, external-video)");
    timeline12([
      [this.items, { opacity: 1, transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.3 }],
      [contentElements, { opacity: [0, 1] }, { duration: 0.2, at: "+0.1" }]
    ]);
  }
};
if (!window.customElements.get("media-grid")) {
  window.customElements.define("media-grid", MediaGrid);
}

// js/sections/media-with-text.js
import { animate as animate15, timeline as timeline13, inView as inView11 } from "vendor";
var reduceMotion = window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
var MediaWithText = class extends HTMLElement {
  connectedCallback() {
    if (reduceMotion) {
      Array.from(this.querySelectorAll(".media-with-text__item")).forEach((item) => {
        inView11(item, (observer) => this.reveal(observer.target));
      });
    }
  }
  reveal(item) {
    const media = item.querySelector(".media-with-text__media");
    imageLoaded(media.querySelector("img")).then(() => {
      timeline13([
        [media, { opacity: [0, 1] }, { duration: 0.3 }],
        [media.querySelector("img, video-media"), { transform: ["scale(1.05)", "scale(1)"] }, { duration: 0.3, at: "<" }]
      ]);
    });
    animate15(item.querySelector(".media-with-text__content > .prose"), { opacity: [0, 1] }, { duration: 0.2, delay: 0.3 });
  }
};
if (!window.customElements.get("media-with-text")) {
  window.customElements.define("media-with-text", MediaWithText);
}

// js/sections/multiple-images-with-text.js
import { timeline as timeline14, animate as animate16, stagger as stagger6, inView as inView12 } from "vendor";
var MultipleImagesWithText = class extends HTMLElement {
  constructor() {
    super();
    this._imageCarousel = this.querySelector("multiple-images-with-text-image-list");
    this._contentCarousel = this.querySelector("multiple-images-with-text-content-list");
    this.addEventListener("control:prev", () => {
      this._imageCarousel.previous();
      this._contentCarousel.previous();
    });
    this.addEventListener("control:next", () => {
      this._imageCarousel.next();
      this._contentCarousel.next();
    });
    if (Shopify.designMode) {
      this.addEventListener("shopify:block:select", (event) => {
        if (event.target.hasAttribute("image-id")) {
          this._imageCarousel.select(this._imageCarousel.items.findIndex((item) => item.id === event.target.getAttribute("image-id")));
        }
      });
    }
  }
};
var MultipleImagesWithTextImageList = class extends EffectCarousel {
  constructor() {
    super();
    inView12(this, this._reveal.bind(this));
  }
  async _reveal() {
    await imageLoaded(this.querySelectorAll("img"));
    if (this.getAttribute("layout") === "stacked") {
      timeline14([
        [this.lastElementChild, { transform: "rotate(0deg)" }],
        [this.lastElementChild?.previousElementSibling, { transform: "rotate(2deg)" }, { at: "<" }],
        [this.lastElementChild?.previousElementSibling?.previousElementSibling, { transform: "rotate(-2deg)" }, { at: "<" }]
      ], {
        defaultOptions: { duration: 0.15, easing: [0.26, 0.02, 0.27, 0.97] }
      });
    } else if (this.getAttribute("layout") === "collage") {
      timeline14([
        [this.children, { opacity: 1, transform: ["translateY(15px)", "translateY(0)"] }, { duration: 0.3, delay: stagger6(0.1) }]
      ]);
    } else {
      timeline14([
        [this.children, { opacity: 1, transform: "rotate(var(--image-rotation, 0deg))" }, { duration: 0.3, delay: stagger6(0.1) }]
      ]);
    }
  }
  _transitionTo(fromSlide, toSlide, { direction, animate: animate19 }) {
    if (this.getAttribute("layout") !== "stacked") {
      return;
    }
    const transitionSpeed = 0.2;
    if (direction === "next") {
      const fromSlideTransform = getComputedStyle(fromSlide).getPropertyValue("transform");
      return timeline14([
        [fromSlide, { opacity: 0, transform: "rotate(5deg) translate(40px, 10px)" }, { duration: transitionSpeed }],
        [toSlide, { zIndex: 1 }, { duration: transitionSpeed, zIndex: { easing: "step-start" } }],
        [fromSlide, { opacity: 1, transform: fromSlideTransform, zIndex: -1 }, { duration: transitionSpeed, at: "<", zIndex: { easing: "step-start" } }],
        [toSlide.previousElementSibling, { zIndex: 0 }, { at: "<", easing: "step-start" }]
      ], { defaultOptions: { easing: [0.26, 0.02, 0.27, 0.97] } }).finished;
    } else {
      const toSlideTransform = getComputedStyle(toSlide).getPropertyValue("transform");
      return timeline14([
        [toSlide, { opacity: 0, transform: "rotate(-5deg) translate(-40px, -10px)" }, { duration: transitionSpeed }],
        this.items.length >= 3 && [fromSlide.previousElementSibling || this.lastElementChild, { zIndex: -1 }, { easing: "step-start" }],
        [toSlide, { opacity: 1, transform: toSlideTransform, zIndex: 1 }, { duration: transitionSpeed, at: this.items.length >= 3 ? "<" : "+0", zIndex: { easing: "step-start" } }],
        [fromSlide, { zIndex: 0 }, { duration: transitionSpeed, at: "<", zIndex: { easing: "step-start" } }]
      ].filter(Boolean), { defaultOptions: { easing: [0.26, 0.02, 0.27, 0.97] } }).finished;
    }
  }
};
var MultipleImagesWithTextContentList = class extends EffectCarousel {
  constructor() {
    super();
    inView12(this, this._reveal.bind(this));
  }
  _reveal() {
    animate16(...getHeadingKeyframe(this.querySelector('[reveal-on-scroll="true"]')));
  }
  _transitionTo(fromSlide, toSlide, { direction = "next", animate: animate19 = true } = {}) {
    fromSlide.classList.remove("is-selected");
    toSlide.classList.add("is-selected");
    return timeline14([
      [fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"] }],
      [toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"] }],
      [...getHeadingKeyframe(toSlide.querySelector('[reveal-on-scroll="true"]'), { at: "<" })]
    ], { duration: animate19 ? parseFloat(this.getAttribute("fade-speed") || 0.5) : 0 }).finished;
  }
};
if (!window.customElements.get("multiple-images-with-text")) {
  window.customElements.define("multiple-images-with-text", MultipleImagesWithText);
}
if (!window.customElements.get("multiple-images-with-text-image-list")) {
  window.customElements.define("multiple-images-with-text-image-list", MultipleImagesWithTextImageList);
}
if (!window.customElements.get("multiple-images-with-text-content-list")) {
  window.customElements.define("multiple-images-with-text-content-list", MultipleImagesWithTextContentList);
}

// js/sections/newsletter-popup.js
var NewsletterPopup = class extends Drawer {
  connectedCallback() {
    super.connectedCallback();
    if (this.shouldAppearAutomatically) {
      setTimeout(() => this.show(), this.apparitionDelay);
    }
  }
  get initialFocus() {
    return false;
  }
  get shouldAppendToBody() {
    return false;
  }
  get apparitionDelay() {
    return parseInt(this.getAttribute("apparition-delay") || 0) * 1e3;
  }
  get shouldAppearAutomatically() {
    return !(localStorage.getItem("theme:popup-filled") === "true" || this.hasAttribute("only-once") && localStorage.getItem("theme:popup-appeared") === "true");
  }
  _setInitialPosition() {
    this.style.top = null;
    this.style.bottom = "0px";
    this.style.left = document.dir === "ltr" ? null : "0px";
    this.style.right = document.dir === "rtl" ? "auto" : "0px";
  }
  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);
    if (name === "open" && this.open) {
      localStorage.setItem("theme:popup-appeared", "true");
    }
  }
};
if (!window.customElements.get("newsletter-popup")) {
  window.customElements.define("newsletter-popup", NewsletterPopup);
}

// js/sections/press.js
import { timeline as timeline15, animate as animate17, inView as inView13 } from "vendor";
var reduceMotion2 = window.matchMedia("(prefers-reduced-motion: no-preference)").matches;
var PressCarousel = class extends EffectCarousel {
  constructor() {
    super();
    if (reduceMotion2) {
      inView13(this, this._reveal.bind(this));
    }
  }
  _reveal() {
    animate17(this.selectedSlide.querySelector(".blockquote"), { opacity: 1, transform: ["translateY(15px)", "translateY(0)"] }, { duration: 0.2 });
  }
  async _transitionTo(fromSlide, toSlide, options = {}) {
    await timeline15([
      [fromSlide.querySelectorAll(".press__logo, .press__author"), { opacity: [null, 0] }, { duration: 0.2 }],
      [fromSlide.querySelector(".blockquote"), { opacity: [null, 0], ...reduceMotion2 && { transform: [null, "translateY(-10px)"] } }, { duration: 0.2, at: "<" }]
    ]).finished;
    fromSlide.classList.remove("is-selected");
    toSlide.classList.add("is-selected");
    await timeline15([
      [toSlide.querySelectorAll(".press__logo, .press__author"), { opacity: [0, 1] }, { duration: 0.2 }],
      [toSlide.querySelector(".blockquote"), { opacity: [0, 1], ...reduceMotion2 && { transform: ["translateY(10px)", "translateY(0px)"] } }, { duration: 0.2, at: "<" }]
    ]).finished;
  }
};
if (!window.customElements.get("press-carousel")) {
  window.customElements.define("press-carousel", PressCarousel);
}

// js/sections/product-recommendations.js
var ProductRecommendations = class extends HTMLElement {
  constructor() {
    super();
    this._isLoaded = false;
  }
  connectedCallback() {
    this._loadRecommendations();
  }
  async _loadRecommendations() {
    if (this._isLoaded) {
      return;
    }
    this._isLoaded = true;
    const section = this.closest(".shopify-section");
    if (getVehicleId()) {
      setRelatedFitmentLoading(section, true);
    }
    const intent = this.getAttribute("intent") || "related", url = `${Shopify.routes.root}recommendations/products?product_id=${this.getAttribute("product")}&limit=${this.getAttribute("limit") || 4}&section_id=${section.id.replace("shopify-section-", "")}&intent=${intent}`, response = await fetch(url, { priority: "low" });
    const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html"), productRecommendationsElement = tempDiv.querySelector("product-recommendations");
    if (productRecommendationsElement.childElementCount > 0) {
      if (getVehicleId()) {
        setRelatedFitmentLoading(section, true);
      }
      this.replaceChildren(...document.importNode(productRecommendationsElement, true).childNodes);
      updateRelatedProductsCompatibility(section);
    } else {
      if (intent === "related") {
        section.remove();
      } else {
        this.remove();
      }
    }
  }
};
if (!window.customElements.get("product-recommendations")) {
  window.customElements.define("product-recommendations", ProductRecommendations);
}

// js/sections/recently-viewed-products.js
var _isLoaded, _RecentlyViewedProducts_instances, searchQueryString_get, loadProducts_fn;
var RecentlyViewedProducts = class extends HTMLElement {
  constructor() {
    super(...arguments);
    __privateAdd(this, _RecentlyViewedProducts_instances);
    __privateAdd(this, _isLoaded, false);
  }
  connectedCallback() {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(__privateMethod(this, _RecentlyViewedProducts_instances, loadProducts_fn).bind(this), { timeout: 1500 });
    } else {
      __privateMethod(this, _RecentlyViewedProducts_instances, loadProducts_fn).call(this);
    }
  }
};
_isLoaded = new WeakMap();
_RecentlyViewedProducts_instances = new WeakSet();
searchQueryString_get = function() {
  const items = new Set(JSON.parse(localStorage.getItem("theme:recently-viewed-products") || "[]"));
  if (this.hasAttribute("exclude-id")) {
    items.delete(parseInt(this.getAttribute("exclude-id")));
  }
  return Array.from(items.values(), (item) => `id:${item}`).slice(0, parseInt(this.getAttribute("products-count"))).join(" OR ");
};
loadProducts_fn = async function() {
  if (__privateGet(this, _isLoaded)) {
    return;
  }
  __privateSet(this, _isLoaded, true);
  const section = this.closest(".shopify-section"), url = `${Shopify.routes.root}search?type=product&q=${__privateGet(this, _RecentlyViewedProducts_instances, searchQueryString_get)}&section_id=${extractSectionId(section)}`, response = await fetch(url, { priority: "low" });
  const tempDiv = new DOMParser().parseFromString(await response.text(), "text/html"), recentlyViewedProductsElement = tempDiv.querySelector("recently-viewed-products");
  if (recentlyViewedProductsElement.childElementCount > 0) {
    this.replaceChildren(...document.importNode(recentlyViewedProductsElement, true).childNodes);
  } else {
    section.remove();
  }
};
if (!window.customElements.get("recently-viewed-products")) {
  window.customElements.define("recently-viewed-products", RecentlyViewedProducts);
}

// js/sections/revealed-image-on-scroll.js
import { scroll as scroll3, timeline as timeline16, ScrollOffset, inView as inView14 } from "vendor";
var RevealedImage = class extends HTMLElement {
  connectedCallback() {
    const scrollTracker = this.querySelector(".revealed-image__scroll-tracker"), scroller = this.querySelector(".revealed-image__scroller");
    scrollTracker.style.height = `${scroller.clientHeight}px`;
    new ResizeObserver((entries) => scrollTracker.style.height = `${entries[0].contentRect.height}px`).observe(scroller);
    scroll3(timeline16([
      [this.querySelectorAll(".revealed-image__image-clipper, .revealed-image__content--inside"), { clipPath: ["inset(37% 37% 41% 37%)", "inset(calc(var(--sticky-area-height) / 2) 0)"] }, { easing: "ease-in" }],
      [this.querySelectorAll("img, svg"), { transform: ["translate(-10%, -1.5%) scale(1.4)", "translate(0, 0) scale(1)"] }, { easing: "ease-in", at: "<" }],
      [this.querySelectorAll(".revealed-image__content"), { opacity: [0, 1, 1] }, { offset: [0, 0.25, 1], at: "<" }]
    ]), {
      target: scrollTracker,
      offset: ScrollOffset.All
    });
    inView14(this, () => {
      this.style.visibility = "visible";
      return () => {
        this.style.visibility = "hidden";
      };
    }, { margin: "200px" });
  }
};
if (!window.customElements.get("revealed-image")) {
  window.customElements.define("revealed-image", RevealedImage);
}

// js/sections/scrolling-text.js
import { animate as animate18, scroll as scroll4 } from "vendor";
var ScrollingText = class extends HTMLElement {
  connectedCallback() {
    if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
      if (window.ScrollTimeline) {
        const timeline18 = new ViewTimeline({
          subject: this,
          axis: "block"
        });
        this.animate(
          {
            transform: ["translateX(calc(var(--transform-logical-flip) * 50vw))", "translateX(calc(var(--transform-logical-flip) * 50vw - 10%))"]
          },
          {
            timeline: timeline18,
            rangeStart: "cover 0%",
            rangeEnd: "cover 100%"
          }
        );
      } else {
        scroll4(
          animate18(
            this,
            {
              transform: ["translateX(calc(var(--transform-logical-flip) * 50vw))", "translateX(calc(var(--transform-logical-flip) * 50vw - 10%))"]
            }
          ),
          {
            target: this,
            offset: ["start end", "end start"]
          }
        );
      }
    }
  }
};
if (!window.customElements.get("scrolling-text")) {
  window.customElements.define("scrolling-text", ScrollingText);
}

// js/sections/shop-the-look.js
var ShopTheLookDots = class extends HTMLElement {
  connectedCallback() {
    this._abortController = new AbortController();
    Array.from(this.children).forEach((dots) => {
      document.getElementById(dots.getAttribute("aria-controls")).addEventListener("carousel:change", (event) => this._onDotClicked(event), { signal: this._abortController.signal });
    });
  }
  disconnectedCallback() {
    this._abortController.abort();
  }
  _onDotClicked(event) {
    Array.from(this.querySelectorAll(`button:nth-child(${event.detail.index + 1})`)).forEach((button) => button.click());
  }
};
if (!window.customElements.get("shop-the-look-dots")) {
  window.customElements.define("shop-the-look-dots", ShopTheLookDots);
}

// js/sections/slideshow.js
import { animate as motionAnimate4, timeline as timeline17, inView as inView15 } from "vendor";
var Slideshow = class extends HTMLElement {
  constructor() {
    super();
    this.addEventListener("carousel:select", this._onSlideSelected);
  }
  async _onSlideSelected(event) {
    const slideStyles = getComputedStyle(event.detail.slide);
    this.style.setProperty("--slideshow-controls-background", slideStyles.getPropertyValue("--slideshow-slide-controls-background"));
    this.style.setProperty("--slideshow-controls-color", slideStyles.getPropertyValue("--slideshow-slide-controls-color"));
    if (!this.classList.contains("slideshow--boxed")) {
      return;
    }
    const backgroundElement = this.querySelector(".slideshow__slide-background");
    const background = slideStyles.getPropertyValue("--slideshow-slide-background");
    backgroundElement.style.background = background;
    await motionAnimate4(backgroundElement, { opacity: [0, 1] }, { duration: 0.2 }).finished;
    this.style.setProperty("--slideshow-background", background);
    motionAnimate4(backgroundElement, { opacity: 0 }, { duration: 0 });
  }
};
var SlideshowCarousel = class extends EffectCarousel {
  _pendingAnimationControls = [];
  constructor() {
    super();
    this.addEventListener("carousel:select", this._onSlideSelected);
    if (this.hasAttribute("reveal-on-scroll")) {
      inView15(this, this._reveal.bind(this));
    }
    if (this.querySelector(".slideshow__cursor")) {
      this.addEventListener("tap", this._onSlideshowTap);
    }
  }
  connectedCallback() {
    super.connectedCallback();
    if (this._player && this.hasAttribute("autoplay")) {
      this._player.addEventListener("player:start", (event) => {
        const cursorRing = this.querySelector(".slideshow__cursor-ring circle");
        if (cursorRing) {
          const cursorRingAnimationControl = motionAnimate4(cursorRing, { strokeDasharray: [`0px, ${cursorRing.getTotalLength()}px`, `${cursorRing.getTotalLength()}px, ${cursorRing.getTotalLength()}px`] }, {
            duration: event.detail.duration,
            easing: "linear"
          });
          this._pendingAnimationControls.push(cursorRingAnimationControl);
        }
        const circles = Array.from(this.querySelectorAll(".numbered-dots__item"));
        circles.forEach((item) => {
          const circle = item.querySelector("circle:last-child");
          let circleAnimationControl = null;
          if (item.getAttribute("aria-current") === "true") {
            circleAnimationControl = motionAnimate4(circle, { strokeDasharray: [`0px, ${circle.getTotalLength()}px`, `${circle.getTotalLength()}px, ${circle.getTotalLength()}px`] }, { duration: event.detail.duration, easing: "linear" });
          } else {
            circleAnimationControl = motionAnimate4(circle, { strokeDasharray: `${circle.getTotalLength()}px, ${circle.getTotalLength()}px` }, { duration: 0, easing: "linear" });
          }
          this._pendingAnimationControls.push(circleAnimationControl);
        });
      });
      this._player.addEventListener("player:pause", () => {
        this._pendingAnimationControls.forEach((control) => control.pause());
      });
      this._player.addEventListener("player:resume", () => {
        this._pendingAnimationControls.forEach((control) => control.play());
      });
    }
  }
  get transitionType() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "fade" : this.getAttribute("transition");
  }
  async _reveal() {
    const selectedSlide = this.selectedSlide;
    await imageLoaded(selectedSlide.querySelectorAll("img"));
    this.style.opacity = "1";
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }
    if (!this._reloaded) {
      timeline17([
        [selectedSlide, { zIndex: 1 }, { duration: 0 }],
        [selectedSlide.querySelectorAll("img, video-media"), { opacity: [0, 1], transform: ["scale(1.05)", "scale(1)"] }, { duration: 0.3 }],
        "content",
        [selectedSlide.querySelectorAll('[data-sequence="subheading"], .button'), { opacity: [0, 1] }, { duration: 0.3, at: "content" }],
        [...getHeadingKeyframe(selectedSlide.querySelector('[data-sequence="heading"]'), { duration: 0.3, at: "content" })],
        [selectedSlide.querySelector(".button"), { opacity: [0, 1] }, { duration: 0.3, at: "content" }],
        [this.querySelector(".slideshow__controls"), { opacity: [0, 1], transform: ["translateY(10px)", "translateY(0)"] }, { duration: 0.3 }]
      ]);
    }
  }
  _onSlideshowTap(event) {
    if (event.target.matches("button, a[href], button :scope, a[href] :scope") || !window.matchMedia("screen and (pointer: fine)").matches) {
      return;
    }
    event.detail.originalEvent.clientX > window.innerWidth / 2 ? this.next() : this.previous();
  }
  /**
   * Perform a simple fade animation. For more complex animations, you should implement your own custom elements
   * that extends the EffectCarousel, and implement your own transition. You should make sure to return a promise
   * that resolves when the animation is finished
   */
  async _transitionTo(fromSlide, toSlide, { direction, animate: animate19 = true } = {}) {
    fromSlide.classList.remove("is-selected");
    toSlide.classList.add("is-selected");
    let timelineControls = null;
    switch (this.transitionType) {
      case "fade":
        timelineControls = this._fade(fromSlide, toSlide, { animate: animate19 });
        break;
      case "fade_with_text":
        timelineControls = this._fadeWithText(fromSlide, toSlide, { animate: animate19 });
        break;
    }
    if (!animate19) {
      timelineControls.finish();
    }
    return timelineControls.finished;
  }
  /**
   * Perform a simple fade transition
   */
  _fade(fromSlide, toSlide) {
    return timeline17([
      [fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"], zIndex: 0 }, { duration: 0.3, easing: "ease-in", zIndex: { easing: "step-end" } }],
      [toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"], zIndex: 1 }, { duration: 0.3, at: "<", easing: "ease-out", zIndex: { easing: "step-end" } }]
    ]);
  }
  /**
   * Perform a transition with fade images and image transition
   */
  async _fadeWithText(fromSlide, toSlide) {
    motionAnimate4(fromSlide, { opacity: [1, 0], visibility: ["visible", "hidden"], zIndex: 0 }, { duration: 0.3, easing: "ease-in", zIndex: { easing: "step-end" } });
    await imageLoaded(toSlide.querySelectorAll("img"));
    motionAnimate4(toSlide, { opacity: [0, 1], visibility: ["hidden", "visible"], zIndex: 1 }, { duration: 0, zIndex: { easing: "step-end" } });
    return timeline17([
      [toSlide.querySelectorAll("img, video-media"), { opacity: [0, 1], transform: ["scale(1.05)", "scale(1)"] }, { duration: 0.3, easing: "ease-out" }],
      "content",
      [toSlide.querySelectorAll('[data-sequence="subheading"], .button'), { opacity: [0, 1] }, { duration: 0.3, at: "content" }],
      [...getHeadingKeyframe(toSlide.querySelector('[data-sequence="heading"]'), { duration: 0.3, at: "content" })]
    ]);
  }
  /**
   * Perform the synchronization with video
   */
  async _onSlideSelected(event) {
    Array.from(this.querySelectorAll("video-media")).forEach((video) => video.pause());
    if (event.detail.slide.getAttribute("data-slide-type") === "video") {
      const visibleVideo = Array.from(event.detail.slide.querySelectorAll("video-media")).filter((video) => video.offsetParent !== null).pop();
      visibleVideo.play({ restart: true });
      if (this.player) {
        const video = visibleVideo.querySelector("video");
        if (isNaN(video.duration)) {
          await new Promise((resolve) => {
            video.onloadedmetadata = () => resolve();
          });
        }
        this._player.setDuration(video.duration);
      }
    } else {
      this._player?.setDuration(parseInt(this.getAttribute("autoplay")));
    }
  }
};
if (!window.customElements.get("x-slideshow")) {
  window.customElements.define("x-slideshow", Slideshow);
}
if (!window.customElements.get("slideshow-carousel")) {
  window.customElements.define("slideshow-carousel", SlideshowCarousel);
}

// js/theme.js
import { Delegate as Delegate7 } from "vendor";
(() => {
  const delegateDocument = new Delegate7(document.documentElement);
  if (window.matchMedia("(prefers-reduced-motion: no-preference)").matches) {
    delegateDocument.on("click", 'a[href*="#"]', (event, target) => {
      if (event.defaultPrevented || target.pathname !== window.location.pathname || target.search !== window.location.search) {
        return;
      }
      const url = new URL(target.href);
      if (url.hash === "") {
        return;
      }
      const anchorElement = document.querySelector(url.hash);
      if (anchorElement) {
        event.preventDefault();
        anchorElement.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    });
  }
  const setScrollbarWidth = () => {
    const scrollbarWidth = window.innerWidth - document.body.clientWidth;
    if (scrollbarWidth > 0) {
      document.documentElement.style.setProperty("--scrollbar-width", `${scrollbarWidth}px`);
    }
  };
  setScrollbarWidth();
  window.addEventListener("resize", throttle(setScrollbarWidth));
})();
export {
  AccordionDisclosure,
  AccountLogin,
  AnimatedDetails,
  AnnouncementBar,
  BuyButtons,
  CartCount,
  CartDrawer,
  CartNote,
  CartNotificationDrawer,
  CollectionList,
  ConfirmButton,
  CopyButton,
  CountrySelector,
  CustomButton,
  CustomCursor,
  DialogElement,
  Drawer,
  DropdownDisclosure,
  EffectCarousel,
  FacetApplyButton,
  FacetDialog,
  FacetDrawer,
  FacetFloatingFilter,
  FacetForm,
  FacetLink,
  FacetSortBy,
  FeatureChart,
  FreeShippingBar,
  GestureArea,
  GiftCardRecipient,
  HeightObserver,
  ImageBanner,
  ImageLinkBlocks,
  ImagesWithTextScrolling,
  ImpactText,
  LineItem,
  LineItemQuantity,
  Listbox,
  MarqueeText,
  MediaCarousel,
  MediaGrid,
  MediaWithText,
  MegaMenuDisclosure,
  MegaMenuPromoCarousel,
  ModelMedia,
  MultipleImagesWithText,
  MultipleImagesWithTextContentList,
  MultipleImagesWithTextImageList,
  NavigationDrawer,
  NewsletterPopup,
  NextButton,
  PageDots,
  PillLoader,
  Player,
  Popover,
  PredictiveSearch,
  PressCarousel,
  PrevButton,
  PriceRange,
  PrivacyBar,
  ProductCard,
  ProductForm,
  ProductGallery,
  ProductLoader,
  ProductQuickAdd,
  ProductRecommendations,
  ProductRerender,
  ProductZoomButton,
  ProgressBar,
  QuantitySelector,
  QuickBuyDrawer,
  RecentlyViewedProducts,
  ResizableTextarea,
  RevealItems,
  RevealedImage,
  SafeSticky,
  ScrollArea,
  ScrollCarousel,
  ScrollProgress,
  ScrollShadow,
  ScrollingText,
  SearchDrawer,
  SearchDropdown,
  SectionHeader,
  ShareButton,
  ShippingEstimator,
  ShopTheLookDots,
  Slideshow,
  SlideshowCarousel,
  SplitCursor,
  SplitLines,
  StoreHeader,
  Tabs,
  VariantPicker,
  VideoMedia,
  cachedFetch,
  createMediaImg,
  debounce,
  deepQuerySelector,
  extractSectionId,
  fetchCart,
  generateSrcset,
  getHeadingKeyframe,
  imageLoaded,
  throttle,
  waitForEvent
};

// V-Sport: out-of-stock add-to-cart confirmation modal
// Modal UI is exposed as window.__vsportShowOosModal(formEl).
// The gate itself lives in ProductForm._onSubmit (above), so it fires before aria-busy/loader.
window.__vsportShowOosModal = function (formEl) {
  const scrollY = window.scrollY;
  const section = formEl?.closest?.('.shopify-section, [id^="shopify-section"]');
  const badge = section && section.querySelector('.vsport-backorder-badge[data-oos-confirm="true"]');
  const title = badge?.getAttribute('data-oos-title') || 'Item Currently Out of Stock';
  const message = badge?.getAttribute('data-oos-message') || 'This product is currently out of stock and will be ordered especially for you. Are you happy to proceed?';
  const cancelLabel = badge?.getAttribute('data-oos-cancel') || 'Cancel';
  const confirmLabel = badge?.getAttribute('data-oos-confirm-label') || 'Yes, proceed';
  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const overlay = document.createElement('div');
  overlay.className = 'vsport-oos-modal-overlay';
  overlay.innerHTML = `
    <div class="vsport-oos-modal" role="dialog" aria-modal="true" aria-labelledby="vsport-oos-title">
      <p class="vsport-oos-modal__title" id="vsport-oos-title">${escapeHtml(title)}</p>
      <p class="vsport-oos-modal__message">${escapeHtml(message)}</p>
      <div class="vsport-oos-modal__actions">
        <button class="vsport-oos-modal__cancel" type="button">${escapeHtml(cancelLabel)}</button>
        <button class="vsport-oos-modal__confirm" type="button">${escapeHtml(confirmLabel)}</button>
      </div>
    </div>`;
  document.body.appendChild(overlay);
  function closeModal() {
    overlay.remove();
    window.scrollTo({ top: scrollY, left: window.scrollX });
  }
  overlay.querySelector('.vsport-oos-modal__cancel').addEventListener('click', function (e) {
    e.preventDefault();
    closeModal();
  });
  overlay.querySelector('.vsport-oos-modal__confirm').addEventListener('click', function (e) {
    e.preventDefault();
    closeModal();
    formEl._vsportOosConfirmed = true;
    formEl.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  });
  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) closeModal();
  });
};
